// ============================================================
//  PCE_RISE V1.70 — VOLLSTAENDIGES Regelwerk, 3115 Regeln.
//
//  ERZEUGT aus PCE_RISE_V1.70.XLSX, Blatt "Ruleset".
//  NICHT VON HAND BEARBEITEN — stattdessen:
//      python3 scripts/build-ruleset.py [pfad/zur/XLSX]
//
//  Der Step ist die Pruefreihenfolge: die erste greifende Regel gewinnt,
//  dadurch setzt sich automatisch die hoechste Klasse durch.
//  Developer 1 - Advanced 472 - Core 354 - Self-Service 1554 - Engine/LoB 734
// ============================================================
import type { EngineMetric, LicenseClass } from "../domain/types";

export interface PceRule {
  step: number;             // Pruefreihenfolge (klein = hoch)
  ruleClass: string;        // Originalbezeichnung aus dem Regelwerk
  cls: LicenseClass | null; // FUE-Klasse; null bei Engine/LoB/technisch
  engine?: EngineMetric;    // Engine-Metrik statt FUE
  type: "AUTH" | "DUMMY" | "ENGI";
  obj: string;              // Auth. Object
  field: string | null;     // Auth. Field
  val: string | null;       // Auth. Value
}

export const PCE_VERSION = "V1.70";

interface StepMeta { ruleClass: string; cls: LicenseClass | null; engine?: EngineMetric }

/** Klasse, Bezeichnung und Engine-Metrik je Step — aus dem Regelwerk uebernommen. */
const STEP_META: Record<number, StepMeta> = {
  10: { ruleClass: "GA Developer Access", cls: "Developer", engine: undefined },
  100: { ruleClass: "GB Advanced Use", cls: "Advanced", engine: undefined },
  200: { ruleClass: "GC Core Use", cls: "Core", engine: undefined },
  300: { ruleClass: "GD Self-Service Use", cls: "SelfService", engine: undefined },
  400: { ruleClass: "GE Engine Use", cls: null, engine: undefined },
  410: { ruleClass: "GF Technical Engine Use", cls: null, engine: undefined },
  420: { ruleClass: "GG Technical Use", cls: null, engine: undefined },
  501: { ruleClass: "Fin.Clos.Cockpit", cls: null, engine: undefined },
  502: { ruleClass: "FSCM/CLM", cls: null, engine: undefined },
  505: { ruleClass: "Utilities", cls: null, engine: undefined },
  506: { ruleClass: "WCM", cls: null, engine: undefined },
  507: { ruleClass: "Transp. Mgmt", cls: null, engine: "TM" },
  508: { ruleClass: "TRM", cls: null, engine: undefined },
  509: { ruleClass: "Fin.Prod.Subledger", cls: null, engine: undefined },
  510: { ruleClass: "Prod. Compl.", cls: null, engine: undefined },
  511: { ruleClass: "Rec. Mgmt", cls: null, engine: undefined },
  512: { ruleClass: "Case Mgmt", cls: null, engine: undefined },
  513: { ruleClass: "HR", cls: null, engine: undefined },
  514: { ruleClass: "Travel Mgmt", cls: null, engine: undefined },
  515: { ruleClass: "Retail", cls: null, engine: undefined },
  516: { ruleClass: "GRC", cls: null, engine: undefined },
  517: { ruleClass: "RE", cls: null, engine: undefined },
  518: { ruleClass: "OT", cls: null, engine: undefined },
  519: { ruleClass: "Payroll", cls: null, engine: undefined },
  520: { ruleClass: "E-Rec", cls: null, engine: undefined },
  521: { ruleClass: "EWM", cls: null, engine: "EWM" },
  522: { ruleClass: "PPM", cls: null, engine: undefined },
  523: { ruleClass: "ICM", cls: null, engine: undefined },
  524: { ruleClass: "FS-CD", cls: null, engine: undefined },
  525: { ruleClass: "FS-CM", cls: null, engine: undefined },
  526: { ruleClass: "Merch.Plan.", cls: null, engine: undefined },
  527: { ruleClass: "Audit Mgmt", cls: null, engine: undefined },
  528: { ruleClass: "3LODefense", cls: null, engine: undefined },
  529: { ruleClass: "Def&amp;Sec", cls: null, engine: undefined },
  530: { ruleClass: "Tax Compl.", cls: null, engine: undefined },
  531: { ruleClass: "LinearAM", cls: null, engine: undefined },
  532: { ruleClass: "GTS", cls: null, engine: "GTS" },
  533: { ruleClass: "MgmtOfChange", cls: null, engine: undefined },
  534: { ruleClass: "CPM WS", cls: null, engine: undefined },
  535: { ruleClass: "CPM RP", cls: null, engine: undefined },
  536: { ruleClass: "CPM I&amp;CM", cls: null, engine: undefined },
  537: { ruleClass: "EHS IM", cls: null, engine: undefined },
  538: { ruleClass: "EHS Health&amp;Safety", cls: null, engine: undefined },
  539: { ruleClass: "Event Management", cls: null, engine: undefined },
  540: { ruleClass: "Bus. Integr. Screening", cls: null, engine: undefined },
  541: { ruleClass: "EHS Env. Mgmt", cls: null, engine: undefined },
  542: { ruleClass: "PSCD", cls: null, engine: undefined },
  543: { ruleClass: "LSO", cls: null, engine: undefined },
  544: { ruleClass: "MRS", cls: null, engine: undefined },
  545: { ruleClass: "IS-H", cls: null, engine: undefined },
  546: { ruleClass: "VIM", cls: null, engine: undefined },
  547: { ruleClass: "BW Query", cls: null, engine: undefined },
  548: { ruleClass: "AIF", cls: null, engine: undefined },
  549: { ruleClass: "O&amp;G Upstream", cls: null, engine: undefined },
};

const TYPE_BY_CODE: Record<string, PceRule["type"]> = { A: "AUTH", D: "DUMMY", E: "ENGI" };

/** Kompakt abgelegt: [Step, Typkuerzel, Objekt, Feld|0, Wert|0]. 0 = leer. */
type Kompakt = [number, string, string, string | 0, string | 0];

const RAW: Kompakt[] = [
  [10,"D","#DEV#",0,0],
  [100,"A","/ECRS/POIA","ACTVT","01"],
  [100,"A","/ECRS/POIA","ACTVT","02"],
  [100,"A","/ECRS/POIA","ACTVT","06"],
  [100,"A","/ECRS/RPHD","ACTVT","01"],
  [100,"A","/ECRS/RPHD","ACTVT","02"],
  [100,"A","/ECRS/RPHD","ACTVT","06"],
  [100,"A","/ECRS/RPHD","ACTVT","43"],
  [100,"A","/ECRS/RPHD","ACTVT","74"],
  [100,"A","/ECRS/RPHD","ACTVT","DL"],
  [100,"A","/IWXBE/USR","ACTVT","01"],
  [100,"A","/IWXBE/USR","ACTVT","02"],
  [100,"A","/IWXBE/USR","ACTVT","06"],
  [100,"A","/SMB/LC_PR","ACTVT","02"],
  [100,"A","/SRMSMC/BO","ACTVT","05"],
  [100,"A","/SRMSMC/BO","ACTVT","06"],
  [100,"A","/SRMSMC/BO","ACTVT","24"],
  [100,"A","/SRMSMC/BO","ACTVT","37"],
  [100,"A","/SRMSMC/BO","ACTVT","43"],
  [100,"A","A_CFG_CMP","ACTVT","01"],
  [100,"A","A_CFG_CMP","ACTVT","02"],
  [100,"A","A_CFG_CMP","ACTVT","05"],
  [100,"A","A_CFG_CMP","ACTVT","06"],
  [100,"A","A_UO","ACTVT","01"],
  [100,"A","A_UO","ACTVT","02"],
  [100,"A","A_UO","ACTVT","06"],
  [100,"A","BEH_SUBSCR","ACTVT","01"],
  [100,"A","BEH_SUBSCR","ACTVT","02"],
  [100,"A","BEH_SUBSCR","ACTVT","06"],
  [100,"A","C_DT_CNV","ACTVT","02"],
  [100,"A","C_DT_FND","ACTVT","02"],
  [100,"A","C_PSCVP_DB","ACTVT","02"],
  [100,"A","C_SIT2INST","ACTVT","06"],
  [100,"A","C_SIT2TYPE","ACTVT","01"],
  [100,"A","C_SIT2TYPE","ACTVT","02"],
  [100,"A","C_SIT2TYPE","ACTVT","06"],
  [100,"A","C_SITNACT","ACTVT","06"],
  [100,"A","C_SITNDEF","ACTVT","06"],
  [100,"A","CIM_ADMIN","ACTVT","01"],
  [100,"A","CM_CON","ACTVT","60"],
  [100,"A","CRM_CONFIG","ACTVT","02"],
  [100,"A","CRM_SE_ENT","ACTVT","02"],
  [100,"A","DMEX_TREE","ACTVT","34"],
  [100,"A","DMEX_TREE","ACTVT","71"],
  [100,"A","E_CS_ADM","ACTVT","06"],
  [100,"A","E_CS_BUNIT","ACTVT","01"],
  [100,"A","E_CS_BUNIT","ACTVT","02"],
  [100,"A","E_CS_BUNIT","ACTVT","06"],
  [100,"A","E_CS_CACTT","ACTVT","02"],
  [100,"A","E_CS_CACTT","ACTVT","05"],
  [100,"A","E_CS_CACTT","ACTVT","16"],
  [100,"A","E_CS_CACTT","ACTVT","95"],
  [100,"A","E_CS_CONGR","ACTVT","01"],
  [100,"A","E_CS_CONGR","ACTVT","02"],
  [100,"A","E_CS_CONGR","ACTVT","06"],
  [100,"A","E_CS_DIMEN","ACTVT","01"],
  [100,"A","E_CS_DIMEN","ACTVT","02"],
  [100,"A","E_CS_DIMEN","ACTVT","06"],
  [100,"A","E_CS_DIMEN","ACTVT","16"],
  [100,"A","E_CS_ITCLG","ACTVT","01"],
  [100,"A","E_CS_ITCLG","ACTVT","02"],
  [100,"A","E_CS_RVERS","ACTVT","02"],
  [100,"A","E_CS_RVERS","ACTVT","16"],
  [100,"A","EI_VIEW_BL","ACTVT","01"],
  [100,"A","EI_VIEW_BL","ACTVT","06"],
  [100,"A","EI_VIEW_CR","ACTVT","01"],
  [100,"A","EI_VIEW_CR","ACTVT","02"],
  [100,"A","EI_VIEW_CR","ACTVT","06"],
  [100,"A","F_ACES_DST","ACTVT","01"],
  [100,"A","F_ACES_DST","ACTVT","02"],
  [100,"A","F_ACES_DST","ACTVT","06"],
  [100,"A","F_ACES_PER","ACTVT","02"],
  [100,"A","F_ACES_PER","ACTVT","30"],
  [100,"A","F_ACES_PER","ACTVT","39"],
  [100,"A","F_ACES_PER","ACTVT","F1"],
  [100,"A","F_ACES_PER","ACTVT","UL"],
  [100,"A","F_AVIK_AVA","ACTVT","02"],
  [100,"A","F_AVIK_BUK","ACTVT","01"],
  [100,"A","F_AVIK_BUK","ACTVT","02"],
  [100,"A","F_AVIK_BUK","ACTVT","06"],
  [100,"A","F_BKPF_BEK","ACTVT","06"],
  [100,"A","F_BKPF_BES","ACTVT","06"],
  [100,"A","F_BKPF_BLA","ACTVT","06"],
  [100,"A","F_BKPF_BLA","ACTVT","10"],
  [100,"A","F_BKPF_BLA","ACTVT","22"],
  [100,"A","F_BKPF_BLA","ACTVT","43"],
  [100,"A","F_BKPF_BLA","ACTVT","85"],
  [100,"A","F_BKPF_BUK","ACTVT","22"],
  [100,"A","F_BKPF_BUK","ACTVT","43"],
  [100,"A","F_BKPF_BUK","ACTVT","C4"],
  [100,"A","F_BKPF_KOA","ACTVT","43"],
  [100,"A","F_BKPF_KOA","ACTVT","85"],
  [100,"A","F_BTTYPE","ACTVT","PA"],
  [100,"A","F_BTTYPE","ACTVT","PB"],
  [100,"A","F_BUK_BUPL","ACTVT","10"],
  [100,"A","F_CUREXRAT","ACTVT","01"],
  [100,"A","F_CUREXRAT","ACTVT","02"],
  [100,"A","F_CUREXRAT","ACTVT","06"],
  [100,"A","F_EF_MAINT","ACTVT","01"],
  [100,"A","F_EF_MAINT","ACTVT","02"],
  [100,"A","F_FAGL_BRG","ACTVT","02"],
  [100,"A","F_FAGL_MPV","ACTVT","02"],
  [100,"A","F_FAGL_OPV","ACTVT","02"],
  [100,"A","F_FBCJ","ACTVT","02"],
  [100,"A","F_FBCJ","ACTVT","06"],
  [100,"A","F_FBCJ","ACTVT","10"],
  [100,"A","F_FBCJ","ACTVT","32"],
  [100,"A","F_FBCJ","ACTVT","36"],
  [100,"A","F_FDSB_BUK","ACTVT","01"],
  [100,"A","F_FDSB_BUK","ACTVT","02"],
  [100,"A","F_FDSB_BUK","ACTVT","06"],
  [100,"A","F_FDSB_GSB","ACTVT","01"],
  [100,"A","F_FDSR_BUK","ACTVT","01"],
  [100,"A","F_FDSR_BUK","ACTVT","02"],
  [100,"A","F_FDSR_GSB","ACTVT","01"],
  [100,"A","F_FEBB_BUK","ACTVT","01"],
  [100,"A","F_FEBB_BUK","ACTVT","02"],
  [100,"A","F_FEBC_BUK","ACTVT","01"],
  [100,"A","F_FEBC_BUK","ACTVT","02"],
  [100,"A","F_FIGM_CLS","ACTVT","01"],
  [100,"A","F_FIGM_CLS","ACTVT","02"],
  [100,"A","F_FIGM_GNT","ACTVT","01"],
  [100,"A","F_FIGM_GNT","ACTVT","02"],
  [100,"A","F_FIGM_GNT","ACTVT","10"],
  [100,"A","F_FIGM_GNT","ACTVT","G1"],
  [100,"A","F_FIGM_GNT","ACTVT","G2"],
  [100,"A","F_FIGM_GNT","ACTVT","G3"],
  [100,"A","F_FIGM_GNT","ACTVT","G4"],
  [100,"A","F_FIGM_PRG","ACTVT","01"],
  [100,"A","F_FIGM_PRG","ACTVT","02"],
  [100,"A","F_FINS_SKF","ACTVT","02"],
  [100,"A","F_GL_CNV","ACTVT","02"],
  [100,"A","F_GL_MAP","ACTVT","02"],
  [100,"A","F_GRIRRCN","ACTVT","34"],
  [100,"A","F_ICA_DC","ACTVT","01"],
  [100,"A","F_ICA_DC","ACTVT","02"],
  [100,"A","F_ICA_DC","ACTVT","06"],
  [100,"A","F_ICA_MJ","ACTVT","02"],
  [100,"A","F_ICA_MJ","ACTVT","06"],
  [100,"A","F_ICA_MJ","ACTVT","10"],
  [100,"A","F_ICA_MM","ACTVT","01"],
  [100,"A","F_ICA_MM","ACTVT","02"],
  [100,"A","F_ICA_MM","ACTVT","06"],
  [100,"A","F_ICA_MM","ACTVT","16"],
  [100,"A","F_ICA_RCON","ACTVT","01"],
  [100,"A","F_ICA_RCON","ACTVT","02"],
  [100,"A","F_ICA_RCON","ACTVT","06"],
  [100,"A","F_ICA_RR","ACTVT","16"],
  [100,"A","F_JPTX_REA","ACTVT","01"],
  [100,"A","F_JPTX_REA","ACTVT","10"],
  [100,"A","F_KKSEPA","ACTVT","01"],
  [100,"A","F_KKSEPA","ACTVT","02"],
  [100,"A","F_KKSEPA","ACTVT","06"],
  [100,"A","F_MANDATE","ACTVT","01"],
  [100,"A","F_MANDATE","ACTVT","02"],
  [100,"A","F_MANDATE","ACTVT","06"],
  [100,"A","F_PMIA_BUK","ACTVT","06"],
  [100,"A","F_PMIA_BUK","ACTVT","DL"],
  [100,"A","F_PST_TMPL","ACTVT","01"],
  [100,"A","F_PST_TMPL","ACTVT","02"],
  [100,"A","F_PST_TMPL","ACTVT","06"],
  [100,"A","F_RE_SC_RU","ACTVT","01"],
  [100,"A","F_RE_SC_RU","ACTVT","02"],
  [100,"A","F_RE_SC_RU","ACTVT","06"],
  [100,"A","F_RE_SC_RU","ACTVT","07"],
  [100,"A","F_RE_SC_RU","ACTVT","21"],
  [100,"A","F_RE_SC_RU","ACTVT","75"],
  [100,"A","F_RE_SC_RU","ACTVT","78"],
  [100,"A","F_REGU_PAY","ACTVT","02"],
  [100,"A","F_RP_BUKRS","ACTVT","02"],
  [100,"A","F_RR_ACCR","ACTVT","10"],
  [100,"A","F_RR_ACCR","ACTVT","48"],
  [100,"A","F_RR_ACCR","ACTVT","93"],
  [100,"A","F_RR_CLOSE","ACTVT","50"],
  [100,"A","F_RR_CLOSE","ACTVT","PB"],
  [100,"A","F_RR_CONTR","ACTVT","01"],
  [100,"A","F_RR_CONTR","ACTVT","A3"],
  [100,"A","F_RR_MFUFI","ACTVT","16"],
  [100,"A","F_RR_PRODD","ACTVT","06"],
  [100,"A","F_RR_STATU","ACTVT","02"],
  [100,"A","F_RRBIZREC","ACTVT","16"],
  [100,"A","F_SAFTN","ACTVT","40"],
  [100,"A","F_SAFTN","ACTVT","41"],
  [100,"A","F_SEPA_MDT","ACTVT","01"],
  [100,"A","F_SEPA_MDT","ACTVT","02"],
  [100,"A","F_SRF_RNTM","ACTVT","01"],
  [100,"A","F_SRF_RNTM","ACTVT","02"],
  [100,"A","F_SRF_RNTM","ACTVT","06"],
  [100,"A","F_SRF_RNTM","ACTVT","D5"],
  [100,"A","F_SRF_RNTM","ACTVT","I1"],
  [100,"A","F_T011","ACTVT","01"],
  [100,"A","F_T011","ACTVT","02"],
  [100,"A","F_T011XBRL","ACTVT","01"],
  [100,"A","F_T011XBRL","ACTVT","02"],
  [100,"A","F_TGCDREP","ACTVT","01"],
  [100,"A","F_TGCDREP","ACTVT","02"],
  [100,"A","F_TGCDREP","ACTVT","06"],
  [100,"A","F_TGCDVER","ACTVT","01"],
  [100,"A","F_TGCDVER","ACTVT","02"],
  [100,"A","F_TGCDVER","ACTVT","06"],
  [100,"A","F_TGCDXBRL","ACTVT","01"],
  [100,"A","F_TGCDXBRL","ACTVT","02"],
  [100,"A","F_TGCDXBRL","ACTVT","06"],
  [100,"A","F_UDM_GRUP","ACTVT","01"],
  [100,"A","F_UDM_GRUP","ACTVT","02"],
  [100,"A","F_UDM_GRUP","ACTVT","06"],
  [100,"A","F_UDM_SGMT","ACTVT","02"],
  [100,"A","F_UKM_SGMT","ACTVT","06"],
  [100,"A","FI_CS_RULE","ACTVT","01"],
  [100,"A","FI_CS_RULE","ACTVT","02"],
  [100,"A","FI_CS_RULE","ACTVT","06"],
  [100,"A","FKSA_VB","ACTVT","16"],
  [100,"A","FOT_B2A_V","ACTVT","01"],
  [100,"A","FOT_B2A_V","ACTVT","02"],
  [100,"A","FOT_B2A_V","ACTVT","06"],
  [100,"A","FOT_B2A_V","ACTVT","A9"],
  [100,"A","FPS_CDICT","ACTVT","01"],
  [100,"A","FPS_CDICT","ACTVT","06"],
  [100,"A","FQM_FLOW","ACTVT","01"],
  [100,"A","FQM_FLOW","ACTVT","02"],
  [100,"A","FQM_FLOW","ACTVT","06"],
  [100,"A","FQM_FLOW","ACTVT","25"],
  [100,"A","G_881_GRLD","ACTVT","01"],
  [100,"A","G_881_GRLD","ACTVT","02"],
  [100,"A","G_881_GRLD","ACTVT","06"],
  [100,"A","G_ALLOCTN","ACTVT","01"],
  [100,"A","G_ALLOCTN","ACTVT","02"],
  [100,"A","G_ALLOCTN","ACTVT","06"],
  [100,"A","G_ALLOCTN","ACTVT","16"],
  [100,"A","G_ALLOCTN","ACTVT","48"],
  [100,"A","G_GB93_","ACTVT","01"],
  [100,"A","G_GB93_","ACTVT","02"],
  [100,"A","G_GB93_","ACTVT","06"],
  [100,"A","GLE_ECS","ACTVT","02"],
  [100,"A","GLE_ECS","ACTVT","06"],
  [100,"A","GLE_ECS","ACTVT","36"],
  [100,"A","GLE_ECS","ACTVT","A8"],
  [100,"A","GTS_AUART","ACTVT","02"],
  [100,"A","ITM_BUKRS","ACTVT","02"],
  [100,"A","ITM_CLS_LR","ACTVT","02"],
  [100,"A","ITM_CLS_NC","ACTVT","02"],
  [100,"A","ITM_CLS_NS","ACTVT","02"],
  [100,"A","ITM_LGREG","ACTVT","02"],
  [100,"A","ITM_LGREG","ACTVT","16"],
  [100,"A","ITM_LMGM","ACTVT","02"],
  [100,"A","J_3RF_REGI","ACTVT","23"],
  [100,"A","K_ALLOCGRP","ACTVT","01"],
  [100,"A","K_ALLOCGRP","ACTVT","02"],
  [100,"A","K_ALLOCGRP","ACTVT","06"],
  [100,"A","K_ALLOCMNT","ACTVT","01"],
  [100,"A","K_ALLOCMNT","ACTVT","02"],
  [100,"A","K_ALLOCMNT","ACTVT","06"],
  [100,"A","K_ALLOCRUN","ACTVT","16"],
  [100,"A","K_ALLOCRUN","ACTVT","48"],
  [100,"A","K_ALLOCRUN","ACTVT","85"],
  [100,"A","K_ALLOCTAG","ACTVT","01"],
  [100,"A","K_ALLOCTAG","ACTVT","02"],
  [100,"A","K_ALLOCTAG","ACTVT","06"],
  [100,"A","K_ALLOMNT2","ACTVT","01"],
  [100,"A","K_ALLOMNT2","ACTVT","02"],
  [100,"A","K_ALLOMNT2","ACTVT","06"],
  [100,"A","K_ALLORUN2","ACTVT","16"],
  [100,"A","K_ALLORUN2","ACTVT","48"],
  [100,"A","K_ALLORUN2","ACTVT","85"],
  [100,"A","K_AUFK_SET","ACTVT","02"],
  [100,"A","K_CBPR_SET","ACTVT","02"],
  [100,"A","K_COSTRATE","ACTVT","02"],
  [100,"A","K_CSKA_SET","ACTVT","02"],
  [100,"A","K_CSLA","ACTVT","01"],
  [100,"A","K_CSLA","ACTVT","02"],
  [100,"A","K_CSLA","ACTVT","06"],
  [100,"A","K_CSLA_SET","ACTVT","01"],
  [100,"A","K_CSLA_SET","ACTVT","02"],
  [100,"A","K_CSLA_SET","ACTVT","06"],
  [100,"A","K_EB_LOG","ACTVT","06"],
  [100,"A","K_EB_POST","ACTVT","10"],
  [100,"A","K_EB_POST","ACTVT","48"],
  [100,"A","K_FVMK","ACTVT","43"],
  [100,"A","K_FVMK","ACTVT","44"],
  [100,"A","K_FVMK","ACTVT","45"],
  [100,"A","K_INA_SET","ACTVT","07"],
  [100,"A","K_KA03","ACTVT","02"],
  [100,"A","K_KA03_SET","ACTVT","02"],
  [100,"A","K_KED_UM","ACTVT","01"],
  [100,"A","K_KED_UM","ACTVT","02"],
  [100,"A","K_KED_UM","ACTVT","16"],
  [100,"A","K_KEDT_TC","ACTVT","02"],
  [100,"A","K_KEDT_TC","ACTVT","16"],
  [100,"A","K_KEKO","ACTVT","06"],
  [100,"A","K_ML_VA","ACTVT","16"],
  [100,"A","K_ML_VA","ACTVT","40"],
  [100,"A","K_ML_VA","ACTVT","45"],
  [100,"A","K_MLPR_VA","ACTVT","16"],
  [100,"A","K_MLPR_VA","ACTVT","44"],
  [100,"A","K_PCAR_REP","ACTVT","01"],
  [100,"A","K_PCAR_REP","ACTVT","02"],
  [100,"A","K_PCAR_REP","ACTVT","06"],
  [100,"A","K_PCAR_REP","ACTVT","76"],
  [100,"A","K_PKSA","ACTVT","01"],
  [100,"A","K_TP_VALU","ACTVT","02"],
  [100,"A","K_TP_VALU","ACTVT","10"],
  [100,"A","K_TRR_AP","ACTVT","02"],
  [100,"A","K_ZBASSL","ACTVT","02"],
  [100,"A","K_ZENTSL","ACTVT","02"],
  [100,"A","K_ZKALSM","ACTVT","02"],
  [100,"A","K_ZZUSSL","ACTVT","02"],
  [100,"A","M_CAT_UTYP","ACTVT","02"],
  [100,"A","M_CTLG_PRP","ACTVT","07"],
  [100,"A","M_EINF_EKG","ACTVT","01"],
  [100,"A","M_EINF_EKG","ACTVT","06"],
  [100,"A","M_EINF_EKO","ACTVT","01"],
  [100,"A","M_EINF_EKO","ACTVT","06"],
  [100,"A","M_EINF_WRK","ACTVT","01"],
  [100,"A","M_EINF_WRK","ACTVT","06"],
  [100,"A","M_INTG_XTR","ACTVT","16"],
  [100,"A","M_LIFO_MLY","ACTVT","02"],
  [100,"A","M_MMSEVAL","ACTVT","02"],
  [100,"A","M_NIWE_BIL","ACTVT","02"],
  [100,"A","M_QUOT_WRK","ACTVT","02"],
  [100,"A","M_QUOT_WRK","ACTVT","06"],
  [100,"A","M_RECH_BUK","ACTVT","01"],
  [100,"A","M_RECH_BUK","ACTVT","81"],
  [100,"A","M_RECH_WRK","ACTVT","01"],
  [100,"A","M_RECH_WRK","ACTVT","81"],
  [100,"A","Q_INSPQUAL","ACTVT","02"],
  [100,"A","RLFW_SCHED","ACTVT","01"],
  [100,"A","RLFW_SCHED","ACTVT","02"],
  [100,"A","RLFW_SCHED","ACTVT","06"],
  [100,"A","S_APJ_JCE","ACTVT","B8"],
  [100,"A","S_ARCD_OBJ","ACTVT","16"],
  [100,"A","S_ATO_COL","ACTVT","02"],
  [100,"A","S_ATO_COL","ACTVT","39"],
  [100,"A","S_ATO_COL","ACTVT","59"],
  [100,"A","S_ATO_COL","ACTVT","60"],
  [100,"A","S_ATO_COL","ACTVT","61"],
  [100,"A","S_ATO_SET","ACTVT","02"],
  [100,"A","S_ATO_SET","ACTVT","78"],
  [100,"A","S_BGRFC_QG","ACTVT","02"],
  [100,"A","S_BGRFC_QG","ACTVT","05"],
  [100,"A","S_BGRFC_QG","ACTVT","06"],
  [100,"A","S_BGRFC_QG","ACTVT","16"],
  [100,"A","S_BGRFC_QG","ACTVT","95"],
  [100,"A","S_CALENDAR","ACTVT","02"],
  [100,"A","S_COM_CSTP","ACTVT","01"],
  [100,"A","S_COM_CSTP","ACTVT","02"],
  [100,"A","S_COM_CSTP","ACTVT","06"],
  [100,"A","S_DMC_S_R","ACTVT","01"],
  [100,"A","S_DMC_S_R","ACTVT","16"],
  [100,"A","S_DMC_S_R","ACTVT","34"],
  [100,"A","S_DMC_S_R","ACTVT","40"],
  [100,"A","S_DMC_S_R","ACTVT","41"],
  [100,"A","S_DMC_S_R","ACTVT","64"],
  [100,"A","S_DMCC","ACTVT","F1"],
  [100,"A","S_DMIS","ACTVT","01"],
  [100,"A","S_DMIS","ACTVT","02"],
  [100,"A","S_DMIS","ACTVT","06"],
  [100,"A","S_DMIS","ACTVT","16"],
  [100,"A","S_DMIS","ACTVT","64"],
  [100,"A","S_DMIS_ANA","ACTVT","01"],
  [100,"A","S_DMIS_ANA","ACTVT","02"],
  [100,"A","S_DMIS_ANA","ACTVT","06"],
  [100,"A","S_DMIS_ANA","ACTVT","16"],
  [100,"A","S_DMIS_MOM","ACTVT","01"],
  [100,"A","S_DMIS_MOM","ACTVT","02"],
  [100,"A","S_DMIS_MOM","ACTVT","06"],
  [100,"A","S_DMIS_RFC","ACTVT","01"],
  [100,"A","S_DMIS_RFC","ACTVT","02"],
  [100,"A","S_DMIS_RFC","ACTVT","06"],
  [100,"A","S_DMIS_RFC","ACTVT","16"],
  [100,"A","S_DMIS_RFC","ACTVT","34"],
  [100,"A","S_DMIS_SL","ACTVT","01"],
  [100,"A","S_DMIS_SL","ACTVT","02"],
  [100,"A","S_DMIS_SL","ACTVT","16"],
  [100,"A","S_DMIS_TR","ACTVT","01"],
  [100,"A","S_DMIS_TR","ACTVT","02"],
  [100,"A","S_DMIS_TR","ACTVT","05"],
  [100,"A","S_DMIS_TR","ACTVT","06"],
  [100,"A","S_DMIS_TR","ACTVT","07"],
  [100,"A","S_DMIS_TR","ACTVT","16"],
  [100,"A","S_DMIS_TR","ACTVT","43"],
  [100,"A","S_DTDC","ACTVT","70"],
  [100,"A","S_EIT","ACTVT","02"],
  [100,"A","S_EIT","ACTVT","39"],
  [100,"A","S_EIT","ACTVT","60"],
  [100,"A","S_EIT","ACTVT","61"],
  [100,"A","S_FTGL_CUS","ACTVT","02"],
  [100,"A","S_HTTP_TRU","ACTVT","02"],
  [100,"A","S_IAM_BR","ACTVT","01"],
  [100,"A","S_IAM_BR","ACTVT","02"],
  [100,"A","S_IAM_BR","ACTVT","06"],
  [100,"A","S_IAM_BR","ACTVT","22"],
  [100,"A","S_INF_CONF","ACTVT","01"],
  [100,"A","S_INF_CONF","ACTVT","02"],
  [100,"A","S_INF_CONF","ACTVT","06"],
  [100,"A","S_IRM_OG","ACTVT","02"],
  [100,"A","S_IRM_RG","ACTVT","02"],
  [100,"A","S_KSR_CFG","ACTVT","01"],
  [100,"A","S_KSR_CFG","ACTVT","07"],
  [100,"A","S_OA2C_ADM","ACTVT","01"],
  [100,"A","S_OA2C_ADM","ACTVT","02"],
  [100,"A","S_OA2C_ADM","ACTVT","06"],
  [100,"A","S_ODQ_MON","ACTVT","02"],
  [100,"A","S_POPQ","ACTVT","01"],
  [100,"A","S_POPQ","ACTVT","02"],
  [100,"A","S_POPQ","ACTVT","06"],
  [100,"A","S_PSE_ADM","ACTVT","01"],
  [100,"A","S_PSE_ADM","ACTVT","02"],
  [100,"A","S_PSE_ADM","ACTVT","06"],
  [100,"A","S_Q_ADM","ACTVT","02"],
  [100,"A","S_Q_ADM","ACTVT","16"],
  [100,"A","S_Q_GOVERN","ACTVT","02"],
  [100,"A","S_RAL_CFG","ACTVT","01"],
  [100,"A","S_RAL_CFG","ACTVT","02"],
  [100,"A","S_RAL_CFG","ACTVT","06"],
  [100,"A","S_RAL_CFG","ACTVT","D1"],
  [100,"A","S_SAIS","ACTVT","16"],
  [100,"A","S_SAIS","ACTVT","70"],
  [100,"A","S_SAIS","ACTVT","71"],
  [100,"A","S_SECPOL","ACTVT","22"],
  [100,"A","S_SMB_ACTV","ACTVT","02"],
  [100,"A","S_SMB_ADM","ACTVT","02"],
  [100,"A","S_SMB_AUTH","ACTVT","02"],
  [100,"A","S_SMB_LCPR","ACTVT","02"],
  [100,"A","S_SRT_SEQ","ACTVT","02"],
  [100,"A","S_SRT_SEQ","ACTVT","05"],
  [100,"A","S_SRT_SEQ","ACTVT","06"],
  [100,"A","S_SRT_SEQ","ACTVT","69"],
  [100,"A","S_SRT_SEQ","ACTVT","81"],
  [100,"A","S_TRANSPRT","ACTVT","01"],
  [100,"A","S_TRANSPRT","ACTVT","02"],
  [100,"A","S_TRANSPRT","ACTVT","43"],
  [100,"A","S_UCON_WHI","ACTVT","02"],
  [100,"A","S_USER_AGR","ACTVT","01"],
  [100,"A","S_USER_AGR","ACTVT","02"],
  [100,"A","S_USER_AGR","ACTVT","06"],
  [100,"A","S_USER_AGR","ACTVT","22"],
  [100,"A","S_USER_AGR","ACTVT","64"],
  [100,"A","S_USER_AGR","ACTVT","UL"],
  [100,"A","S_USER_BLK","ACTVT","06"],
  [100,"A","S_USER_BLK","ACTVT","91"],
  [100,"A","S_USER_BLK","ACTVT","B4"],
  [100,"A","S_USER_GRP","ACTVT","01"],
  [100,"A","S_USER_GRP","ACTVT","22"],
  [100,"A","S_USER_GRP","ACTVT","78"],
  [100,"A","S_USER_PRO","ACTVT","01"],
  [100,"A","S_USER_PRO","ACTVT","02"],
  [100,"A","S_USER_PRO","ACTVT","06"],
  [100,"A","S_USER_PRO","ACTVT","07"],
  [100,"A","S_USER_PRO","ACTVT","22"],
  [100,"A","S_USER_SAS","ACTVT","22"],
  [100,"A","S_VIEW_REP","ACTVT","01"],
  [100,"A","S_VIEW_REP","ACTVT","02"],
  [100,"A","S_VIEW_REP","ACTVT","06"],
  [100,"A","S_WFDEFADM","ACTVT","W4"],
  [100,"A","S_WFDEFADM","ACTVT","W5"],
  [100,"A","S_WFDEFADM","ACTVT","W6"],
  [100,"A","S_WFDEFADM","ACTVT","W7"],
  [100,"A","S_WFDEFADM","ACTVT","W8"],
  [100,"A","S_WFFLXDEF","ACTVT","01"],
  [100,"A","S_WFFLXDEF","ACTVT","02"],
  [100,"A","S_WFFLXDEF","ACTVT","06"],
  [100,"A","S_WFFLXDEF","ACTVT","60"],
  [100,"A","S_WFFLXDEF","ACTVT","61"],
  [100,"A","S_WFFLXDEF","ACTVT","63"],
  [100,"A","S_WFFLXDEF","ACTVT","67"],
  [100,"A","S_WFFLXDEF","ACTVT","95"],
  [100,"A","S_YCM_APS","ACTVT","01"],
  [100,"A","S_YCM_APS","ACTVT","02"],
  [100,"A","S_YCM_APS","ACTVT","06"],
  [100,"A","S_YCM_APS","ACTVT","16"],
  [100,"A","S_YCM_APS","ACTVT","39"],
  [100,"A","S_YCM_APS","ACTVT","AF"],
  [100,"A","V_VBUK_FRE","ACTVT","23"],
  [200,"A","V_KNKK_FRE","ACTVT","23"],
  [200,"A","/BSNAGT/FM","ACTVT","02"],
  [200,"A","/BSNAGT/PL","ACTVT","02"],
  [200,"A","/BSNAGT/PU","ACTVT","16"],
  [200,"A","/BSNAGT/RP","ACTVT","02"],
  [200,"A","/BSNAGT/SA","ACTVT","16"],
  [200,"A","/BSNAGT/SN","ACTVT","16"],
  [200,"A","/BSNAGT/ST","ACTVT","02"],
  [200,"A","/S4PPM/BGT","ACTVT","02"],
  [200,"A","/S4PPM/PRJ","ACTVT","01"],
  [200,"A","/S4PPM/PRJ","ACTVT","02"],
  [200,"A","/S4PPM/PRJ","ACTVT","06"],
  [200,"A","/S4PPM/PRJ","ACTVT","24"],
  [200,"A","/S4PPM/PRR","ACTVT","01"],
  [200,"A","/S4PPM/PRR","ACTVT","02"],
  [200,"A","/S4PPM/PRR","ACTVT","06"],
  [200,"A","A_S_ANLKL","ACTVT","01"],
  [200,"A","A_S_ANLKL","ACTVT","02"],
  [200,"A","A_S_ANLKL","ACTVT","05"],
  [200,"A","A_S_ANLKL","ACTVT","06"],
  [200,"A","A_S_MD","ACTVT","01"],
  [200,"A","A_S_MD","ACTVT","02"],
  [200,"A","A_S_MD","ACTVT","05"],
  [200,"A","A_S_MD","ACTVT","06"],
  [200,"A","B_BUP_PCPT","ACTVT","05"],
  [200,"A","B_BUP_PCPT","ACTVT","95"],
  [200,"A","B_BUPA_CRS","ACTVT","02"],
  [200,"A","B_BUPA_GRP","ACTVT","06"],
  [200,"A","B_BUPA_RAT","ACTVT","01"],
  [200,"A","B_BUPA_RAT","ACTVT","02"],
  [200,"A","B_BUPA_RAT","ACTVT","06"],
  [200,"A","C_APO_LOC","ACTVT","01"],
  [200,"A","C_APO_LOC","ACTVT","02"],
  [200,"A","C_APO_PROD","ACTVT","06"],
  [200,"A","C_APO_PROD","ACTVT","16"],
  [200,"A","C_APO_RELH","ACTVT","01"],
  [200,"A","C_APO_RELH","ACTVT","02"],
  [200,"A","C_APO_VERS","ACTVT","01"],
  [200,"A","C_APO_VERS","ACTVT","02"],
  [200,"A","C_APO_VERS","ACTVT","06"],
  [200,"A","C_APO_VERS","ACTVT","16"],
  [200,"A","C_ARPL_GT","ACTVT","01"],
  [200,"A","C_ARPL_GT","ACTVT","02"],
  [200,"A","C_ARPL_GT","ACTVT","06"],
  [200,"A","C_ARPL_WRK","ACTVT","01"],
  [200,"A","C_ARPL_WRK","ACTVT","02"],
  [200,"A","C_ARPL_WRK","ACTVT","05"],
  [200,"A","C_ARPL_WRK","ACTVT","06"],
  [200,"A","C_ARPL_WRK","ACTVT","11"],
  [200,"A","C_ARPL_WRK","ACTVT","76"],
  [200,"A","C_CABN","ACTVT","01"],
  [200,"A","C_CABN","ACTVT","02"],
  [200,"A","C_CRFH_BRG","ACTVT","01"],
  [200,"A","C_CRFH_BRG","ACTVT","02"],
  [200,"A","C_CRFH_BRG","ACTVT","06"],
  [200,"A","C_DEMAND","ACTVT","01"],
  [200,"A","C_DEMAND","ACTVT","02"],
  [200,"A","C_DEMAND","ACTVT","06"],
  [200,"A","C_ETOPROBJ","ACTVT","01"],
  [200,"A","C_ETOPROBJ","ACTVT","02"],
  [200,"A","C_ETOPROBJ","ACTVT","06"],
  [200,"A","C_FVER_WRK","ACTVT","01"],
  [200,"A","C_FVER_WRK","ACTVT","02"],
  [200,"A","C_FVER_WRK","ACTVT","06"],
  [200,"A","C_KAPA_PLA","ACTVT","16"],
  [200,"A","C_KLAH_BKP","ACTVT","06"],
  [200,"A","C_PPE_INST","ACTVT","01"],
  [200,"A","C_PPE_INST","ACTVT","02"],
  [200,"A","C_PPE_INST","ACTVT","06"],
  [200,"A","C_PPE_PAID","ACTVT","01"],
  [200,"A","C_PPE_PAID","ACTVT","02"],
  [200,"A","C_PPE_PAID","ACTVT","06"],
  [200,"A","C_PRJBLGRQ","ACTVT","01"],
  [200,"A","C_PRJBLGRQ","ACTVT","02"],
  [200,"A","C_PVS_PATY","ACTVT","01"],
  [200,"A","C_PVS_PATY","ACTVT","02"],
  [200,"A","C_PVS_PATY","ACTVT","06"],
  [200,"A","C_PVS_PNID","ACTVT","01"],
  [200,"A","C_PVS_PNID","ACTVT","02"],
  [200,"A","C_PVS_PNID","ACTVT","06"],
  [200,"A","C_PVS_PVID","ACTVT","01"],
  [200,"A","C_PVS_PVID","ACTVT","02"],
  [200,"A","C_PVS_PVID","ACTVT","06"],
  [200,"A","C_PVS_PVTY","ACTVT","01"],
  [200,"A","C_PVS_PVTY","ACTVT","02"],
  [200,"A","C_PVS_PVTY","ACTVT","06"],
  [200,"A","C_ROUT","ACTVT","01"],
  [200,"A","C_ROUT","ACTVT","02"],
  [200,"A","C_ROUT","ACTVT","06"],
  [200,"A","C_ROUT","ACTVT","41"],
  [200,"A","C_SITNDEF","ACTVT","01"],
  [200,"A","C_SITNDEF","ACTVT","02"],
  [200,"A","C_SITNINST","ACTVT","01"],
  [200,"A","C_SITNINST","ACTVT","06"],
  [200,"A","C_STUE_BER","ACTVT","01"],
  [200,"A","C_STUE_BER","ACTVT","02"],
  [200,"A","C_STUE_BER","ACTVT","06"],
  [200,"A","C_STUE_BER","ACTVT","24"],
  [200,"A","C_STUE_BER","ACTVT","41"],
  [200,"A","C_STUE_MAI","ACTVT","23"],
  [200,"A","C_STUE_WRK","ACTVT","01"],
  [200,"A","C_STUE_WRK","ACTVT","02"],
  [200,"A","C_VC_MODEL","ACTVT","01"],
  [200,"A","C_VC_MODEL","ACTVT","02"],
  [200,"A","C_VCM_INST","ACTVT","02"],
  [200,"A","C_WTY_CLM","ACTVT","01"],
  [200,"A","C_WTY_CLM","ACTVT","02"],
  [200,"A","CA_OC_ORI","ACTVT","01"],
  [200,"A","CA_OC_ORI","ACTVT","02"],
  [200,"A","CPRO_DPO","ACTVT","01"],
  [200,"A","CRM_ORD_OE","ACTVT","01"],
  [200,"A","CRM_ORD_OP","ACTVT","06"],
  [200,"A","CRM_ORD_PC","ACTVT","02"],
  [200,"A","CRM_ORD_PR","ACTVT","06"],
  [200,"A","DRF_ADM","ACTVT","06"],
  [200,"A","DRF_ADM","ACTVT","23"],
  [200,"A","DRF_ADM","ACTVT","66"],
  [200,"A","DRF_ADM","ACTVT","94"],
  [200,"A","DRF_RECEIV","ACTVT","16"],
  [200,"A","EAMS_AUTCF","ACTVT","16"],
  [200,"A","F_BKPF_BEB","ACTVT","01"],
  [200,"A","F_BKPF_BEK","ACTVT","01"],
  [200,"A","F_BKPF_BES","ACTVT","01"],
  [200,"A","F_BKPF_BLA","ACTVT","01"],
  [200,"A","F_BKPF_BUK","ACTVT","06"],
  [200,"A","F_BKPF_BUK","ACTVT","07"],
  [200,"A","F_BKPF_KOA","ACTVT","06"],
  [200,"A","F_BNKA_BUK","ACTVT","02"],
  [200,"A","F_BNKA_MAN","ACTVT","01"],
  [200,"A","F_BNKA_MAN","ACTVT","02"],
  [200,"A","F_BNKA_MAO","ACTVT","01"],
  [200,"A","F_BNKA_MAO","ACTVT","02"],
  [200,"A","F_BUAC_PST","ACTVT","01"],
  [200,"A","F_FAGL_SEG","ACTVT","01"],
  [200,"A","F_FAGL_SEG","ACTVT","02"],
  [200,"A","F_KNA1_APP","ACTVT","01"],
  [200,"A","F_KNA1_APP","ACTVT","02"],
  [200,"A","F_KNA1_APP","ACTVT","05"],
  [200,"A","F_KNA1_APP","ACTVT","06"],
  [200,"A","F_KNA1_APP","ACTVT","C1"],
  [200,"A","F_KNA1_APP","ACTVT","C8"],
  [200,"A","F_KNA1_BED","ACTVT","C8"],
  [200,"A","F_KNA1_BUK","ACTVT","01"],
  [200,"A","F_KNA1_BUK","ACTVT","05"],
  [200,"A","F_KNA1_BUK","ACTVT","06"],
  [200,"A","F_KNA1_BUK","ACTVT","C8"],
  [200,"A","F_KNA1_GEN","ACTVT","C8"],
  [200,"A","F_KNA1_GRP","ACTVT","05"],
  [200,"A","F_KNA1_GRP","ACTVT","06"],
  [200,"A","F_KNA1_GRP","ACTVT","C8"],
  [200,"A","F_KNA1_KGD","ACTVT","26"],
  [200,"A","F_LFA1_APP","ACTVT","05"],
  [200,"A","F_LFA1_APP","ACTVT","06"],
  [200,"A","F_LFA1_APP","ACTVT","C8"],
  [200,"A","F_LFA1_BEK","ACTVT","C8"],
  [200,"A","F_LFA1_BUK","ACTVT","05"],
  [200,"A","F_LFA1_BUK","ACTVT","06"],
  [200,"A","F_LFA1_BUK","ACTVT","C8"],
  [200,"A","F_LFA1_GEN","ACTVT","C8"],
  [200,"A","F_LFA1_GRP","ACTVT","05"],
  [200,"A","F_LFA1_GRP","ACTVT","06"],
  [200,"A","F_LFA1_GRP","ACTVT","C8"],
  [200,"A","F_RRRAI","ACTVT","01"],
  [200,"A","F_UKM_SGMT","ACTVT","02"],
  [200,"A","I_LOGBOOK","ACTVT","01"],
  [200,"A","I_LOGBOOK","ACTVT","02"],
  [200,"A","I_LOGBOOK","ACTVT","06"],
  [200,"A","I_PLBKTTY","ACTVT","01"],
  [200,"A","I_PLBKTTY","ACTVT","02"],
  [200,"A","I_ROUT","ACTVT","41"],
  [200,"A","I_ROUT2","ACTVT","01"],
  [200,"A","I_ROUT2","ACTVT","02"],
  [200,"A","JIT_DSG","ACTVT","01"],
  [200,"A","JIT_DSG","ACTVT","02"],
  [200,"A","JIT_DSG","ACTVT","06"],
  [200,"A","JIT_DSG","ACTVT","07"],
  [200,"A","JIT_DSG","ACTVT","16"],
  [200,"A","JIT_DSG","ACTVT","63"],
  [200,"A","JIT_DSG","ACTVT","H1"],
  [200,"A","JIT_PGD","ACTVT","01"],
  [200,"A","JIT_PGD","ACTVT","02"],
  [200,"A","JIT_PGD","ACTVT","07"],
  [200,"A","JIT_PGD","ACTVT","16"],
  [200,"A","JIT_S2C_CR","ACTVT","01"],
  [200,"A","K_ACDOCP_P","ACTVT","06"],
  [200,"A","K_BDGT_DOC","ACTVT","02"],
  [200,"A","K_BDGT_DOC","ACTVT","06"],
  [200,"A","K_CSKS","ACTVT","01"],
  [200,"A","K_CSKS","ACTVT","02"],
  [200,"A","K_CSKS","ACTVT","06"],
  [200,"A","K_CSKS","ACTVT","63"],
  [200,"A","K_CSKS_BUD","ACTVT","02"],
  [200,"A","K_CSKS_SET","ACTVT","02"],
  [200,"A","K_CSKS_SET","ACTVT","06"],
  [200,"A","K_ML_VA","ACTVT","02"],
  [200,"A","K_PCA_MD","ACTVT","01"],
  [200,"A","K_PCA_MD","ACTVT","02"],
  [200,"A","K_PCAP_SET","ACTVT","01"],
  [200,"A","K_PCAP_SET","ACTVT","02"],
  [200,"A","K_PCAP_SET","ACTVT","06"],
  [200,"A","K_VRGNG","ACTVT","01"],
  [200,"A","K_VRGNG","ACTVT","02"],
  [200,"A","K_VRGNG","ACTVT","48"],
  [200,"A","K_WIP","ACTVT","02"],
  [200,"A","K_WIP_BU","ACTVT","02"],
  [200,"A","K_WIP_PC","ACTVT","02"],
  [200,"A","M_LFM1_EKO","ACTVT","01"],
  [200,"A","M_LFM1_EKO","ACTVT","02"],
  [200,"A","M_LFM1_EKO","ACTVT","05"],
  [200,"A","M_LFM1_EKO","ACTVT","06"],
  [200,"A","M_MATE_BUK","ACTVT","01"],
  [200,"A","M_MATE_BUK","ACTVT","02"],
  [200,"A","M_MATE_BUK","ACTVT","06"],
  [200,"A","M_MATE_DRV","ACTVT","02"],
  [200,"A","M_MATE_DRV","ACTVT","43"],
  [200,"A","M_MATE_LGN","ACTVT","01"],
  [200,"A","M_MATE_LGN","ACTVT","02"],
  [200,"A","M_MATE_LGN","ACTVT","06"],
  [200,"A","M_MATE_MEX","ACTVT","01"],
  [200,"A","M_MATE_MEX","ACTVT","02"],
  [200,"A","M_MATE_MZP","ACTVT","01"],
  [200,"A","M_MATE_MZP","ACTVT","02"],
  [200,"A","MDC_ADMIN","ACTVT","60"],
  [200,"A","MDC_LOAD","ACTVT","61"],
  [200,"A","MDG_IDM","ACTVT","02"],
  [200,"A","MDQ_EVAL","ACTVT","01"],
  [200,"A","MDQ_EVAL","ACTVT","02"],
  [200,"A","MDQ_EVAL","ACTVT","06"],
  [200,"A","MFG_PLNR","ACTVT","01"],
  [200,"A","MFG_PLNR","ACTVT","02"],
  [200,"A","PLM_PPEPF","ACTVT","01"],
  [200,"A","PLM_PPEPF","ACTVT","02"],
  [200,"A","PLM_PPEPF","ACTVT","06"],
  [200,"A","PLM_PPEPF","ACTVT","36"],
  [200,"A","PLM_PPEPF","ACTVT","70"],
  [200,"A","PLM_PPEPF","ACTVT","71"],
  [200,"A","PLM_PPEPF","ACTVT","PU"],
  [200,"A","PLM_PPEPI","ACTVT","01"],
  [200,"A","PLM_PPEPI","ACTVT","02"],
  [200,"A","PLM_PPEPI","ACTVT","06"],
  [200,"A","PLM_PPEPI","ACTVT","22"],
  [200,"A","PLM_PPEPI","ACTVT","36"],
  [200,"A","PLM_PPEPI","ACTVT","70"],
  [200,"A","PLM_PPEPI","ACTVT","71"],
  [200,"A","PLM_PPEPI","ACTVT","PU"],
  [200,"A","PLM_PPEPIV","ACTVT","01"],
  [200,"A","PLM_PPEPIV","ACTVT","02"],
  [200,"A","PLM_PPEPIV","ACTVT","06"],
  [200,"A","PLM_PPEPIV","ACTVT","36"],
  [200,"A","PLM_PPEPIV","ACTVT","70"],
  [200,"A","PLM_PPEPIV","ACTVT","71"],
  [200,"A","PLM_PPEPIV","ACTVT","PU"],
  [200,"A","PLM_PPEPV","ACTVT","01"],
  [200,"A","PLM_PPEPV","ACTVT","02"],
  [200,"A","PLM_PPEPV","ACTVT","06"],
  [200,"A","PLM_PPEPV","ACTVT","36"],
  [200,"A","PLM_PPEPV","ACTVT","70"],
  [200,"A","PLM_PPEPV","ACTVT","71"],
  [200,"A","PLM_PPEPV","ACTVT","PU"],
  [200,"A","Q_CERT_IN","ACTVT","02"],
  [200,"A","Q_CERT_IN","ACTVT","A3"],
  [200,"A","Q_CGRP_ACT","ACTVT","02"],
  [200,"A","Q_CSSET_AC","ACTVT","02"],
  [200,"A","Q_CTRLCHRT","ACTVT","B2"],
  [200,"A","Q_DYNMODRL","ACTVT","01"],
  [200,"A","Q_DYNMODRL","ACTVT","02"],
  [200,"A","Q_INSPPNT","ACTVT","01"],
  [200,"A","Q_PRSLVPR","ACTVT","01"],
  [200,"A","Q_PRSLVPR","ACTVT","02"],
  [200,"A","Q_PRSLVPR","ACTVT","06"],
  [200,"A","Q_PRSLVPR","ACTVT","B2"],
  [200,"A","Q_PRSLVPR","ACTVT","C5"],
  [200,"A","Q_QIRECPRC","ACTVT","01"],
  [200,"A","Q_QIRECPRC","ACTVT","02"],
  [200,"A","Q_QLEVEL","ACTVT","02"],
  [200,"A","Q_QLEVEL","ACTVT","06"],
  [200,"A","Q_SMPLPROC","ACTVT","01"],
  [200,"A","Q_SMPLPROC","ACTVT","02"],
  [200,"A","Q_SMPLSCHM","ACTVT","01"],
  [200,"A","Q_SMPLSCHM","ACTVT","02"],
  [200,"A","S_ABPLNGVS","ACTVT","01"],
  [200,"A","S_ABPLNGVS","ACTVT","02"],
  [200,"A","S_ABPLNGVS","ACTVT","06"],
  [200,"A","S_ABPLNGVS","ACTVT","07"],
  [200,"A","S_ABPLNGVS","ACTVT","16"],
  [200,"A","S_CDR_ADM","ACTVT","01"],
  [200,"A","S_CDR_ADM","ACTVT","02"],
  [200,"A","S_CDR_ADM","ACTVT","06"],
  [200,"A","S_CDR_ADM","ACTVT","16"],
  [200,"A","S_CDR_ADM","ACTVT","66"],
  [200,"A","S_CDR_ADM","ACTVT","DL"],
  [200,"A","S_DEVELOP","ACTVT","01"],
  [200,"A","S_DEVELOP","ACTVT","02"],
  [200,"A","S_DEVELOP","ACTVT","06"],
  [200,"A","S_DEVELOP","ACTVT","07"],
  [200,"A","S_ISM_DEPL","ACTVT","01"],
  [200,"A","S_ISM_DEPL","ACTVT","02"],
  [200,"A","S_ISM_DEPL","ACTVT","06"],
  [200,"A","S_ISM_INFE","ACTVT","01"],
  [200,"A","S_ISM_INFE","ACTVT","02"],
  [200,"A","S_ISM_INFE","ACTVT","06"],
  [200,"A","S_ISM_MDL","ACTVT","01"],
  [200,"A","S_ISM_MDL","ACTVT","02"],
  [200,"A","S_ISM_MDL","ACTVT","06"],
  [200,"A","S_ISM_MVER","ACTVT","01"],
  [200,"A","S_ISM_MVER","ACTVT","02"],
  [200,"A","S_ISM_MVER","ACTVT","06"],
  [200,"A","S_ISM_PS","ACTVT","01"],
  [200,"A","S_ISM_PS","ACTVT","02"],
  [200,"A","S_ISM_PS","ACTVT","06"],
  [200,"A","S_NUMBER","ACTVT","02"],
  [200,"A","V_BUK","ACTVT","01"],
  [200,"A","V_BUK","ACTVT","02"],
  [200,"A","V_BUK","ACTVT","06"],
  [200,"A","V_KNA1_BRG","ACTVT","01"],
  [200,"A","V_KNA1_BRG","ACTVT","02"],
  [200,"A","V_KNA1_BRG","ACTVT","05"],
  [200,"A","V_KNA1_BRG","ACTVT","06"],
  [200,"A","V_KNA1_VKO","ACTVT","01"],
  [200,"A","V_KNA1_VKO","ACTVT","02"],
  [200,"A","V_KNA1_VKO","ACTVT","05"],
  [200,"A","V_KNA1_VKO","ACTVT","06"],
  [200,"A","V_KOND_VEA","ACTVT","01"],
  [200,"A","V_KONG_VWE","ACTVT","01"],
  [200,"A","V_KONG_VWE","ACTVT","02"],
  [200,"A","V_KONH_VKO","ACTVT","01"],
  [200,"A","V_KONH_VKS","ACTVT","01"],
  [200,"A","V_SALEPLAN","ACTVT","01"],
  [200,"A","V_SALEPLAN","ACTVT","02"],
  [200,"A","V_SALEPLAN","ACTVT","06"],
  [200,"A","V_VBAK_AAT","ACTVT","01"],
  [200,"A","V_VBRK_FKA","ACTVT","01"],
  [200,"A","V_VBRK_FKA","ACTVT","C1"],
  [200,"A","V_VBRK_VKO","ACTVT","01"],
  [200,"A","V_VBRK_VKO","ACTVT","C1"],
  [200,"A","V_VBSK_STO","ACTVT","01"],
  [200,"A","V_VBSK_STO","ACTVT","19"],
  [200,"A","W_COCO","ACTVT","01"],
  [200,"A","W_COCO","ACTVT","84"],
  [200,"A","W_COCO_ORG","ACTVT","01"],
  [200,"A","W_COCO_ORG","ACTVT","84"],
  [200,"A","W_SWL_HEAD","ACTVT","01"],
  [200,"A","W_SWL_HEAD","ACTVT","02"],
  [200,"A","W_SWL_HEAD","ACTVT","06"],
  [200,"A","W_SWL_ITEM","ACTVT","01"],
  [200,"A","W_SWL_ITEM","ACTVT","02"],
  [200,"A","W_SWL_ITEM","ACTVT","06"],
  [200,"A","W_WBRK_FKA","ACTVT","01"],
  [200,"A","W_WBRK_FKA","ACTVT","85"],
  [200,"A","W_WBRK_FKA","ACTVT","99"],
  [200,"A","W_WBRK_ORG","ACTVT","01"],
  [200,"A","W_WBRK_ORG","ACTVT","85"],
  [200,"A","W_WBRK_ORG","ACTVT","99"],
  [200,"A","W_WBRK_PER","ACTVT","01"],
  [300,"A","/BSNAGT/FM","ACTVT","03"],
  [300,"A","/BSNAGT/PL","ACTVT","03"],
  [300,"A","/BSNAGT/SE","ACTVT","03"],
  [300,"A","/ECRS/POIA","ACTVT","03"],
  [300,"A","/ECRS/RPHD","ACTVT","03"],
  [300,"A","/ECRS/SP","ACTVT","16"],
  [300,"A","/IWXBE/USR","ACTVT","03"],
  [300,"A","/PF1/PO","ACTVT","43"],
  [300,"A","/S4PPM/BGT","ACTVT","03"],
  [300,"A","/S4PPM/BGT","ACTVT","F4"],
  [300,"A","/S4PPM/PRJ","ACTVT","03"],
  [300,"A","/S4PPM/PRJ","ACTVT","F4"],
  [300,"A","/S4PPM/PRR","ACTVT","03"],
  [300,"A","/S4PPM/PRR","ACTVT","F4"],
  [300,"A","/SAPCND/CM","ACTVT","03"],
  [300,"A","/SCMB/LMEW","ACTVT","02"],
  [300,"A","/SCMB/LMEW","ACTVT","03"],
  [300,"A","/SCMB/LOCB","ACTVT","03"],
  [300,"A","/SCMB/LOCB","ACTVT","F4"],
  [300,"A","/SCMB/WDCP","ACTVT","02"],
  [300,"A","/SCMB/WDCP","ACTVT","03"],
  [300,"A","/SCMB/WDCP","ACTVT","06"],
  [300,"A","/SCMB/WDEF","ACTVT","02"],
  [300,"A","/SCMB/WDEF","ACTVT","03"],
  [300,"A","/SCMB/WDEF","ACTVT","06"],
  [300,"A","/SCWM/ATTS","ACTVT","01"],
  [300,"A","/SCWM/ATTS","ACTVT","02"],
  [300,"A","/SCWM/ATTS","ACTVT","03"],
  [300,"A","/SCWM/ATTS","ACTVT","06"],
  [300,"A","/SCWM/CHG","ACTVT","03"],
  [300,"A","/SCWM/CHG","ACTVT","05"],
  [300,"A","/SCWM/COST","ACTVT","03"],
  [300,"A","/SCWM/DATC","ACTVT","02"],
  [300,"A","/SCWM/DATC","ACTVT","03"],
  [300,"A","/SCWM/DATC","ACTVT","60"],
  [300,"A","/SCWM/DATC","ACTVT","61"],
  [300,"A","/SCWM/DLV2","ACTVT","03"],
  [300,"A","/SCWM/DLV2","ACTVT","06"],
  [300,"A","/SCWM/DLV2","ACTVT","23"],
  [300,"A","/SCWM/DLV2","ACTVT","43"],
  [300,"A","/SCWM/DLV2","ACTVT","92"],
  [300,"A","/SCWM/DSLP","ACTVT","03"],
  [300,"A","/SCWM/HU","ACTVT","01"],
  [300,"A","/SCWM/HU","ACTVT","02"],
  [300,"A","/SCWM/HU","ACTVT","03"],
  [300,"A","/SCWM/HU","ACTVT","06"],
  [300,"A","/SCWM/HU","ACTVT","F4"],
  [300,"A","/SCWM/HUHS","ACTVT","03"],
  [300,"A","/SCWM/ILT","ACTVT","01"],
  [300,"A","/SCWM/ILT","ACTVT","02"],
  [300,"A","/SCWM/ILT","ACTVT","03"],
  [300,"A","/SCWM/ILT","ACTVT","06"],
  [300,"A","/SCWM/LDPV","ACTVT","03"],
  [300,"A","/SCWM/LG","ACTVT","03"],
  [300,"A","/SCWM/MFS","ACTVT","02"],
  [300,"A","/SCWM/MFS","ACTVT","16"],
  [300,"A","/SCWM/MSTR","ACTVT","01"],
  [300,"A","/SCWM/OC","ACTVT","03"],
  [300,"A","/SCWM/PAS","ACTVT","01"],
  [300,"A","/SCWM/PAS","ACTVT","02"],
  [300,"A","/SCWM/PAS","ACTVT","03"],
  [300,"A","/SCWM/PAS","ACTVT","06"],
  [300,"A","/SCWM/PIMP","ACTVT","03"],
  [300,"A","/SCWM/PIPR","ACTVT","01"],
  [300,"A","/SCWM/PIPR","ACTVT","02"],
  [300,"A","/SCWM/PIPR","ACTVT","03"],
  [300,"A","/SCWM/POHU","ACTVT","01"],
  [300,"A","/SCWM/POHU","ACTVT","02"],
  [300,"A","/SCWM/POHU","ACTVT","03"],
  [300,"A","/SCWM/PROD","ACTVT","03"],
  [300,"A","/SCWM/QDOC","ACTVT","01"],
  [300,"A","/SCWM/QDOC","ACTVT","03"],
  [300,"A","/SCWM/QELM","ACTVT","03"],
  [300,"A","/SCWM/QFID","ACTVT","03"],
  [300,"A","/SCWM/QSUP","ACTVT","02"],
  [300,"A","/SCWM/QSUP","ACTVT","03"],
  [300,"A","/SCWM/RSRC","ACTVT","03"],
  [300,"A","/SCWM/TATT","ACTVT","01"],
  [300,"A","/SCWM/TATT","ACTVT","02"],
  [300,"A","/SCWM/TATT","ACTVT","03"],
  [300,"A","/SCWM/TATT","ACTVT","06"],
  [300,"A","/SCWM/TOHA","ACTVT","03"],
  [300,"A","/SCWM/TOHA","ACTVT","31"],
  [300,"A","/SCWM/VAS","ACTVT","03"],
  [300,"A","/SCWM/VM","ACTVT","03"],
  [300,"A","/SCWM/WAV2","ACTVT","02"],
  [300,"A","/SCWM/WAV2","ACTVT","03"],
  [300,"A","/SCWM/WAV2","ACTVT","43"],
  [300,"A","/SCWM/WHF4","ACTVT","03"],
  [300,"A","/SCWM/WHF4","ACTVT","F4"],
  [300,"A","/SMB/LC_PR","ACTVT","03"],
  [300,"A","/SRMSMC/BO","ACTVT","03"],
  [300,"A","/SRMSMC/BO","ACTVT","04"],
  [300,"A","/SRMSMC/BO","ACTVT","F4"],
  [300,"A","/SRMSMC/R1","ACTVT","03"],
  [300,"A","/SRMSMC/R2","ACTVT","03"],
  [300,"A","/SRMSMC/R3","ACTVT","03"],
  [300,"A","/SRMSMC/R4","ACTVT","03"],
  [300,"A","/SRMSMC/R5","ACTVT","03"],
  [300,"A","/UI2/UIPC","ACTVT","03"],
  [300,"A","/UI2/UISC","ACTVT","03"],
  [300,"A","A_B_ANLKL","ACTVT","02"],
  [300,"A","A_B_ANLKL","ACTVT","03"],
  [300,"A","A_B_ANLKL","ACTVT","77"],
  [300,"A","A_CFG_CMP","ACTVT","03"],
  [300,"A","A_M_ANLKL","ACTVT","03"],
  [300,"A","A_S_ANLKL","ACTVT","03"],
  [300,"A","A_S_ANLKL","ACTVT","F4"],
  [300,"A","A_S_MD","ACTVT","03"],
  [300,"A","A_S_MD","ACTVT","F4"],
  [300,"A","A_UO","ACTVT","03"],
  [300,"A","A_UO","ACTVT","F4"],
  [300,"A","ADPIC_RIC","ACTVT","03"],
  [300,"A","AS_ESH_BRW","ACTVT","03"],
  [300,"A","B_BUP_PCPT","ACTVT","03"],
  [300,"A","B_BUPA_ADR","ACTVT","03"],
  [300,"A","B_BUPA_ADR","ACTVT","F4"],
  [300,"A","B_BUPA_ATT","ACTVT","03"],
  [300,"A","B_BUPA_BNK","ACTVT","03"],
  [300,"A","B_BUPA_BNK","ACTVT","F4"],
  [300,"A","B_BUPA_CRS","ACTVT","03"],
  [300,"A","B_BUPA_FDG","ACTVT","03"],
  [300,"A","B_BUPA_GRP","ACTVT","01"],
  [300,"A","B_BUPA_GRP","ACTVT","03"],
  [300,"A","B_BUPA_GRP","ACTVT","F4"],
  [300,"A","B_BUPA_PGM","ACTVT","03"],
  [300,"A","B_BUPA_RAT","ACTVT","03"],
  [300,"A","B_BUPA_RLT","ACTVT","03"],
  [300,"A","B_BUPA_RLT","ACTVT","F4"],
  [300,"A","B_BUPR_BZT","ACTVT","03"],
  [300,"A","B_BUPR_BZT","ACTVT","F4"],
  [300,"A","B_BUPR_FDG","ACTVT","03"],
  [300,"A","B_CCARD","ACTVT","03"],
  [300,"A","B_CCARD","ACTVT","F4"],
  [300,"A","BEH_SUBSCR","ACTVT","03"],
  [300,"A","BEH_SUBSCR","ACTVT","33"],
  [300,"A","C_AENR_BGR","ACTVT","03"],
  [300,"A","C_AENR_BGR","ACTVT","F4"],
  [300,"A","C_AENR_ERW","ACTVT","03"],
  [300,"A","C_AENR_ERW","ACTVT","F4"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","01"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","21"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","22"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","23"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","24"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","25"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","26"],
  [300,"A","C_AFKO_ACT","PS_ACTVT","42"],
  [300,"A","C_AFKO_ATY","ACTVT","03"],
  [300,"A","C_AFKO_ATY","ACTVT","F4"],
  [300,"A","C_AFKO_AWA","ACTVT","03"],
  [300,"A","C_AFKO_AWA","ACTVT","F4"],
  [300,"A","C_AFKO_CST","ACTVT","03"],
  [300,"A","C_AFRU_AWK","ACTVT","01"],
  [300,"A","C_AFRU_AWK","ACTVT","02"],
  [300,"A","C_AFRU_AWK","ACTVT","03"],
  [300,"A","C_AFRU_AWK","ACTVT","85"],
  [300,"A","C_APO_DEF","ACTVT","01"],
  [300,"A","C_APO_DEF","ACTVT","02"],
  [300,"A","C_APO_DEF","ACTVT","03"],
  [300,"A","C_APO_LOC","ACTVT","03"],
  [300,"A","C_APO_LOC","ACTVT","16"],
  [300,"A","C_APO_LOC","ACTVT","F4"],
  [300,"A","C_APO_PROD","ACTVT","03"],
  [300,"A","C_APO_RELH","ACTVT","03"],
  [300,"A","C_APO_RES","ACTVT","02"],
  [300,"A","C_APO_RES","ACTVT","03"],
  [300,"A","C_APO_VERS","ACTVT","03"],
  [300,"A","C_ARPL_GT","ACTVT","03"],
  [300,"A","C_ARPL_GT","ACTVT","F4"],
  [300,"A","C_ARPL_OP","ACTVT","01"],
  [300,"A","C_ARPL_OP","ACTVT","02"],
  [300,"A","C_ARPL_OP","ACTVT","03"],
  [300,"A","C_ARPL_OP","ACTVT","06"],
  [300,"A","C_ARPL_OP","ACTVT","F4"],
  [300,"A","C_ARPL_WRK","ACTVT","03"],
  [300,"A","C_ARPL_WRK","ACTVT","F4"],
  [300,"A","C_BUYOFF","ACTVT","03"],
  [300,"A","C_CABN","ACTVT","03"],
  [300,"A","C_CABN","ACTVT","F4"],
  [300,"A","C_COCF_SRA","ACTVT","01"],
  [300,"A","C_COCF_SRA","ACTVT","02"],
  [300,"A","C_COCF_SRA","ACTVT","03"],
  [300,"A","C_COCF_SRA","ACTVT","06"],
  [300,"A","C_COCF_SRA","ACTVT","61"],
  [300,"A","C_COCF_SRE","ACTVT","01"],
  [300,"A","C_COCF_SRE","ACTVT","02"],
  [300,"A","C_COCF_SRE","ACTVT","03"],
  [300,"A","C_COCF_SRE","ACTVT","06"],
  [300,"A","C_COCF_SRF","ACTVT","01"],
  [300,"A","C_COCF_SRF","ACTVT","02"],
  [300,"A","C_COCF_SRF","ACTVT","03"],
  [300,"A","C_COCF_SRF","ACTVT","06"],
  [300,"A","C_COCF_SRH","ACTVT","01"],
  [300,"A","C_COCF_SRH","ACTVT","02"],
  [300,"A","C_COCF_SRH","ACTVT","03"],
  [300,"A","C_COCF_SRH","ACTVT","06"],
  [300,"A","C_COCF_SRH","ACTVT","61"],
  [300,"A","C_CRFH_BRG","ACTVT","03"],
  [300,"A","C_DEMAND","ACTVT","03"],
  [300,"A","C_DEMAND","ACTVT","F4"],
  [300,"A","C_DRAD_OBJ","ACTVT","03"],
  [300,"A","C_DRAD_OBJ","ACTVT","F4"],
  [300,"A","C_DT_CNV","ACTVT","03"],
  [300,"A","C_DT_FND","ACTVT","03"],
  [300,"A","C_ETOPROBJ","ACTVT","03"],
  [300,"A","C_FVER_WRK","ACTVT","03"],
  [300,"A","C_FVER_WRK","ACTVT","F4"],
  [300,"A","C_KAPA_ABG","ACTVT","01"],
  [300,"A","C_KAPA_ABG","ACTVT","02"],
  [300,"A","C_KAPA_ABG","ACTVT","03"],
  [300,"A","C_KAPA_ABG","ACTVT","06"],
  [300,"A","C_KAPA_ABG","ACTVT","16"],
  [300,"A","C_KAPA_PLA","ACTVT","03"],
  [300,"A","C_KAPA_PLA","ACTVT","F4"],
  [300,"A","C_KLAH_BKL","ACTVT","03"],
  [300,"A","C_KLAH_BKP","ACTVT","03"],
  [300,"A","C_KLAH_BKP","ACTVT","F4"],
  [300,"A","C_LIME_HU","ACTVT","02"],
  [300,"A","C_LIME_HU","ACTVT","03"],
  [300,"A","C_LIME_HU","ACTVT","10"],
  [300,"A","C_LIME_LOC","ACTVT","03"],
  [300,"A","C_LIME_SI","ACTVT","02"],
  [300,"A","C_LIME_SI","ACTVT","03"],
  [300,"A","C_LO_CM","ACTVT","01"],
  [300,"A","C_LO_CM","ACTVT","02"],
  [300,"A","C_LO_CM","ACTVT","03"],
  [300,"A","C_LO_CM","ACTVT","04"],
  [300,"A","C_LO_CM","ACTVT","05"],
  [300,"A","C_LO_CM","ACTVT","06"],
  [300,"A","C_LO_CM","ACTVT","08"],
  [300,"A","C_LO_CM","ACTVT","60"],
  [300,"A","C_LO_CM","ACTVT","61"],
  [300,"A","C_LO_CM","ACTVT","DL"],
  [300,"A","C_LO_HU","ACTVT","01"],
  [300,"A","C_LO_HU","ACTVT","02"],
  [300,"A","C_LO_HU","ACTVT","03"],
  [300,"A","C_LO_HU","ACTVT","04"],
  [300,"A","C_LO_HU","ACTVT","F4"],
  [300,"A","C_MD_SCU","ACTVT","03"],
  [300,"A","C_MPE_ACTS","ACTVT","03"],
  [300,"A","C_MPE_HOLD","ACTVT","01"],
  [300,"A","C_MPE_HOLD","ACTVT","03"],
  [300,"A","C_MPE_HOLD","ACTVT","43"],
  [300,"A","C_MPE_QUAL","ACTVT","03"],
  [300,"A","C_MPE_UPRO","ACTVT","03"],
  [300,"A","C_OA_EXEC","ACTVT","03"],
  [300,"A","C_OA_EXEC","ACTVT","16"],
  [300,"A","C_PPE_INST","ACTVT","03"],
  [300,"A","C_PPE_PAID","ACTVT","03"],
  [300,"A","C_PRJBLGEL","ACTVT","03"],
  [300,"A","C_PRJBLGRQ","ACTVT","03"],
  [300,"A","C_PROJ_KOK","PS_ACTVT","41"],
  [300,"A","C_PROJ_PRC","PS_ACTVT","41"],
  [300,"A","C_PROJ_PUR","ACTVT","03"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","01"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","02"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","22"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","23"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","24"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","25"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","26"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","28"],
  [300,"A","C_PROJ_VNR","PS_ACTVT","41"],
  [300,"A","C_PRPS_ART","PS_ACTVT","41"],
  [300,"A","C_PRPS_KST","PS_ACTVT","41"],
  [300,"A","C_PRPS_PRC","PS_ACTVT","41"],
  [300,"A","C_PRPS_USR","PS_ACTVT","02"],
  [300,"A","C_PRPS_USR","PS_ACTVT","20"],
  [300,"A","C_PRPS_USR","PS_ACTVT","21"],
  [300,"A","C_PRPS_USR","PS_ACTVT","23"],
  [300,"A","C_PRPS_USR","PS_ACTVT","25"],
  [300,"A","C_PRPS_USR","PS_ACTVT","27"],
  [300,"A","C_PRPS_USR","PS_ACTVT","45"],
  [300,"A","C_PRPS_VNR","PS_ACTVT","01"],
  [300,"A","C_PRPS_VNR","PS_ACTVT","02"],
  [300,"A","C_PRPS_VNR","PS_ACTVT","25"],
  [300,"A","C_PRPS_VNR","PS_ACTVT","27"],
  [300,"A","C_PRPS_VNR","PS_ACTVT","41"],
  [300,"A","C_PSCVP_DB","ACTVT","03"],
  [300,"A","C_PSCVP_DB","ACTVT","F4"],
  [300,"A","C_PVS_PATY","ACTVT","03"],
  [300,"A","C_PVS_PNID","ACTVT","03"],
  [300,"A","C_PVS_PNID","ACTVT","F4"],
  [300,"A","C_PVS_PNTY","ACTVT","03"],
  [300,"A","C_PVS_PVID","ACTVT","03"],
  [300,"A","C_PVS_PVTY","ACTVT","03"],
  [300,"A","C_ROUT","ACTVT","03"],
  [300,"A","C_ROUT","ACTVT","F4"],
  [300,"A","C_SFO_ACTN","ACTVT","16"],
  [300,"A","C_SIT2INST","ACTVT","03"],
  [300,"A","C_SIT2TYPE","ACTVT","03"],
  [300,"A","C_SITNACT","ACTVT","03"],
  [300,"A","C_SITNACT","ACTVT","F4"],
  [300,"A","C_SITNDEF","ACTVT","03"],
  [300,"A","C_SITNDEF","ACTVT","F4"],
  [300,"A","C_SITNINST","ACTVT","03"],
  [300,"A","C_SITNINST","ACTVT","F4"],
  [300,"A","C_STDTEXT","ACTVT","03"],
  [300,"A","C_STDTEXT","ACTVT","F4"],
  [300,"A","C_STUE_BER","ACTVT","03"],
  [300,"A","C_STUE_BER","ACTVT","F4"],
  [300,"A","C_STUE_WRK","ACTVT","03"],
  [300,"A","C_STUE_WRK","ACTVT","F4"],
  [300,"A","C_STXT_WRK","ACTVT","03"],
  [300,"A","C_STXT_WRK","ACTVT","F4"],
  [300,"A","C_VC_MODEL","ACTVT","03"],
  [300,"A","C_VC_MODEL","ACTVT","F4"],
  [300,"A","C_VCM_INST","ACTVT","03"],
  [300,"A","C_VCM_INST","ACTVT","F4"],
  [300,"A","C_VE_TXS_T","ACTVT","03"],
  [300,"A","C_WTY_CLM","ACTVT","03"],
  [300,"A","C_WTY_CLM","ACTVT","F4"],
  [300,"A","CA_OC_ORI","ACTVT","03"],
  [300,"A","CM_CON","ACTVT","03"],
  [300,"A","CPE_DOCDAT","ACTVT","02"],
  [300,"A","CPE_DOCDAT","ACTVT","03"],
  [300,"A","CPE_SETTIN","ACTVT","03"],
  [300,"A","CRM_ORD_OE","ACTVT","03"],
  [300,"A","CRM_ORD_OP","ACTVT","03"],
  [300,"A","CRM_ORD_PC","ACTVT","03"],
  [300,"A","CRM_ORD_PR","ACTVT","03"],
  [300,"A","CRM_ORD_PR","ACTVT","F4"],
  [300,"A","CRM_SE_ENT","ACTVT","03"],
  [300,"A","CRM_TXT_ID","ACTVT","03"],
  [300,"A","CRM_TXT_ID","ACTVT","F4"],
  [300,"A","CRMS4_PBDL","ACTVT","03"],
  [300,"A","CRMS4_SLSO","ACTVT","03"],
  [300,"A","CRMS4_SLSO","ACTVT","F4"],
  [300,"A","DMEX_TREE","ACTVT","33"],
  [300,"A","DRF_ADM","ACTVT","03"],
  [300,"A","E_CS_BUNIT","ACTVT","03"],
  [300,"A","E_CS_BUNIT","ACTVT","F4"],
  [300,"A","E_CS_CACTT","ACTVT","03"],
  [300,"A","E_CS_CONGR","ACTVT","03"],
  [300,"A","E_CS_DIMEN","ACTVT","03"],
  [300,"A","E_CS_DIMEN","ACTVT","F4"],
  [300,"A","E_CS_ITCLG","ACTVT","03"],
  [300,"A","E_CS_ITCLG","ACTVT","06"],
  [300,"A","E_CS_ITCLG","ACTVT","F4"],
  [300,"A","E_CS_RVERS","ACTVT","03"],
  [300,"A","E_CS_RVERS","ACTVT","F4"],
  [300,"A","EAMS_SV","ACTVT","03"],
  [300,"A","EDOIN_ORG1","ACTVT","03"],
  [300,"A","EI_VIEW_BL","ACTVT","03"],
  [300,"A","EI_VIEW_CR","ACTVT","03"],
  [300,"A","F_ACES_DST","ACTVT","03"],
  [300,"A","F_ACES_DST","ACTVT","F4"],
  [300,"A","F_ACES_PER","ACTVT","03"],
  [300,"A","F_ACES_PER","ACTVT","F4"],
  [300,"A","F_AUDIT_OI","ACTVT","03"],
  [300,"A","F_AVIK_BUK","ACTVT","03"],
  [300,"A","F_AVIK_BUK","ACTVT","F4"],
  [300,"A","F_BAF4_MDC","ACTVT","03"],
  [300,"A","F_BKPF_BEB","ACTVT","03"],
  [300,"A","F_BKPF_BEB","ACTVT","77"],
  [300,"A","F_BKPF_BEB","ACTVT","F4"],
  [300,"A","F_BKPF_BED","ACTVT","01"],
  [300,"A","F_BKPF_BED","ACTVT","02"],
  [300,"A","F_BKPF_BED","ACTVT","03"],
  [300,"A","F_BKPF_BED","ACTVT","77"],
  [300,"A","F_BKPF_BED","ACTVT","F4"],
  [300,"A","F_BKPF_BEK","ACTVT","02"],
  [300,"A","F_BKPF_BEK","ACTVT","03"],
  [300,"A","F_BKPF_BEK","ACTVT","77"],
  [300,"A","F_BKPF_BEK","ACTVT","F4"],
  [300,"A","F_BKPF_BES","ACTVT","02"],
  [300,"A","F_BKPF_BES","ACTVT","03"],
  [300,"A","F_BKPF_BES","ACTVT","77"],
  [300,"A","F_BKPF_BES","ACTVT","F4"],
  [300,"A","F_BKPF_BLA","ACTVT","02"],
  [300,"A","F_BKPF_BLA","ACTVT","03"],
  [300,"A","F_BKPF_BLA","ACTVT","04"],
  [300,"A","F_BKPF_BLA","ACTVT","77"],
  [300,"A","F_BKPF_BLA","ACTVT","F4"],
  [300,"A","F_BKPF_BUK","ACTVT","01"],
  [300,"A","F_BKPF_BUK","ACTVT","02"],
  [300,"A","F_BKPF_BUK","ACTVT","03"],
  [300,"A","F_BKPF_BUK","ACTVT","10"],
  [300,"A","F_BKPF_BUK","ACTVT","77"],
  [300,"A","F_BKPF_GSB","ACTVT","01"],
  [300,"A","F_BKPF_GSB","ACTVT","02"],
  [300,"A","F_BKPF_GSB","ACTVT","03"],
  [300,"A","F_BKPF_GSB","ACTVT","77"],
  [300,"A","F_BKPF_GSB","ACTVT","F4"],
  [300,"A","F_BKPF_KOA","ACTVT","01"],
  [300,"A","F_BKPF_KOA","ACTVT","02"],
  [300,"A","F_BKPF_KOA","ACTVT","03"],
  [300,"A","F_BKPF_KOA","ACTVT","77"],
  [300,"A","F_BKPF_KOA","ACTVT","F4"],
  [300,"A","F_BL_BANK","ACTVT","03"],
  [300,"A","F_BL_BANK","ACTVT","F4"],
  [300,"A","F_BNKA_BUK","ACTVT","03"],
  [300,"A","F_BNKA_BUK","ACTVT","F4"],
  [300,"A","F_BNKA_MAN","ACTVT","03"],
  [300,"A","F_BNKA_MAN","ACTVT","F4"],
  [300,"A","F_BNKA_MAO","ACTVT","03"],
  [300,"A","F_BNKA_MAO","ACTVT","F4"],
  [300,"A","F_BTTYPE","ACTVT","03"],
  [300,"A","F_BTTYPE","ACTVT","F4"],
  [300,"A","F_BUAC_PST","ACTVT","03"],
  [300,"A","F_BUAC_PST","ACTVT","F4"],
  [300,"A","F_BUK_BUPL","ACTVT","03"],
  [300,"A","F_CC_HIER","ACTVT","03"],
  [300,"A","F_CC_HIER","ACTVT","F4"],
  [300,"A","F_CNTXI_CX","ACTVT","03"],
  [300,"A","F_CUREXRAT","ACTVT","03"],
  [300,"A","F_CUREXRAT","ACTVT","F4"],
  [300,"A","F_DOC_BGT","ACTVT","03"],
  [300,"A","F_DOC_BGT","ACTVT","F4"],
  [300,"A","F_EF_MAINT","ACTVT","03"],
  [300,"A","F_EF_MAINT","ACTVT","F4"],
  [300,"A","F_FAGL_BRG","ACTVT","03"],
  [300,"A","F_FAGL_LDR","ACTVT","01"],
  [300,"A","F_FAGL_LDR","ACTVT","02"],
  [300,"A","F_FAGL_LDR","ACTVT","03"],
  [300,"A","F_FAGL_LDR","ACTVT","77"],
  [300,"A","F_FAGL_LDR","ACTVT","F4"],
  [300,"A","F_FAGL_MPV","ACTVT","03"],
  [300,"A","F_FAGL_OPV","ACTVT","03"],
  [300,"A","F_FAGL_SEG","ACTVT","03"],
  [300,"A","F_FAGL_SEG","ACTVT","77"],
  [300,"A","F_FAGL_SEG","ACTVT","F4"],
  [300,"A","F_FAGL_SKF","ACTVT","03"],
  [300,"A","F_FBCJ","ACTVT","33"],
  [300,"A","F_FDES_BUK","ACTVT","03"],
  [300,"A","F_FDES_GSB","ACTVT","03"],
  [300,"A","F_FDSB_BUK","ACTVT","03"],
  [300,"A","F_FDSB_BUK","ACTVT","16"],
  [300,"A","F_FDSR_BUK","ACTVT","03"],
  [300,"A","F_FDSR_BUK","ACTVT","16"],
  [300,"A","F_FEBB_BUK","ACTVT","03"],
  [300,"A","F_FEBB_BUK","ACTVT","F4"],
  [300,"A","F_FEBC_BUK","ACTVT","03"],
  [300,"A","F_FEBC_BUK","ACTVT","F4"],
  [300,"A","F_FIGM_CLS","ACTVT","03"],
  [300,"A","F_FIGM_GNT","ACTVT","03"],
  [300,"A","F_FIGM_GNT","ACTVT","F4"],
  [300,"A","F_FIGM_PRG","ACTVT","03"],
  [300,"A","F_FINS_SKF","ACTVT","03"],
  [300,"A","F_FUND_PST","ACTVT","03"],
  [300,"A","F_FUND_PST","ACTVT","F4"],
  [300,"A","F_GL_CNV","ACTVT","03"],
  [300,"A","F_GL_MAP","ACTVT","03"],
  [300,"A","F_ICA_DC","ACTVT","03"],
  [300,"A","F_ICA_DCM","ACTVT","03"],
  [300,"A","F_ICA_MJ","ACTVT","03"],
  [300,"A","F_ICA_MM","ACTVT","03"],
  [300,"A","F_ICA_RCON","ACTVT","03"],
  [300,"A","F_ICA_RR","ACTVT","03"],
  [300,"A","F_IT_ALV","ACTVT","01"],
  [300,"A","F_IT_ALV","ACTVT","02"],
  [300,"A","F_JPTX_REA","ACTVT","03"],
  [300,"A","F_KK_LOCK","ACTVT","03"],
  [300,"A","F_KKBIXBIP","ACTVT","03"],
  [300,"A","F_KKINTER","ACTVT","03"],
  [300,"A","F_KKINTER","ACTVT","F4"],
  [300,"A","F_KKINV","ACTVT","03"],
  [300,"A","F_KKINV","ACTVT","F4"],
  [300,"A","F_KKINVBUK","ACTVT","03"],
  [300,"A","F_KKINVBUK","ACTVT","F4"],
  [300,"A","F_KKKO_BEG","ACTVT","03"],
  [300,"A","F_KKKO_BUK","ACTVT","03"],
  [300,"A","F_KKKO_GSB","ACTVT","03"],
  [300,"A","F_KKSEPA","ACTVT","03"],
  [300,"A","F_KKSEPA","ACTVT","F4"],
  [300,"A","F_KKVK_BEG","ACTVT","03"],
  [300,"A","F_KKVK_BEG","ACTVT","F4"],
  [300,"A","F_KKVK_BUK","ACTVT","03"],
  [300,"A","F_KKVK_BUK","ACTVT","F4"],
  [300,"A","F_KKVK_VKT","ACTVT","03"],
  [300,"A","F_KKVK_VKT","ACTVT","F4"],
  [300,"A","F_KKVT_AUT","ACTVT","03"],
  [300,"A","F_KKVT_BEG","ACTVT","03"],
  [300,"A","F_KKVT_BEG","ACTVT","F4"],
  [300,"A","F_KKVT_BUK","ACTVT","03"],
  [300,"A","F_KKVT_BUK","ACTVT","F4"],
  [300,"A","F_KKVT_FDG","ACTVT","03"],
  [300,"A","F_KKVTD_BG","ACTVT","03"],
  [300,"A","F_KNA1_APP","ACTVT","03"],
  [300,"A","F_KNA1_APP","ACTVT","04"],
  [300,"A","F_KNA1_APP","ACTVT","F4"],
  [300,"A","F_KNA1_BED","ACTVT","01"],
  [300,"A","F_KNA1_BED","ACTVT","02"],
  [300,"A","F_KNA1_BED","ACTVT","03"],
  [300,"A","F_KNA1_BED","ACTVT","F4"],
  [300,"A","F_KNA1_BUK","ACTVT","02"],
  [300,"A","F_KNA1_BUK","ACTVT","03"],
  [300,"A","F_KNA1_BUK","ACTVT","F4"],
  [300,"A","F_KNA1_GEN","ACTVT","01"],
  [300,"A","F_KNA1_GEN","ACTVT","02"],
  [300,"A","F_KNA1_GEN","ACTVT","03"],
  [300,"A","F_KNA1_GEN","ACTVT","05"],
  [300,"A","F_KNA1_GEN","ACTVT","06"],
  [300,"A","F_KNA1_GEN","ACTVT","F4"],
  [300,"A","F_KNA1_GRP","ACTVT","01"],
  [300,"A","F_KNA1_GRP","ACTVT","02"],
  [300,"A","F_KNA1_GRP","ACTVT","03"],
  [300,"A","F_KNA1_GRP","ACTVT","F4"],
  [300,"A","F_KNKA_KKB","ACTVT","03"],
  [300,"A","F_KNKA_KKB","ACTVT","F4"],
  [300,"A","F_KNKA_MAN","ACTVT","03"],
  [300,"A","F_KNKK_BED","ACTVT","03"],
  [300,"A","F_LFA1_APP","ACTVT","01"],
  [300,"A","F_LFA1_APP","ACTVT","02"],
  [300,"A","F_LFA1_APP","ACTVT","03"],
  [300,"A","F_LFA1_BEK","ACTVT","01"],
  [300,"A","F_LFA1_BEK","ACTVT","02"],
  [300,"A","F_LFA1_BEK","ACTVT","03"],
  [300,"A","F_LFA1_BEK","ACTVT","05"],
  [300,"A","F_LFA1_BEK","ACTVT","06"],
  [300,"A","F_LFA1_BEK","ACTVT","F4"],
  [300,"A","F_LFA1_BUK","ACTVT","01"],
  [300,"A","F_LFA1_BUK","ACTVT","02"],
  [300,"A","F_LFA1_BUK","ACTVT","03"],
  [300,"A","F_LFA1_BUK","ACTVT","F4"],
  [300,"A","F_LFA1_GEN","ACTVT","01"],
  [300,"A","F_LFA1_GEN","ACTVT","02"],
  [300,"A","F_LFA1_GEN","ACTVT","03"],
  [300,"A","F_LFA1_GEN","ACTVT","05"],
  [300,"A","F_LFA1_GEN","ACTVT","06"],
  [300,"A","F_LFA1_GEN","ACTVT","F4"],
  [300,"A","F_LFA1_GRP","ACTVT","01"],
  [300,"A","F_LFA1_GRP","ACTVT","02"],
  [300,"A","F_LFA1_GRP","ACTVT","03"],
  [300,"A","F_LFA1_GRP","ACTVT","F4"],
  [300,"A","F_MANDATE","ACTVT","03"],
  [300,"A","F_MANDATE","ACTVT","F4"],
  [300,"A","F_NFBA","ACTVT","03"],
  [300,"A","F_ORGLCHG","ACTVT","03"],
  [300,"A","F_ORGLCHG","ACTVT","F4"],
  [300,"A","F_PAYRQ","ACTVT","01"],
  [300,"A","F_PAYRQ","ACTVT","02"],
  [300,"A","F_PAYRQ","ACTVT","03"],
  [300,"A","F_PAYRQ","ACTVT","43"],
  [300,"A","F_PAYRQ","ACTVT","85"],
  [300,"A","F_PST_TMPL","ACTVT","03"],
  [300,"A","F_PST_TMPL","ACTVT","F4"],
  [300,"A","F_RE_SC_RU","ACTVT","03"],
  [300,"A","F_REGU_PAY","ACTVT","03"],
  [300,"A","F_RP_BUKRS","ACTVT","03"],
  [300,"A","F_RPCODE","ACTVT","03"],
  [300,"A","F_RR_ACCR","ACTVT","03"],
  [300,"A","F_RR_CLOSE","ACTVT","03"],
  [300,"A","F_RR_CONTR","ACTVT","02"],
  [300,"A","F_RR_CONTR","ACTVT","03"],
  [300,"A","F_RR_CONTR","ACTVT","F4"],
  [300,"A","F_RRBIZREC","ACTVT","03"],
  [300,"A","F_RRRAI","ACTVT","03"],
  [300,"A","F_SAFTN","ACTVT","03"],
  [300,"A","F_SEPA_MDT","ACTVT","03"],
  [300,"A","F_SKA1_BES","ACTVT","01"],
  [300,"A","F_SKA1_BES","ACTVT","02"],
  [300,"A","F_SKA1_BES","ACTVT","03"],
  [300,"A","F_SKA1_BES","ACTVT","05"],
  [300,"A","F_SKA1_BES","ACTVT","06"],
  [300,"A","F_SKA1_BUK","ACTVT","01"],
  [300,"A","F_SKA1_BUK","ACTVT","02"],
  [300,"A","F_SKA1_BUK","ACTVT","03"],
  [300,"A","F_SKA1_BUK","ACTVT","05"],
  [300,"A","F_SKA1_BUK","ACTVT","06"],
  [300,"A","F_SKA1_BUK","ACTVT","F4"],
  [300,"A","F_SKA1_KTP","ACTVT","01"],
  [300,"A","F_SKA1_KTP","ACTVT","02"],
  [300,"A","F_SKA1_KTP","ACTVT","03"],
  [300,"A","F_SKA1_KTP","ACTVT","05"],
  [300,"A","F_SKA1_KTP","ACTVT","06"],
  [300,"A","F_SKA1_KTP","ACTVT","F4"],
  [300,"A","F_SRF_RNTM","ACTVT","03"],
  [300,"A","F_T011","ACTVT","03"],
  [300,"A","F_T011","ACTVT","F4"],
  [300,"A","F_T011XBRL","ACTVT","03"],
  [300,"A","F_T033","ACTVT","03"],
  [300,"A","F_TGCDREP","ACTVT","03"],
  [300,"A","F_TGCDVER","ACTVT","03"],
  [300,"A","F_TGCDVER","ACTVT","F4"],
  [300,"A","F_TGCDXBRL","ACTVT","03"],
  [300,"A","F_UDM_GRUP","ACTVT","03"],
  [300,"A","F_UKM_SGMT","ACTVT","03"],
  [300,"A","F_UKM_SGMT","ACTVT","08"],
  [300,"A","F_UNI_HIER","ACTVT","01"],
  [300,"A","F_UNI_HIER","ACTVT","02"],
  [300,"A","F_UNI_HIER","ACTVT","03"],
  [300,"A","F_UNI_HIER","ACTVT","06"],
  [300,"A","F_UNI_HIER","ACTVT","F4"],
  [300,"A","FI_CS_MD","ACTVT","03"],
  [300,"A","FI_CS_MD","ACTVT","F4"],
  [300,"A","FI_CS_RULE","ACTVT","03"],
  [300,"A","FI_CS_SEL","ACTVT","03"],
  [300,"A","FIAR_CLIPA","ACTVT","03"],
  [300,"A","FIJP_ACCSS","ACTVT","03"],
  [300,"A","FOT_B2A_V","ACTVT","03"],
  [300,"A","FOT_B2A_V","ACTVT","16"],
  [300,"A","FPS_CDICT","ACTVT","03"],
  [300,"A","FQM_FLOW","ACTVT","03"],
  [300,"A","G_800S_GSE","ACTVT","03"],
  [300,"A","G_881_GRLD","ACTVT","03"],
  [300,"A","G_ALLOCTN","ACTVT","03"],
  [300,"A","G_GB90_","ACTVT","03"],
  [300,"A","G_GB93_","ACTVT","03"],
  [300,"A","G_GEF_GEOM","ACTVT","03"],
  [300,"A","G_GLTP","ACTVT","03"],
  [300,"A","G_REPO_LOC","ACTVT","03"],
  [300,"A","GLE_ECS","ACTVT","03"],
  [300,"A","I_LOGBOOK","ACTVT","03"],
  [300,"A","I_PHSE_CTL","ACTVT","01"],
  [300,"A","I_PHSE_CTL","ACTVT","06"],
  [300,"A","I_PLBKTTY","ACTVT","03"],
  [300,"A","I_PLBKTTY","ACTVT","F4"],
  [300,"A","I_ROUT","ACTVT","03"],
  [300,"A","I_ROUT","ACTVT","F4"],
  [300,"A","I_ROUT2","ACTVT","03"],
  [300,"A","I_ROUT2","ACTVT","F4"],
  [300,"A","ITM_BUKRS","ACTVT","03"],
  [300,"A","ITM_BUKRS","ACTVT","F4"],
  [300,"A","ITM_CLS_LR","ACTVT","03"],
  [300,"A","ITM_CLS_NC","ACTVT","03"],
  [300,"A","ITM_CLS_NS","ACTVT","03"],
  [300,"A","ITM_LGREG","ACTVT","03"],
  [300,"A","ITM_LGREG","ACTVT","F4"],
  [300,"A","ITM_LMGM","ACTVT","03"],
  [300,"A","ITM_LMGM","ACTVT","F4"],
  [300,"A","IWP_APT","ACTVT","03"],
  [300,"A","J_3RF_REGI","ACTVT","03"],
  [300,"A","JIT_CUSTMR","ACTVT","03"],
  [300,"A","JIT_CUSTMR","ACTVT","F4"],
  [300,"A","JIT_DSG","ACTVT","03"],
  [300,"A","JIT_DSG","ACTVT","F4"],
  [300,"A","JIT_PGD","ACTVT","03"],
  [300,"A","JIT_PGD","ACTVT","F4"],
  [300,"A","JIT_S2C_CR","ACTVT","02"],
  [300,"A","JIT_S2C_CR","ACTVT","03"],
  [300,"A","JIT_S2C_CR","ACTVT","16"],
  [300,"A","JIT_S2C_CR","ACTVT","F4"],
  [300,"A","JIT_S2C_DC","ACTVT","03"],
  [300,"A","JIT_S2C_DC","ACTVT","F4"],
  [300,"A","JIT_SC","ACTVT","03"],
  [300,"A","JIT_SC","ACTVT","F4"],
  [300,"A","K_ACDOCP_P","ACTVT","02"],
  [300,"A","K_ACDOCP_P","ACTVT","03"],
  [300,"A","K_ACDOCP_P","ACTVT","F4"],
  [300,"A","K_ALLOCGRP","ACTVT","03"],
  [300,"A","K_ALLOCGRP","ACTVT","F4"],
  [300,"A","K_ALLOCMNT","ACTVT","03"],
  [300,"A","K_ALLOCMNT","ACTVT","F4"],
  [300,"A","K_ALLOCRUN","ACTVT","03"],
  [300,"A","K_ALLOCTAG","ACTVT","03"],
  [300,"A","K_ALLOCTAG","ACTVT","F4"],
  [300,"A","K_ALLOMNT2","ACTVT","03"],
  [300,"A","K_ALLOMNT2","ACTVT","F4"],
  [300,"A","K_ALLORUN2","ACTVT","03"],
  [300,"A","K_ALLORUN2","ACTVT","F4"],
  [300,"A","K_AUFK_SET","ACTVT","03"],
  [300,"A","K_BDGT_DOC","ACTVT","03"],
  [300,"A","K_BDGT_DOC","ACTVT","F4"],
  [300,"A","K_CBPR_SET","ACTVT","03"],
  [300,"A","K_CBPR_SET","ACTVT","F4"],
  [300,"A","K_CCA_HIER","ACTVT","03"],
  [300,"A","K_CCA_HIER","ACTVT","F4"],
  [300,"A","K_CKBS","ACTVT","01"],
  [300,"A","K_CKBS","ACTVT","02"],
  [300,"A","K_CKBS","ACTVT","03"],
  [300,"A","K_CKPH","ACTVT","03"],
  [300,"A","K_CKPH_SET","ACTVT","03"],
  [300,"A","K_CKPH_SET","ACTVT","F4"],
  [300,"A","K_COSTRATE","ACTVT","03"],
  [300,"A","K_COSTRATE","ACTVT","F4"],
  [300,"A","K_CSKA_SET","ACTVT","03"],
  [300,"A","K_CSKB","ACTVT","01"],
  [300,"A","K_CSKB","ACTVT","02"],
  [300,"A","K_CSKB","ACTVT","03"],
  [300,"A","K_CSKB","ACTVT","05"],
  [300,"A","K_CSKB","ACTVT","06"],
  [300,"A","K_CSKS","ACTVT","03"],
  [300,"A","K_CSKS","ACTVT","F4"],
  [300,"A","K_CSKS_BUD","ACTVT","03"],
  [300,"A","K_CSKS_SET","ACTVT","03"],
  [300,"A","K_CSLA","ACTVT","03"],
  [300,"A","K_CSLA","ACTVT","F4"],
  [300,"A","K_CSLA_SET","ACTVT","03"],
  [300,"A","K_EB_LOG","ACTVT","03"],
  [300,"A","K_EB_POST","ACTVT","03"],
  [300,"A","K_KA03","ACTVT","03"],
  [300,"A","K_KA03","ACTVT","F4"],
  [300,"A","K_KA03_SET","ACTVT","03"],
  [300,"A","K_KEA_TC","ACTVT","03"],
  [300,"A","K_KED_UM","ACTVT","03"],
  [300,"A","K_KEKO","ACTVT","03"],
  [300,"A","K_KEKO","ACTVT","16"],
  [300,"A","K_KEKO","ACTVT","39"],
  [300,"A","K_KEKO","ACTVT","F4"],
  [300,"A","K_KER_TC","ACTVT","03"],
  [300,"A","K_KONH_KKS","ACTVT","03"],
  [300,"A","K_ML_MTART","ACTVT","03"],
  [300,"A","K_ML_VA","ACTVT","03"],
  [300,"A","K_ML_VA","ACTVT","F4"],
  [300,"A","K_MLPR_VA","ACTVT","03"],
  [300,"A","K_PCA_HIER","ACTVT","03"],
  [300,"A","K_PCA_HIER","ACTVT","F4"],
  [300,"A","K_PCA_MD","ACTVT","03"],
  [300,"A","K_PCA_MD","ACTVT","F4"],
  [300,"A","K_PCAP_SET","ACTVT","03"],
  [300,"A","K_PCAR_REP","ACTVT","03"],
  [300,"A","K_PCAR_REP","ACTVT","F4"],
  [300,"A","K_PKSA","ACTVT","03"],
  [300,"A","K_PKSA","ACTVT","F4"],
  [300,"A","K_PRPS_SET","ACTVT","03"],
  [300,"A","K_REPO_CCA","ACTVT","F4"],
  [300,"A","K_SUM_CO","ACTVT","03"],
  [300,"A","K_TP_VALU","ACTVT","03"],
  [300,"A","K_TRR_AP","ACTVT","03"],
  [300,"A","K_VRGNG","ACTVT","03"],
  [300,"A","K_VRGNG","ACTVT","06"],
  [300,"A","K_VRGNG","ACTVT","16"],
  [300,"A","K_VRGNG","ACTVT","F4"],
  [300,"A","K_WIP","ACTVT","03"],
  [300,"A","K_WIP_BU","ACTVT","03"],
  [300,"A","K_WIP_PC","ACTVT","03"],
  [300,"A","K_ZBASSL","ACTVT","03"],
  [300,"A","K_ZENTSL","ACTVT","03"],
  [300,"A","K_ZKALSM","ACTVT","03"],
  [300,"A","K_ZZUSSL","ACTVT","03"],
  [300,"A","LCM_CTX","ACTVT","03"],
  [300,"A","LCM_GEN","ACTVT","03"],
  [300,"A","LCM_LT","ACTVT","03"],
  [300,"A","LCM_LTENCC","ACTVT","03"],
  [300,"A","LCM_LTENCC","ACTVT","F4"],
  [300,"A","LCM_LTENPO","ACTVT","03"],
  [300,"A","LCM_LTENPO","ACTVT","F4"],
  [300,"A","LCM_LTENSO","ACTVT","03"],
  [300,"A","LCM_LTENSO","ACTVT","F4"],
  [300,"A","LO_HU_PI","ACTVT","03"],
  [300,"A","LO_HU_PI","ACTVT","F4"],
  [300,"A","LO_HU_REF","ACTVT","03"],
  [300,"A","LO_HU_REF","ACTVT","F4"],
  [300,"A","M_ANFR_BSA","ACTVT","03"],
  [300,"A","M_ANFR_BSA","ACTVT","04"],
  [300,"A","M_ANFR_BSA","ACTVT","08"],
  [300,"A","M_ANFR_BSA","ACTVT","09"],
  [300,"A","M_ANFR_BSA","ACTVT","F4"],
  [300,"A","M_ANFR_EKG","ACTVT","03"],
  [300,"A","M_ANFR_EKG","ACTVT","04"],
  [300,"A","M_ANFR_EKG","ACTVT","08"],
  [300,"A","M_ANFR_EKG","ACTVT","09"],
  [300,"A","M_ANFR_EKG","ACTVT","F4"],
  [300,"A","M_ANFR_EKO","ACTVT","03"],
  [300,"A","M_ANFR_EKO","ACTVT","04"],
  [300,"A","M_ANFR_EKO","ACTVT","08"],
  [300,"A","M_ANFR_EKO","ACTVT","09"],
  [300,"A","M_ANFR_EKO","ACTVT","F4"],
  [300,"A","M_ANFR_LGO","ACTVT","F4"],
  [300,"A","M_ANFR_WRK","ACTVT","03"],
  [300,"A","M_ANFR_WRK","ACTVT","08"],
  [300,"A","M_ANFR_WRK","ACTVT","F4"],
  [300,"A","M_ANGB_BSA","ACTVT","03"],
  [300,"A","M_ANGB_BSA","ACTVT","08"],
  [300,"A","M_ANGB_BSA","ACTVT","09"],
  [300,"A","M_ANGB_BSA","ACTVT","F4"],
  [300,"A","M_ANGB_EKG","ACTVT","03"],
  [300,"A","M_ANGB_EKG","ACTVT","08"],
  [300,"A","M_ANGB_EKG","ACTVT","09"],
  [300,"A","M_ANGB_EKG","ACTVT","F4"],
  [300,"A","M_ANGB_EKO","ACTVT","03"],
  [300,"A","M_ANGB_EKO","ACTVT","08"],
  [300,"A","M_ANGB_EKO","ACTVT","09"],
  [300,"A","M_ANGB_EKO","ACTVT","F4"],
  [300,"A","M_ANGB_LGO","ACTVT","F4"],
  [300,"A","M_ANGB_WRK","ACTVT","03"],
  [300,"A","M_ANGB_WRK","ACTVT","F4"],
  [300,"A","M_BANF_BSA","ACTVT","01"],
  [300,"A","M_BANF_BSA","ACTVT","02"],
  [300,"A","M_BANF_BSA","ACTVT","03"],
  [300,"A","M_BANF_BSA","ACTVT","06"],
  [300,"A","M_BANF_BSA","ACTVT","F4"],
  [300,"A","M_BANF_EKG","ACTVT","01"],
  [300,"A","M_BANF_EKG","ACTVT","02"],
  [300,"A","M_BANF_EKG","ACTVT","03"],
  [300,"A","M_BANF_EKG","ACTVT","06"],
  [300,"A","M_BANF_EKG","ACTVT","F4"],
  [300,"A","M_BANF_EKO","ACTVT","01"],
  [300,"A","M_BANF_EKO","ACTVT","02"],
  [300,"A","M_BANF_EKO","ACTVT","03"],
  [300,"A","M_BANF_EKO","ACTVT","06"],
  [300,"A","M_BANF_EKO","ACTVT","F4"],
  [300,"A","M_BANF_LGO","ACTVT","01"],
  [300,"A","M_BANF_LGO","ACTVT","02"],
  [300,"A","M_BANF_LGO","ACTVT","03"],
  [300,"A","M_BANF_WRK","ACTVT","01"],
  [300,"A","M_BANF_WRK","ACTVT","02"],
  [300,"A","M_BANF_WRK","ACTVT","03"],
  [300,"A","M_BANF_WRK","ACTVT","06"],
  [300,"A","M_BANF_WRK","ACTVT","F4"],
  [300,"A","M_BEST_BSA","ACTVT","01"],
  [300,"A","M_BEST_BSA","ACTVT","02"],
  [300,"A","M_BEST_BSA","ACTVT","03"],
  [300,"A","M_BEST_BSA","ACTVT","04"],
  [300,"A","M_BEST_BSA","ACTVT","06"],
  [300,"A","M_BEST_BSA","ACTVT","75"],
  [300,"A","M_BEST_BSA","ACTVT","76"],
  [300,"A","M_BEST_BSA","ACTVT","F4"],
  [300,"A","M_BEST_EKG","ACTVT","01"],
  [300,"A","M_BEST_EKG","ACTVT","02"],
  [300,"A","M_BEST_EKG","ACTVT","03"],
  [300,"A","M_BEST_EKG","ACTVT","04"],
  [300,"A","M_BEST_EKG","ACTVT","06"],
  [300,"A","M_BEST_EKG","ACTVT","75"],
  [300,"A","M_BEST_EKG","ACTVT","76"],
  [300,"A","M_BEST_EKG","ACTVT","F4"],
  [300,"A","M_BEST_EKO","ACTVT","01"],
  [300,"A","M_BEST_EKO","ACTVT","02"],
  [300,"A","M_BEST_EKO","ACTVT","03"],
  [300,"A","M_BEST_EKO","ACTVT","04"],
  [300,"A","M_BEST_EKO","ACTVT","06"],
  [300,"A","M_BEST_EKO","ACTVT","75"],
  [300,"A","M_BEST_EKO","ACTVT","76"],
  [300,"A","M_BEST_EKO","ACTVT","F4"],
  [300,"A","M_BEST_LGO","ACTVT","03"],
  [300,"A","M_BEST_WRK","ACTVT","01"],
  [300,"A","M_BEST_WRK","ACTVT","02"],
  [300,"A","M_BEST_WRK","ACTVT","03"],
  [300,"A","M_BEST_WRK","ACTVT","04"],
  [300,"A","M_BEST_WRK","ACTVT","06"],
  [300,"A","M_BEST_WRK","ACTVT","75"],
  [300,"A","M_BEST_WRK","ACTVT","76"],
  [300,"A","M_BEST_WRK","ACTVT","F4"],
  [300,"A","M_CAT_UTYP","ACTVT","03"],
  [300,"A","M_CCTR_BSA","ACTVT","01"],
  [300,"A","M_CCTR_BSA","ACTVT","02"],
  [300,"A","M_CCTR_BSA","ACTVT","03"],
  [300,"A","M_CCTR_BSA","ACTVT","04"],
  [300,"A","M_CCTR_BSA","ACTVT","06"],
  [300,"A","M_CCTR_EKG","ACTVT","01"],
  [300,"A","M_CCTR_EKG","ACTVT","02"],
  [300,"A","M_CCTR_EKG","ACTVT","03"],
  [300,"A","M_CCTR_EKG","ACTVT","04"],
  [300,"A","M_CCTR_EKG","ACTVT","06"],
  [300,"A","M_CCTR_EKO","ACTVT","01"],
  [300,"A","M_CCTR_EKO","ACTVT","02"],
  [300,"A","M_CCTR_EKO","ACTVT","03"],
  [300,"A","M_CCTR_EKO","ACTVT","04"],
  [300,"A","M_CCTR_EKO","ACTVT","06"],
  [300,"A","M_CONF_BSA","ACTVT","01"],
  [300,"A","M_CONF_BSA","ACTVT","02"],
  [300,"A","M_CONF_BSA","ACTVT","03"],
  [300,"A","M_CONF_BSA","ACTVT","F4"],
  [300,"A","M_CONF_EKG","ACTVT","01"],
  [300,"A","M_CONF_EKG","ACTVT","02"],
  [300,"A","M_CONF_EKG","ACTVT","03"],
  [300,"A","M_CONF_EKG","ACTVT","F4"],
  [300,"A","M_CONF_EKO","ACTVT","01"],
  [300,"A","M_CONF_EKO","ACTVT","02"],
  [300,"A","M_CONF_EKO","ACTVT","03"],
  [300,"A","M_CONF_EKO","ACTVT","F4"],
  [300,"A","M_CPO_BSA","ACTVT","02"],
  [300,"A","M_CPO_BSA","ACTVT","03"],
  [300,"A","M_CPO_BSA","ACTVT","04"],
  [300,"A","M_CPO_BSA","ACTVT","F4"],
  [300,"A","M_CPO_EKG","ACTVT","02"],
  [300,"A","M_CPO_EKG","ACTVT","03"],
  [300,"A","M_CPO_EKG","ACTVT","04"],
  [300,"A","M_CPO_EKG","ACTVT","F4"],
  [300,"A","M_CPO_EKO","ACTVT","02"],
  [300,"A","M_CPO_EKO","ACTVT","03"],
  [300,"A","M_CPO_EKO","ACTVT","04"],
  [300,"A","M_CPO_EKO","ACTVT","F4"],
  [300,"A","M_CPO_WRK","ACTVT","02"],
  [300,"A","M_CPO_WRK","ACTVT","03"],
  [300,"A","M_CPO_WRK","ACTVT","04"],
  [300,"A","M_CPO_WRK","ACTVT","F4"],
  [300,"A","M_CPR_BSA","ACTVT","01"],
  [300,"A","M_CPR_BSA","ACTVT","02"],
  [300,"A","M_CPR_BSA","ACTVT","03"],
  [300,"A","M_CPR_BSA","ACTVT","06"],
  [300,"A","M_CPR_BSA","ACTVT","F4"],
  [300,"A","M_CPR_EKG","ACTVT","01"],
  [300,"A","M_CPR_EKG","ACTVT","02"],
  [300,"A","M_CPR_EKG","ACTVT","03"],
  [300,"A","M_CPR_EKG","ACTVT","06"],
  [300,"A","M_CPR_EKG","ACTVT","F4"],
  [300,"A","M_CPR_EKO","ACTVT","01"],
  [300,"A","M_CPR_EKO","ACTVT","02"],
  [300,"A","M_CPR_EKO","ACTVT","03"],
  [300,"A","M_CPR_EKO","ACTVT","06"],
  [300,"A","M_CPR_EKO","ACTVT","F4"],
  [300,"A","M_CPR_WRK","ACTVT","01"],
  [300,"A","M_CPR_WRK","ACTVT","02"],
  [300,"A","M_CPR_WRK","ACTVT","03"],
  [300,"A","M_CPR_WRK","ACTVT","06"],
  [300,"A","M_CPR_WRK","ACTVT","F4"],
  [300,"A","M_CQTN_BSA","ACTVT","03"],
  [300,"A","M_CQTN_BSA","ACTVT","F4"],
  [300,"A","M_CQTN_EKG","ACTVT","03"],
  [300,"A","M_CQTN_EKO","ACTVT","03"],
  [300,"A","M_CR_WERKS","ACTVT","01"],
  [300,"A","M_CR_WERKS","ACTVT","02"],
  [300,"A","M_CR_WERKS","ACTVT","03"],
  [300,"A","M_CR_WERKS","ACTVT","F4"],
  [300,"A","M_CRFQ_BSA","ACTVT","03"],
  [300,"A","M_CRFQ_EKG","ACTVT","03"],
  [300,"A","M_CRFQ_EKO","ACTVT","03"],
  [300,"A","M_CTLG_ACC","ACTVT","03"],
  [300,"A","M_CTLG_ACC","ACTVT","F4"],
  [300,"A","M_CTLG_PRP","ACTVT","03"],
  [300,"A","M_EINF_EKG","ACTVT","02"],
  [300,"A","M_EINF_EKG","ACTVT","03"],
  [300,"A","M_EINF_EKG","ACTVT","F4"],
  [300,"A","M_EINF_EKO","ACTVT","02"],
  [300,"A","M_EINF_EKO","ACTVT","03"],
  [300,"A","M_EINF_EKO","ACTVT","08"],
  [300,"A","M_EINF_EKO","ACTVT","F4"],
  [300,"A","M_EINF_WRK","ACTVT","02"],
  [300,"A","M_EINF_WRK","ACTVT","03"],
  [300,"A","M_EINF_WRK","ACTVT","F4"],
  [300,"A","M_FCDM_ORG","ACTVT","01"],
  [300,"A","M_FCDM_ORG","ACTVT","02"],
  [300,"A","M_FCDM_ORG","ACTVT","03"],
  [300,"A","M_FCDM_ORG","ACTVT","43"],
  [300,"A","M_FORECAST","ACTVT","16"],
  [300,"A","M_HUB_MAR","ACTVT","01"],
  [300,"A","M_HUB_MAR","ACTVT","02"],
  [300,"A","M_HUB_MAR","ACTVT","03"],
  [300,"A","M_HUB_MAR","ACTVT","06"],
  [300,"A","M_HUB_MAR","ACTVT","F4"],
  [300,"A","M_HUB_MAT","ACTVT","01"],
  [300,"A","M_HUB_MAT","ACTVT","02"],
  [300,"A","M_HUB_MAT","ACTVT","03"],
  [300,"A","M_HUB_MAT","ACTVT","06"],
  [300,"A","M_HUB_MAT","ACTVT","F4"],
  [300,"A","M_HUB_WGR","ACTVT","01"],
  [300,"A","M_HUB_WGR","ACTVT","02"],
  [300,"A","M_HUB_WGR","ACTVT","03"],
  [300,"A","M_HUB_WGR","ACTVT","06"],
  [300,"A","M_HUB_WGR","ACTVT","F4"],
  [300,"A","M_INFO_ANL","ACTVT","71"],
  [300,"A","M_INVVAL","ACTVT","03"],
  [300,"A","M_ISEG_WDB","ACTVT","01"],
  [300,"A","M_ISEG_WDB","ACTVT","03"],
  [300,"A","M_ISEG_WDB","ACTVT","04"],
  [300,"A","M_ISEG_WIB","ACTVT","01"],
  [300,"A","M_ISEG_WIB","ACTVT","02"],
  [300,"A","M_ISEG_WIB","ACTVT","03"],
  [300,"A","M_ISEG_WIB","ACTVT","04"],
  [300,"A","M_ISEG_WIB","ACTVT","06"],
  [300,"A","M_ISEG_WZB","ACTVT","01"],
  [300,"A","M_ISEG_WZL","ACTVT","01"],
  [300,"A","M_ISEG_WZL","ACTVT","02"],
  [300,"A","M_ISEG_WZL","ACTVT","03"],
  [300,"A","M_ISEG_WZL","ACTVT","AI"],
  [300,"A","M_LFA1_BEK","ACTVT","03"],
  [300,"A","M_LFA1_BEK","ACTVT","F4"],
  [300,"A","M_LFA1_GEN","ACTVT","01"],
  [300,"A","M_LFA1_GEN","ACTVT","02"],
  [300,"A","M_LFA1_GEN","ACTVT","03"],
  [300,"A","M_LFA1_GEN","ACTVT","05"],
  [300,"A","M_LFA1_GEN","ACTVT","06"],
  [300,"A","M_LFA1_GEN","ACTVT","C8"],
  [300,"A","M_LFA1_GEN","ACTVT","F4"],
  [300,"A","M_LFA1_GRP","ACTVT","01"],
  [300,"A","M_LFA1_GRP","ACTVT","02"],
  [300,"A","M_LFA1_GRP","ACTVT","03"],
  [300,"A","M_LFA1_GRP","ACTVT","05"],
  [300,"A","M_LFA1_GRP","ACTVT","06"],
  [300,"A","M_LFA1_GRP","ACTVT","C8"],
  [300,"A","M_LFA1_GRP","ACTVT","F4"],
  [300,"A","M_LFM1_EKO","ACTVT","03"],
  [300,"A","M_LFM1_EKO","ACTVT","F4"],
  [300,"A","M_LIFO_MLY","ACTVT","03"],
  [300,"A","M_LPET_BSA","ACTVT","01"],
  [300,"A","M_LPET_BSA","ACTVT","02"],
  [300,"A","M_LPET_BSA","ACTVT","03"],
  [300,"A","M_LPET_EKG","ACTVT","01"],
  [300,"A","M_LPET_EKG","ACTVT","02"],
  [300,"A","M_LPET_EKG","ACTVT","03"],
  [300,"A","M_LPET_EKO","ACTVT","01"],
  [300,"A","M_LPET_EKO","ACTVT","02"],
  [300,"A","M_LPET_EKO","ACTVT","03"],
  [300,"A","M_LPET_WRK","ACTVT","01"],
  [300,"A","M_LPET_WRK","ACTVT","02"],
  [300,"A","M_LPET_WRK","ACTVT","03"],
  [300,"A","M_LPET_WRK","ACTVT","F4"],
  [300,"A","M_MATE_BUK","ACTVT","03"],
  [300,"A","M_MATE_CHG","ACTVT","01"],
  [300,"A","M_MATE_CHG","ACTVT","02"],
  [300,"A","M_MATE_CHG","ACTVT","03"],
  [300,"A","M_MATE_CHG","ACTVT","F4"],
  [300,"A","M_MATE_CHP","ACTVT","01"],
  [300,"A","M_MATE_CHP","ACTVT","02"],
  [300,"A","M_MATE_DRV","ACTVT","03"],
  [300,"A","M_MATE_LGN","ACTVT","03"],
  [300,"A","M_MATE_MAN","ACTVT","01"],
  [300,"A","M_MATE_MAN","ACTVT","02"],
  [300,"A","M_MATE_MAN","ACTVT","03"],
  [300,"A","M_MATE_MAN","ACTVT","06"],
  [300,"A","M_MATE_MAN","ACTVT","F4"],
  [300,"A","M_MATE_MAR","ACTVT","01"],
  [300,"A","M_MATE_MAR","ACTVT","02"],
  [300,"A","M_MATE_MAR","ACTVT","03"],
  [300,"A","M_MATE_MAR","ACTVT","06"],
  [300,"A","M_MATE_MAR","ACTVT","F4"],
  [300,"A","M_MATE_MAT","ACTVT","01"],
  [300,"A","M_MATE_MAT","ACTVT","02"],
  [300,"A","M_MATE_MAT","ACTVT","03"],
  [300,"A","M_MATE_MAT","ACTVT","06"],
  [300,"A","M_MATE_MAT","ACTVT","F4"],
  [300,"A","M_MATE_MEX","ACTVT","03"],
  [300,"A","M_MATE_MTA","ACTVT","02"],
  [300,"A","M_MATE_MZP","ACTVT","03"],
  [300,"A","M_MATE_STA","ACTVT","01"],
  [300,"A","M_MATE_STA","ACTVT","02"],
  [300,"A","M_MATE_STA","ACTVT","03"],
  [300,"A","M_MATE_STA","ACTVT","06"],
  [300,"A","M_MATE_VKO","ACTVT","01"],
  [300,"A","M_MATE_VKO","ACTVT","02"],
  [300,"A","M_MATE_VKO","ACTVT","03"],
  [300,"A","M_MATE_VKO","ACTVT","06"],
  [300,"A","M_MATE_VKO","ACTVT","F4"],
  [300,"A","M_MATE_WGR","ACTVT","01"],
  [300,"A","M_MATE_WGR","ACTVT","02"],
  [300,"A","M_MATE_WGR","ACTVT","03"],
  [300,"A","M_MATE_WGR","ACTVT","06"],
  [300,"A","M_MATE_WGR","ACTVT","F4"],
  [300,"A","M_MATE_WRK","ACTVT","01"],
  [300,"A","M_MATE_WRK","ACTVT","02"],
  [300,"A","M_MATE_WRK","ACTVT","03"],
  [300,"A","M_MATE_WRK","ACTVT","06"],
  [300,"A","M_MATE_WRK","ACTVT","F4"],
  [300,"A","M_MIPA_ORG","ACTVT","03"],
  [300,"A","M_MMSEVAL","ACTVT","03"],
  [300,"A","M_MRES_BWA","ACTVT","01"],
  [300,"A","M_MRES_BWA","ACTVT","02"],
  [300,"A","M_MRES_BWA","ACTVT","03"],
  [300,"A","M_MRES_BWA","ACTVT","06"],
  [300,"A","M_MRES_BWA","ACTVT","65"],
  [300,"A","M_MRES_BWA","ACTVT","70"],
  [300,"A","M_MRES_BWA","ACTVT","F4"],
  [300,"A","M_MRES_WWA","ACTVT","01"],
  [300,"A","M_MRES_WWA","ACTVT","02"],
  [300,"A","M_MRES_WWA","ACTVT","03"],
  [300,"A","M_MRES_WWA","ACTVT","06"],
  [300,"A","M_MRES_WWA","ACTVT","65"],
  [300,"A","M_MRES_WWA","ACTVT","70"],
  [300,"A","M_MRES_WWA","ACTVT","F4"],
  [300,"A","M_MSEG_BMB","ACTVT","01"],
  [300,"A","M_MSEG_BMB","ACTVT","02"],
  [300,"A","M_MSEG_BMB","ACTVT","03"],
  [300,"A","M_MSEG_BWA","ACTVT","01"],
  [300,"A","M_MSEG_BWA","ACTVT","02"],
  [300,"A","M_MSEG_BWA","ACTVT","03"],
  [300,"A","M_MSEG_BWE","ACTVT","01"],
  [300,"A","M_MSEG_BWE","ACTVT","02"],
  [300,"A","M_MSEG_BWE","ACTVT","03"],
  [300,"A","M_MSEG_BWF","ACTVT","01"],
  [300,"A","M_MSEG_BWF","ACTVT","02"],
  [300,"A","M_MSEG_BWF","ACTVT","03"],
  [300,"A","M_MSEG_LGO","ACTVT","01"],
  [300,"A","M_MSEG_LGO","ACTVT","02"],
  [300,"A","M_MSEG_LGO","ACTVT","03"],
  [300,"A","M_MSEG_LGO","ACTVT","F4"],
  [300,"A","M_MSEG_WMB","ACTVT","01"],
  [300,"A","M_MSEG_WMB","ACTVT","02"],
  [300,"A","M_MSEG_WMB","ACTVT","03"],
  [300,"A","M_MSEG_WMB","ACTVT","04"],
  [300,"A","M_MSEG_WMB","ACTVT","06"],
  [300,"A","M_MSEG_WMB","ACTVT","69"],
  [300,"A","M_MSEG_WMB","ACTVT","F4"],
  [300,"A","M_MSEG_WWA","ACTVT","01"],
  [300,"A","M_MSEG_WWA","ACTVT","02"],
  [300,"A","M_MSEG_WWA","ACTVT","03"],
  [300,"A","M_MSEG_WWA","ACTVT","04"],
  [300,"A","M_MSEG_WWE","ACTVT","01"],
  [300,"A","M_MSEG_WWE","ACTVT","02"],
  [300,"A","M_MSEG_WWE","ACTVT","03"],
  [300,"A","M_MSEG_WWE","ACTVT","04"],
  [300,"A","M_MSEG_WWF","ACTVT","01"],
  [300,"A","M_MSEG_WWF","ACTVT","02"],
  [300,"A","M_MSEG_WWF","ACTVT","03"],
  [300,"A","M_MSEG_WWF","ACTVT","F4"],
  [300,"A","M_NGN_TYPE","ACTVT","01"],
  [300,"A","M_NGN_TYPE","ACTVT","02"],
  [300,"A","M_NGN_TYPE","ACTVT","03"],
  [300,"A","M_NGN_TYPE","ACTVT","F4"],
  [300,"A","M_NIWE_BIL","ACTVT","03"],
  [300,"A","M_ORDR_EKO","ACTVT","02"],
  [300,"A","M_ORDR_EKO","ACTVT","03"],
  [300,"A","M_ORDR_EKO","ACTVT","06"],
  [300,"A","M_ORDR_WRK","ACTVT","02"],
  [300,"A","M_ORDR_WRK","ACTVT","03"],
  [300,"A","M_ORDR_WRK","ACTVT","06"],
  [300,"A","M_PIC_EXCH","ACTVT","F4"],
  [300,"A","M_PMRP","ACTVT","03"],
  [300,"A","M_PMRP","ACTVT","F4"],
  [300,"A","M_PP_BUK","ACTVT","03"],
  [300,"A","M_PP_BUK","ACTVT","F4"],
  [300,"A","M_QTN_BSA","ACTVT","01"],
  [300,"A","M_QTN_BSA","ACTVT","02"],
  [300,"A","M_QTN_BSA","ACTVT","03"],
  [300,"A","M_QTN_BSA","ACTVT","F4"],
  [300,"A","M_QTN_EKG","ACTVT","01"],
  [300,"A","M_QTN_EKG","ACTVT","02"],
  [300,"A","M_QTN_EKG","ACTVT","03"],
  [300,"A","M_QTN_EKG","ACTVT","F4"],
  [300,"A","M_QTN_EKO","ACTVT","01"],
  [300,"A","M_QTN_EKO","ACTVT","02"],
  [300,"A","M_QTN_EKO","ACTVT","03"],
  [300,"A","M_QTN_EKO","ACTVT","F4"],
  [300,"A","M_QTN_WRK","ACTVT","01"],
  [300,"A","M_QTN_WRK","ACTVT","02"],
  [300,"A","M_QTN_WRK","ACTVT","03"],
  [300,"A","M_QTN_WRK","ACTVT","F4"],
  [300,"A","M_QUOT_WRK","ACTVT","03"],
  [300,"A","M_RAHM_BSA","ACTVT","01"],
  [300,"A","M_RAHM_BSA","ACTVT","02"],
  [300,"A","M_RAHM_BSA","ACTVT","03"],
  [300,"A","M_RAHM_BSA","ACTVT","04"],
  [300,"A","M_RAHM_BSA","ACTVT","06"],
  [300,"A","M_RAHM_BSA","ACTVT","F4"],
  [300,"A","M_RAHM_EKG","ACTVT","01"],
  [300,"A","M_RAHM_EKG","ACTVT","02"],
  [300,"A","M_RAHM_EKG","ACTVT","03"],
  [300,"A","M_RAHM_EKG","ACTVT","04"],
  [300,"A","M_RAHM_EKG","ACTVT","06"],
  [300,"A","M_RAHM_EKG","ACTVT","F4"],
  [300,"A","M_RAHM_EKO","ACTVT","01"],
  [300,"A","M_RAHM_EKO","ACTVT","02"],
  [300,"A","M_RAHM_EKO","ACTVT","03"],
  [300,"A","M_RAHM_EKO","ACTVT","04"],
  [300,"A","M_RAHM_EKO","ACTVT","06"],
  [300,"A","M_RAHM_EKO","ACTVT","F4"],
  [300,"A","M_RAHM_LGO","ACTVT","01"],
  [300,"A","M_RAHM_LGO","ACTVT","02"],
  [300,"A","M_RAHM_LGO","ACTVT","03"],
  [300,"A","M_RAHM_LGO","ACTVT","04"],
  [300,"A","M_RAHM_LGO","ACTVT","F4"],
  [300,"A","M_RAHM_WRK","ACTVT","01"],
  [300,"A","M_RAHM_WRK","ACTVT","02"],
  [300,"A","M_RAHM_WRK","ACTVT","03"],
  [300,"A","M_RAHM_WRK","ACTVT","04"],
  [300,"A","M_RAHM_WRK","ACTVT","06"],
  [300,"A","M_RAHM_WRK","ACTVT","F4"],
  [300,"A","M_RECH_AKZ","ACTVT","03"],
  [300,"A","M_RECH_BUK","ACTVT","03"],
  [300,"A","M_RECH_BUK","ACTVT","04"],
  [300,"A","M_RECH_BUK","ACTVT","43"],
  [300,"A","M_RECH_BUK","ACTVT","77"],
  [300,"A","M_RECH_BUK","ACTVT","F4"],
  [300,"A","M_RECH_BUK","ACTVT","UL"],
  [300,"A","M_RECH_EKG","ACTVT","03"],
  [300,"A","M_RECH_UPL","ACTVT","01"],
  [300,"A","M_RECH_UPL","ACTVT","03"],
  [300,"A","M_RECH_UPL","ACTVT","06"],
  [300,"A","M_RECH_WF","ACTVT","16"],
  [300,"A","M_RECH_WFI","ACTVT","03"],
  [300,"A","M_RECH_WRK","ACTVT","03"],
  [300,"A","M_RECH_WRK","ACTVT","04"],
  [300,"A","M_RECH_WRK","ACTVT","77"],
  [300,"A","M_RECH_WRK","ACTVT","F4"],
  [300,"A","M_RFQ_BSA","ACTVT","01"],
  [300,"A","M_RFQ_BSA","ACTVT","02"],
  [300,"A","M_RFQ_BSA","ACTVT","03"],
  [300,"A","M_RFQ_BSA","ACTVT","F4"],
  [300,"A","M_RFQ_EKG","ACTVT","01"],
  [300,"A","M_RFQ_EKG","ACTVT","02"],
  [300,"A","M_RFQ_EKG","ACTVT","03"],
  [300,"A","M_RFQ_EKG","ACTVT","F4"],
  [300,"A","M_RFQ_EKO","ACTVT","01"],
  [300,"A","M_RFQ_EKO","ACTVT","02"],
  [300,"A","M_RFQ_EKO","ACTVT","03"],
  [300,"A","M_RFQ_EKO","ACTVT","F4"],
  [300,"A","M_RFQ_WRK","ACTVT","01"],
  [300,"A","M_RFQ_WRK","ACTVT","02"],
  [300,"A","M_RFQ_WRK","ACTVT","03"],
  [300,"A","M_RFQ_WRK","ACTVT","F4"],
  [300,"A","M_SES_EKG","ACTVT","01"],
  [300,"A","M_SES_EKG","ACTVT","02"],
  [300,"A","M_SES_EKG","ACTVT","03"],
  [300,"A","M_SES_EKG","ACTVT","06"],
  [300,"A","M_SES_EKG","ACTVT","37"],
  [300,"A","M_SES_EKG","ACTVT","74"],
  [300,"A","M_SES_EKG","ACTVT","F4"],
  [300,"A","M_SES_EKO","ACTVT","01"],
  [300,"A","M_SES_EKO","ACTVT","02"],
  [300,"A","M_SES_EKO","ACTVT","03"],
  [300,"A","M_SES_EKO","ACTVT","06"],
  [300,"A","M_SES_EKO","ACTVT","37"],
  [300,"A","M_SES_EKO","ACTVT","74"],
  [300,"A","M_SES_EKO","ACTVT","F4"],
  [300,"A","M_SES_WRK","ACTVT","01"],
  [300,"A","M_SES_WRK","ACTVT","02"],
  [300,"A","M_SES_WRK","ACTVT","03"],
  [300,"A","M_SES_WRK","ACTVT","06"],
  [300,"A","M_SES_WRK","ACTVT","37"],
  [300,"A","M_SES_WRK","ACTVT","74"],
  [300,"A","M_SES_WRK","ACTVT","F4"],
  [300,"A","M_SP_BUKRS","ACTVT","01"],
  [300,"A","M_SP_BUKRS","ACTVT","02"],
  [300,"A","M_SP_BUKRS","ACTVT","03"],
  [300,"A","M_SP_BUKRS","ACTVT","F4"],
  [300,"A","M_SP_EKG","ACTVT","03"],
  [300,"A","M_SP_EKG","ACTVT","F4"],
  [300,"A","M_SP_EKO","ACTVT","03"],
  [300,"A","M_SP_EKO","ACTVT","F4"],
  [300,"A","M_SP_MATKL","ACTVT","01"],
  [300,"A","M_SP_MATKL","ACTVT","02"],
  [300,"A","M_SP_MATKL","ACTVT","03"],
  [300,"A","M_SP_MATKL","ACTVT","F4"],
  [300,"A","M_SP_TYPE","ACTVT","01"],
  [300,"A","M_SP_TYPE","ACTVT","02"],
  [300,"A","M_SP_TYPE","ACTVT","03"],
  [300,"A","M_SP_TYPE","ACTVT","F4"],
  [300,"A","M_SPQ_BUKR","ACTVT","01"],
  [300,"A","M_SPQ_BUKR","ACTVT","02"],
  [300,"A","M_SPQ_BUKR","ACTVT","03"],
  [300,"A","M_SPQ_BUKR","ACTVT","F4"],
  [300,"A","M_SPQ_EKG","ACTVT","03"],
  [300,"A","M_SPQ_EKO","ACTVT","03"],
  [300,"A","M_SPQ_MATK","ACTVT","01"],
  [300,"A","M_SPQ_MATK","ACTVT","02"],
  [300,"A","M_SPQ_MATK","ACTVT","03"],
  [300,"A","M_SPQ_MATK","ACTVT","F4"],
  [300,"A","M_SPQ_TYPE","ACTVT","03"],
  [300,"A","M_SRSLARTP","ACTVT","03"],
  [300,"A","M_SRSLARTP","ACTVT","22"],
  [300,"A","M_SRSLARTP","ACTVT","F4"],
  [300,"A","M_TEMPLATE","ACTVT","01"],
  [300,"A","M_TEMPLATE","ACTVT","02"],
  [300,"A","M_TEMPLATE","ACTVT","03"],
  [300,"A","M_TEMPLATE","ACTVT","06"],
  [300,"A","M_USRDF_CC","ACTVT","02"],
  [300,"A","M_USRDF_CC","ACTVT","03"],
  [300,"A","M_XLS_INTG","ACTVT","64"],
  [300,"A","M_XLS_INTG","ACTVT","DL"],
  [300,"A","M_XLS_INTG","ACTVT","UL"],
  [300,"A","MDC_ADMIN","ACTVT","02"],
  [300,"A","MDC_ADMIN","ACTVT","06"],
  [300,"A","MDC_DQEVAL","ACTVT","01"],
  [300,"A","MDC_DQEVAL","ACTVT","02"],
  [300,"A","MDC_DQEVAL","ACTVT","03"],
  [300,"A","MDC_DQEVAL","ACTVT","06"],
  [300,"A","MDC_DQEVAL","ACTVT","31"],
  [300,"A","MDC_DQEVAL","ACTVT","36"],
  [300,"A","MDC_LOAD","ACTVT","01"],
  [300,"A","MDC_LOAD","ACTVT","02"],
  [300,"A","MDC_LOAD","ACTVT","03"],
  [300,"A","MDC_LOAD","ACTVT","06"],
  [300,"A","MDC_LOAD","ACTVT","16"],
  [300,"A","MDC_MASS","ACTVT","01"],
  [300,"A","MDC_MASS","ACTVT","02"],
  [300,"A","MDC_MASS","ACTVT","03"],
  [300,"A","MDC_MASS","ACTVT","06"],
  [300,"A","MDC_MASS","ACTVT","31"],
  [300,"A","MDC_MASS","ACTVT","36"],
  [300,"A","MDC_MASSBS","ACTVT","01"],
  [300,"A","MDC_MASSBS","ACTVT","02"],
  [300,"A","MDC_MASSBS","ACTVT","03"],
  [300,"A","MDC_MASSBS","ACTVT","06"],
  [300,"A","MDC_MASSBS","ACTVT","31"],
  [300,"A","MDC_PROOT","ACTVT","01"],
  [300,"A","MDC_PROOT","ACTVT","02"],
  [300,"A","MDC_PROOT","ACTVT","03"],
  [300,"A","MDC_PROOT","ACTVT","06"],
  [300,"A","MDC_PROOT","ACTVT","31"],
  [300,"A","MDC_PROOT","ACTVT","36"],
  [300,"A","MDC_PROOT","ACTVT","37"],
  [300,"A","MDG_IDM","ACTVT","03"],
  [300,"A","MDQ_EVAL","ACTVT","03"],
  [300,"A","MDQ_RULREP","ACTVT","03"],
  [300,"A","MDQRM_RULE","ACTVT","03"],
  [300,"A","MDQRM_RULE","ACTVT","F4"],
  [300,"A","MFG_PLNR","ACTVT","03"],
  [300,"A","P_CATSXT","ACTVT","*"],
  [300,"A","P_PACKSPEC","ACTVT","03"],
  [300,"A","P_PKGBUILD","ACTVT","03"],
  [300,"A","PLM_ASSGOB","ACTVT","03"],
  [300,"A","PLM_PPEPF","ACTVT","03"],
  [300,"A","PLM_PPEPI","ACTVT","03"],
  [300,"A","PLM_PPEPIV","ACTVT","03"],
  [300,"A","PLM_PPEPV","ACTVT","03"],
  [300,"A","PLM_SWC","ACTVT","03"],
  [300,"A","POC_DEFN","ACTVT","03"],
  [300,"A","POC_DEFN","ACTVT","F4"],
  [300,"A","Q_CERT_IN","ACTVT","03"],
  [300,"A","Q_CGRP_ACT","ACTVT","03"],
  [300,"A","Q_CGRP_ACT","ACTVT","F4"],
  [300,"A","Q_CHARGRP","ACTVT","03"],
  [300,"A","Q_CSSET_AC","ACTVT","03"],
  [300,"A","Q_CSSET_AC","ACTVT","F4"],
  [300,"A","Q_CTRLCHRT","ACTVT","03"],
  [300,"A","Q_CTRLCHRT","ACTVT","63"],
  [300,"A","Q_CTRLCHRT","ACTVT","93"],
  [300,"A","Q_DEFCAT","ACTVT","01"],
  [300,"A","Q_DEFCAT","ACTVT","02"],
  [300,"A","Q_DEFCAT","ACTVT","03"],
  [300,"A","Q_DEFCAT","ACTVT","06"],
  [300,"A","Q_DEFCAT","ACTVT","F4"],
  [300,"A","Q_DEFECT","ACTVT","01"],
  [300,"A","Q_DEFECT","ACTVT","02"],
  [300,"A","Q_DEFECT","ACTVT","03"],
  [300,"A","Q_DEFECT","ACTVT","06"],
  [300,"A","Q_DYNMODRL","ACTVT","03"],
  [300,"A","Q_DYNMODRL","ACTVT","F4"],
  [300,"A","Q_INSPPNT","ACTVT","02"],
  [300,"A","Q_INSPPNT","ACTVT","03"],
  [300,"A","Q_INSPQUAL","ACTVT","03"],
  [300,"A","Q_INSPQUAL","ACTVT","F4"],
  [300,"A","Q_MATSMPL","ACTVT","03"],
  [300,"A","Q_MATSMPL","ACTVT","F4"],
  [300,"A","Q_PRSLVPR","ACTVT","03"],
  [300,"A","Q_QIRECPRC","ACTVT","03"],
  [300,"A","Q_QLEVEL","ACTVT","03"],
  [300,"A","Q_RELSPDMR","ACTVT","03"],
  [300,"A","Q_ROUT","ACTVT","01"],
  [300,"A","Q_ROUT","ACTVT","02"],
  [300,"A","Q_ROUT","ACTVT","03"],
  [300,"A","Q_ROUT","ACTVT","06"],
  [300,"A","Q_ROUT","ACTVT","F4"],
  [300,"A","Q_SMPLPROC","ACTVT","03"],
  [300,"A","Q_SMPLPROC","ACTVT","F4"],
  [300,"A","Q_SMPLSCHM","ACTVT","03"],
  [300,"A","Q_SMPLSCHM","ACTVT","F4"],
  [300,"A","Q_SMPLSLOC","ACTVT","F4"],
  [300,"A","Q_TASK","ACTVT","01"],
  [300,"A","Q_TASK","ACTVT","02"],
  [300,"A","Q_TASK","ACTVT","03"],
  [300,"A","Q_TASK","ACTVT","06"],
  [300,"A","QIE_ELEM","ACTVT","03"],
  [300,"A","QIE_INSPDO","ACTVT","01"],
  [300,"A","QIE_INSPDO","ACTVT","02"],
  [300,"A","QIE_INSPDO","ACTVT","03"],
  [300,"A","QIE_INSPDO","ACTVT","06"],
  [300,"A","QIE_INSPDO","ACTVT","75"],
  [300,"A","QIE_INSPDO","ACTVT","85"],
  [300,"A","QIE_IRULE","ACTVT","01"],
  [300,"A","QIE_IRULE","ACTVT","03"],
  [300,"A","RLFW_SCHED","ACTVT","03"],
  [300,"A","RSH_RES","ACTVT","03"],
  [300,"A","RSMTEAM","ACTVT","01"],
  [300,"A","RSMTEAM","ACTVT","02"],
  [300,"A","RSMTEAM","ACTVT","03"],
  [300,"A","RSMTEAM","ACTVT","06"],
  [300,"A","RSMTEAM","ACTVT","F4"],
  [300,"A","S_ABPLNGVS","ACTVT","03"],
  [300,"A","S_APJ_JCE","ACTVT","02"],
  [300,"A","S_APJ_JCE","ACTVT","03"],
  [300,"A","S_APJ_JCE","ACTVT","06"],
  [300,"A","S_ARCD_OBJ","ACTVT","03"],
  [300,"A","S_ARCD_RES","ACTVT","03"],
  [300,"A","S_ARCD_RUN","ACTVT","03"],
  [300,"A","S_ATO_COL","ACTVT","03"],
  [300,"A","S_ATO_SET","ACTVT","03"],
  [300,"A","S_BGRFC_QG","ACTVT","03"],
  [300,"A","S_CALENDAR","ACTVT","03"],
  [300,"A","S_CDR_ADM","ACTVT","03"],
  [300,"A","S_COM_CSTP","ACTVT","03"],
  [300,"A","S_DEVELOP","ACTVT","16"],
  [300,"A","S_DMC_S_R","ACTVT","03"],
  [300,"A","S_DMC_S_R","ACTVT","33"],
  [300,"A","S_DMCC","ACTVT","03"],
  [300,"A","S_DMIS","ACTVT","03"],
  [300,"A","S_DMIS_ANA","ACTVT","03"],
  [300,"A","S_DMIS_MOM","ACTVT","03"],
  [300,"A","S_DMIS_RFC","ACTVT","03"],
  [300,"A","S_DMIS_RFC","ACTVT","33"],
  [300,"A","S_DMIS_SL","ACTVT","03"],
  [300,"A","S_DMIS_TR","ACTVT","03"],
  [300,"A","S_EIT","ACTVT","03"],
  [300,"A","S_ESH_ADM","ACTVT","01"],
  [300,"A","S_ESH_ADM","ACTVT","02"],
  [300,"A","S_ESH_ADM","ACTVT","03"],
  [300,"A","S_ESH_ADM","ACTVT","06"],
  [300,"A","S_ESH_QL","ACTVT","23"],
  [300,"A","S_ESH_QL","ACTVT","65"],
  [300,"A","S_GOS_ATT","ACTVT","02"],
  [300,"A","S_GOS_ATT","ACTVT","06"],
  [300,"A","S_HTTP_TRU","ACTVT","03"],
  [300,"A","S_IAM_BR","ACTVT","03"],
  [300,"A","S_IDOCDEFT","ACTVT","03"],
  [300,"A","S_INF_CONF","ACTVT","03"],
  [300,"A","S_IRM_BLOC","ACTVT","03"],
  [300,"A","S_IRM_OG","ACTVT","03"],
  [300,"A","S_IRM_OTC","ACTVT","03"],
  [300,"A","S_IRM_OTC","ACTVT","08"],
  [300,"A","S_IRM_OTPT","ACTVT","03"],
  [300,"A","S_IRM_OTPT","ACTVT","08"],
  [300,"A","S_IRM_POL","ACTVT","03"],
  [300,"A","S_IRM_POL","ACTVT","08"],
  [300,"A","S_IRM_PT","ACTVT","03"],
  [300,"A","S_IRM_PT","ACTVT","08"],
  [300,"A","S_IRM_RG","ACTVT","03"],
  [300,"A","S_IRM_RLGN","ACTVT","08"],
  [300,"A","S_ISM_DEPL","ACTVT","03"],
  [300,"A","S_ISM_INFE","ACTVT","03"],
  [300,"A","S_ISM_MC","ACTVT","03"],
  [300,"A","S_ISM_MDL","ACTVT","03"],
  [300,"A","S_ISM_MVER","ACTVT","03"],
  [300,"A","S_ISM_PS","ACTVT","03"],
  [300,"A","S_KSR","ACTVT","03"],
  [300,"A","S_KSR_CFG","ACTVT","03"],
  [300,"A","S_NUMBER","ACTVT","03"],
  [300,"A","S_OA2_CL","ACTVT","03"],
  [300,"A","S_OA2C_ADM","ACTVT","03"],
  [300,"A","S_ODQ_MON","ACTVT","03"],
  [300,"A","S_PSE_ADM","ACTVT","03"],
  [300,"A","S_RAL_CFG","ACTVT","03"],
  [300,"A","S_RAL_CLIS","ACTVT","03"],
  [300,"A","S_RAL_ELOG","ACTVT","03"],
  [300,"A","S_RAL_LDOM","ACTVT","03"],
  [300,"A","S_RAL_LOG","ACTVT","03"],
  [300,"A","S_RAL_PURP","ACTVT","03"],
  [300,"A","S_RAL_REC","ACTVT","03"],
  [300,"A","S_RS_ZEN","ACTVT","03"],
  [300,"A","S_RS_ZEN","ACTVT","16"],
  [300,"A","S_SAL_LOG","ACTVT","03"],
  [300,"A","S_SMB_ACTV","ACTVT","03"],
  [300,"A","S_SMB_ADM","ACTVT","03"],
  [300,"A","S_SMB_AUTH","ACTVT","03"],
  [300,"A","S_SMB_LCPR","ACTVT","03"],
  [300,"A","S_SRT_SEQ","ACTVT","03"],
  [300,"A","S_SSCUI_FL","ACTVT","03"],
  [300,"A","S_TABU_LIN","ACTVT","02"],
  [300,"A","S_TABU_LIN","ACTVT","03"],
  [300,"A","S_TABU_NAM","ACTVT","02"],
  [300,"A","S_TMC_DP","ACTVT","03"],
  [300,"A","S_TMC_TM","ACTVT","03"],
  [300,"A","S_TRANSPRT","ACTVT","03"],
  [300,"A","S_UCON_WHI","ACTVT","03"],
  [300,"A","S_USER_BLK","ACTVT","03"],
  [300,"A","S_USER_GRP","ACTVT","02"],
  [300,"A","S_USER_GRP","ACTVT","06"],
  [300,"A","S_USER_PRO","ACTVT","03"],
  [300,"A","S_VIEW_REP","ACTVT","03"],
  [300,"A","S_WFDEFADM","ACTVT","03"],
  [300,"A","S_WFFLXDEF","ACTVT","03"],
  [300,"A","S_WFFLXDEF","ACTVT","ST"],
  [300,"A","S_WFFLXDEF","ACTVT","WA"],
  [300,"A","S_YCM_APS","ACTVT","03"],
  [300,"A","SOM_PROD","ACTVT","03"],
  [300,"A","SOM_SHGR","ACTVT","03"],
  [300,"A","SUPFM_MDFP","ACTVT","03"],
  [300,"A","V_BUK","ACTVT","03"],
  [300,"A","V_BUK","ACTVT","F4"],
  [300,"A","V_KNA1_BRG","ACTVT","03"],
  [300,"A","V_KNA1_BRG","ACTVT","F4"],
  [300,"A","V_KNA1_VKO","ACTVT","03"],
  [300,"A","V_KNA1_VKO","ACTVT","F4"],
  [300,"A","V_KNKK_FRE","ACTVT","03"],
  [300,"A","V_KONA_VKO","ACTVT","03"],
  [300,"A","V_KONA_VKO","ACTVT","F4"],
  [300,"A","V_KOND_VEA","ACTVT","02"],
  [300,"A","V_KOND_VEA","ACTVT","03"],
  [300,"A","V_KOND_VEA","ACTVT","06"],
  [300,"A","V_KONG_VWE","ACTVT","03"],
  [300,"A","V_KONH_EKO","ACTVT","01"],
  [300,"A","V_KONH_EKO","ACTVT","02"],
  [300,"A","V_KONH_EKO","ACTVT","03"],
  [300,"A","V_KONH_VKO","ACTVT","02"],
  [300,"A","V_KONH_VKO","ACTVT","03"],
  [300,"A","V_KONH_VKS","ACTVT","02"],
  [300,"A","V_KONH_VKS","ACTVT","03"],
  [300,"A","V_LIKP_VST","ACTVT","01"],
  [300,"A","V_LIKP_VST","ACTVT","02"],
  [300,"A","V_LIKP_VST","ACTVT","03"],
  [300,"A","V_LIKP_VST","ACTVT","04"],
  [300,"A","V_LIKP_VST","ACTVT","18"],
  [300,"A","V_LIKP_VST","ACTVT","24"],
  [300,"A","V_LIKP_VST","ACTVT","25"],
  [300,"A","V_LIKP_VST","ACTVT","85"],
  [300,"A","V_LIKP_VST","ACTVT","F4"],
  [300,"A","V_SALEPLAN","ACTVT","03"],
  [300,"A","V_VBAK_AAT","ACTVT","02"],
  [300,"A","V_VBAK_AAT","ACTVT","03"],
  [300,"A","V_VBAK_AAT","ACTVT","04"],
  [300,"A","V_VBAK_AAT","ACTVT","05"],
  [300,"A","V_VBAK_AAT","ACTVT","06"],
  [300,"A","V_VBAK_AAT","ACTVT","24"],
  [300,"A","V_VBAK_AAT","ACTVT","25"],
  [300,"A","V_VBAK_AAT","ACTVT","43"],
  [300,"A","V_VBAK_AAT","ACTVT","C1"],
  [300,"A","V_VBAK_AAT","ACTVT","C3"],
  [300,"A","V_VBAK_AAT","ACTVT","F4"],
  [300,"A","V_VBAK_APM","ACTVT","02"],
  [300,"A","V_VBAK_CCD","ACTVT","C1"],
  [300,"A","V_VBAK_VKO","ACTVT","01"],
  [300,"A","V_VBAK_VKO","ACTVT","02"],
  [300,"A","V_VBAK_VKO","ACTVT","03"],
  [300,"A","V_VBAK_VKO","ACTVT","04"],
  [300,"A","V_VBAK_VKO","ACTVT","06"],
  [300,"A","V_VBAK_VKO","ACTVT","24"],
  [300,"A","V_VBAK_VKO","ACTVT","25"],
  [300,"A","V_VBAK_VKO","ACTVT","F4"],
  [300,"A","V_VBRK_APM","ACTVT","02"],
  [300,"A","V_VBRK_FKA","ACTVT","02"],
  [300,"A","V_VBRK_FKA","ACTVT","03"],
  [300,"A","V_VBRK_FKA","ACTVT","04"],
  [300,"A","V_VBRK_FKA","ACTVT","19"],
  [300,"A","V_VBRK_FKA","ACTVT","48"],
  [300,"A","V_VBRK_FKA","ACTVT","49"],
  [300,"A","V_VBRK_FKA","ACTVT","F4"],
  [300,"A","V_VBRK_VKO","ACTVT","02"],
  [300,"A","V_VBRK_VKO","ACTVT","03"],
  [300,"A","V_VBRK_VKO","ACTVT","04"],
  [300,"A","V_VBRK_VKO","ACTVT","19"],
  [300,"A","V_VBRK_VKO","ACTVT","48"],
  [300,"A","V_VBRK_VKO","ACTVT","49"],
  [300,"A","V_VBRK_VKO","ACTVT","F4"],
  [300,"A","V_VBSK_STO","ACTVT","03"],
  [300,"A","V_VBUK_FRE","ACTVT","03"],
  [300,"A","V_VTTK_SHT","ACTVT","F4"],
  [300,"A","V_VTTK_TDS","ACTVT","F4"],
  [300,"A","W_AUFT_BAA","ACTVT","F4"],
  [300,"A","W_BETR_USR","ACTVT","03"],
  [300,"A","W_BETR_USR","ACTVT","F4"],
  [300,"A","W_COCO","ACTVT","02"],
  [300,"A","W_COCO","ACTVT","03"],
  [300,"A","W_COCO","ACTVT","04"],
  [300,"A","W_COCO","ACTVT","43"],
  [300,"A","W_COCO","ACTVT","F4"],
  [300,"A","W_COCO_ORG","ACTVT","02"],
  [300,"A","W_COCO_ORG","ACTVT","03"],
  [300,"A","W_COCO_ORG","ACTVT","04"],
  [300,"A","W_COCO_ORG","ACTVT","43"],
  [300,"A","W_COCO_ORG","ACTVT","F4"],
  [300,"A","W_SWL_HEAD","ACTVT","03"],
  [300,"A","W_SWL_HEAD","ACTVT","F4"],
  [300,"A","W_SWL_ITEM","ACTVT","03"],
  [300,"A","W_WBRC_CR","ACTVT","01"],
  [300,"A","W_WBRC_CR","ACTVT","02"],
  [300,"A","W_WBRC_CR","ACTVT","03"],
  [300,"A","W_WBRC_CR","ACTVT","16"],
  [300,"A","W_WBRC_CR","ACTVT","A3"],
  [300,"A","W_WBRK_FKA","ACTVT","02"],
  [300,"A","W_WBRK_FKA","ACTVT","03"],
  [300,"A","W_WBRK_FKA","ACTVT","04"],
  [300,"A","W_WBRK_FKA","ACTVT","43"],
  [300,"A","W_WBRK_FKA","ACTVT","A3"],
  [300,"A","W_WBRK_FKA","ACTVT","F4"],
  [300,"A","W_WBRK_ORG","ACTVT","02"],
  [300,"A","W_WBRK_ORG","ACTVT","03"],
  [300,"A","W_WBRK_ORG","ACTVT","04"],
  [300,"A","W_WBRK_ORG","ACTVT","43"],
  [300,"A","W_WBRK_ORG","ACTVT","A3"],
  [300,"A","W_WBRK_ORG","ACTVT","F4"],
  [300,"A","W_WBRK_PER","ACTVT","02"],
  [300,"A","W_WBRK_PER","ACTVT","03"],
  [300,"A","W_WBRK_PER","ACTVT","04"],
  [300,"A","W_WBRK_PER","ACTVT","43"],
  [300,"A","W_WBRK_PER","ACTVT","A3"],
  [300,"A","W_WBRK_PER","ACTVT","F4"],
  [300,"A","W_WBRK_STS","ACTVT","97"],
  [300,"A","W_WBRK_STS","ACTVT","A3"],
  [300,"A","WFD_ASSGMT","ACTVT","01"],
  [300,"A","WFD_ASSGMT","ACTVT","02"],
  [300,"A","WFD_ASSGMT","ACTVT","03"],
  [300,"A","WFD_ASSGMT","ACTVT","06"],
  [300,"A","WFD_PAYMT","ACTVT","03"],
  [400,"D","*","*","*"],
  [410,"D","*","*","*"],
  [420,"D","*","*","*"],
  [501,"E","B_FCC_GEN","FCC_AREA","*"],
  [501,"E","B_FCC_ORG","ACTVT","*"],
  [501,"E","B_FCC_TASK","ACTVT","*"],
  [501,"E","B_FCC_TEMP","ACTVT","*"],
  [502,"E","F_CLM_BAH2","ACTVT","*"],
  [502,"E","F_CLM_BAM","ACTVT","*"],
  [502,"E","F_CLM_BSB","ACTVT","*"],
  [502,"E","F_CLM_CMR","ACTVT","*"],
  [502,"E","F_CLM_COND","ACTVT","*"],
  [502,"E","F_CLM_CP","ACTVT","*"],
  [502,"E","F_CLM_CPL","ACTVT","*"],
  [502,"E","F_CLM_LQF","ACTVT","*"],
  [502,"E","F_CLM_RULE","ACTVT","*"],
  [502,"E","F_CLM_SIG","ACTVT","*"],
  [502,"E","F_CLM_UP","ACTVT","*"],
  [505,"E","E_AMI_IN","ISU_ACTIVT","*"],
  [505,"E","E_B_BIL_PL","ISU_ACTIVT","*"],
  [505,"E","E_BILL_CL","ISU_ACTIVT","*"],
  [505,"E","E_CONNOBJ","ISU_ACTIVT","*"],
  [505,"E","E_CONTRACT","ISU_ACTIVT","*"],
  [505,"E","E_DEV_CHNG","ISU_ACTIVT","*"],
  [505,"E","E_DEV_RATE","ISU_ACTIVT","*"],
  [505,"E","E_DEV_REL","ISU_ACTIVT","*"],
  [505,"E","E_DEVGRP","ISU_ACTIVT","*"],
  [505,"E","E_DEVLOC","ISU_ACTIVT","*"],
  [505,"E","E_DISC_AMI","ISU_ACTIVT","*"],
  [505,"E","E_DISC_DOC","ISU_ACTIVT","*"],
  [505,"E","E_DISCOUNT","ISU_ACTIVT","*"],
  [505,"E","E_DRGSCEN","ISU_ACTIVT","*"],
  [505,"E","E_GRID","ISU_ACTIVT","*"],
  [505,"E","E_INSTFACT","ISU_ACTIVT","*"],
  [505,"E","E_INSTLN","ISU_ACTIVT","*"],
  [505,"E","E_INSTLN2","ISU_ACTIVT","*"],
  [505,"E","E_INSTLN3","ISU_ACTIVT","*"],
  [505,"E","E_INVOICE","ISU_ACTIVT","*"],
  [505,"E","E_LOG_REG","ISU_ACTIVT","*"],
  [505,"E","E_LOYALACC","ISU_ACTIVT","*"],
  [505,"E","E_MDUSCONF","ISU_ACTIVT","*"],
  [505,"E","E_METER_RR","ISU_ACTIVT","*"],
  [505,"E","E_MOVE_IN","ISU_ACTIVT","*"],
  [505,"E","E_MOVE_OUT","ISU_ACTIVT","*"],
  [505,"E","E_MRD_UNIT","ISU_ACTIVT","*"],
  [505,"E","E_NBSERVI2","ISU_ACTIVT","*"],
  [505,"E","E_NBSERVIC","ISU_ACTIVT","*"],
  [505,"E","E_OPERAND","ISU_ACTIVT","*"],
  [505,"E","E_POD","ISU_ACTIVT","*"],
  [505,"E","E_PORTION","ISU_ACTIVT","*"],
  [505,"E","E_PRDOC","ISU_ACTIVT","*"],
  [505,"E","E_PREMISE","ISU_ACTIVT","*"],
  [505,"E","E_PRESCL","ISU_ACTIVT","*"],
  [505,"E","E_PRICE1","ISU_ACTIVT","*"],
  [505,"E","E_PROPERTY","ISU_ACTIVT","*"],
  [505,"E","E_RATE","ISU_ACTIVT","*"],
  [505,"E","E_RATE_CAT","ISU_ACTIVT","*"],
  [505,"E","E_RATE_DET","ISU_ACTIVT","*"],
  [505,"E","E_REDEMPTN","ISU_ACTIVT","*"],
  [505,"E","E_REG_REL","ISU_ACTIVT","*"],
  [505,"E","E_REGIOGRP","ISU_ACTIVT","*"],
  [505,"E","E_SAMP_LOT","ISU_ACTIVT","*"],
  [505,"E","E_SCHEMA","ISU_ACTIVT","*"],
  [505,"E","E_SERVPROV","ISU_ACTIVT","*"],
  [505,"E","E_SWTDOC","ISU_ACTIVT","*"],
  [505,"E","E_VARIANT","ISU_ACTIVT","*"],
  [506,"E","I_CONFLICT","WC_CONFTYP","*"],
  [506,"E","I_FCODE","WC_FCODE","*"],
  [506,"E","I_VAL","WC_VAL","*"],
  [506,"E","I_WCUSE","WCUSE","*"],
  [507,"E","T_AGR_FR","ACTVT","*"],
  [507,"E","T_AGR_FRS","ACTVT","*"],
  [507,"E","T_AGR_FW","ACTVT","*"],
  [507,"E","T_AGR_INT","ACTVT","*"],
  [507,"E","T_AGR_MRFQ","ACTVT","*"],
  [507,"E","T_AGR_MUT","ACTVT","*"],
  [507,"E","T_AGR_QUOT","ACTVT","*"],
  [507,"E","T_AGR_RFQ","ACTVT","*"],
  [507,"E","T_AGR_SPC","ACTVT","*"],
  [507,"E","T_BO","ACTVT","*"],
  [507,"E","T_BUSSHARE","ACTVT","*"],
  [507,"E","T_CALCSH","ACTVT","*"],
  [507,"E","T_COND","ACTVT","*"],
  [507,"E","T_EXP_USR","ACTVT","*"],
  [507,"E","T_FUBRULE","ACTVT","*"],
  [507,"E","T_INS","ACTVT","*"],
  [507,"E","T_LAYOUT","ACTVT","*"],
  [507,"E","T_MIGRS4","ACTVT","*"],
  [507,"E","T_PL_SETS","ACTVT","*"],
  [507,"E","T_PRF_DCA","ACTVT","*"],
  [507,"E","T_PRF_DLV","ACTVT","*"],
  [507,"E","T_PRF_INC","ACTVT","*"],
  [507,"E","T_PRF_PLN","ACTVT","*"],
  [507,"E","T_PRF_SEL","ACTVT","*"],
  [507,"E","T_PRF_TEND","ACTVT","*"],
  [507,"E","T_RATE","ACTVT","*"],
  [507,"E","T_SB","ACTVT","*"],
  [507,"E","T_SCALE","ACTVT","*"],
  [507,"E","T_SCHEDULE","ACTVT","*"],
  [507,"E","T_SD_FSD","ACTVT","*"],
  [507,"E","T_SD_FWSD","ACTVT","*"],
  [507,"E","T_TACT_RT","ACTVT","*"],
  [507,"E","T_TAL","ACTVT","*"],
  [507,"E","T_TDL","ACTVT","*"],
  [507,"E","T_TOOLS","ACTVT","*"],
  [507,"E","T_TOR_EXE","ACTVT","*"],
  [507,"E","T_TOR_PUR","ACTVT","*"],
  [507,"E","T_TOR_WHN","ACTVT","*"],
  [507,"E","T_TR_DTR","ACTVT","*"],
  [507,"E","T_TR_FWO","ACTVT","*"],
  [507,"E","T_TR_FWOF","ACTVT","*"],
  [507,"E","T_TR_FWQ","ACTVT","*"],
  [507,"E","T_TR_OTR","ACTVT","*"],
  [507,"E","T_TRANSLOC","ACTVT","*"],
  [507,"E","T_WB_PUR","ACTVT","*"],
  [507,"E","T_WB_SLS","ACTVT","*"],
  [508,"E","CMM_ESTIME","ACTVT","*"],
  [508,"E","CMM_STIME","ACTVT","*"],
  [508,"E","F_T_FBNAME","ACTVT","*"],
  [508,"E","F_T_TRANSB","TCD","*"],
  [508,"E","F_T_VTBLL","ACTVT","*"],
  [508,"E","F_T_VTBLR","ACTVT","*"],
  [508,"E","F_T_VTBLV","ACTVT","*"],
  [508,"E","F_TR_MRM_S","ACTVT","*"],
  [508,"E","T_ASGTTMPL","ACTVT","*"],
  [508,"E","T_CML_ARCH","ACTVT","*"],
  [508,"E","T_DEAL_AG","ACTVT","*"],
  [508,"E","T_DEAL_DP","ACTVT","*"],
  [508,"E","T_DEAL_LC","LC_ACTVT","*"],
  [508,"E","T_DEAL_PD","ACTVT","*"],
  [508,"E","T_DEAL_PF","ACTVT","*"],
  [508,"E","T_DEAL_REF","ACTVT","*"],
  [509,"E","F_BABR_ADJ","ACTVT","*"],
  [509,"E","F_BABR_BAS","ACTVT","*"],
  [509,"E","F_BABR_CLC","ACTVT","*"],
  [509,"E","F_BABR_FPS","ACTVT","*"],
  [509,"E","F_BABR_GLC","ACTVT","*"],
  [509,"E","F_BABR_REG","ACTVT","*"],
  [509,"E","F_BABR_SCB","ACTVT","*"],
  [509,"E","F_BABR_UI","ACTVT","*"],
  [509,"E","F_BAFC_PCD","ACTVT","*"],
  [509,"E","F_FPS_TLS","ACTVT","*"],
  [510,"E","EHDGM_ARG","ACTVT","*"],
  [510,"E","EHDGM_CCI","ACTVT","*"],
  [510,"E","EHENV_SCEN","ACTVT","*"],
  [510,"E","EHFND_CCI","ACTVT","*"],
  [510,"E","EHFND_CHA","ACTVT","*"],
  [510,"E","EHFND_CHDC","ACTVT","*"],
  [510,"E","EHFND_CHM","ACTVT","*"],
  [510,"E","EHFND_CP","ACTVT","*"],
  [510,"E","EHFND_CRR","ACTVT","*"],
  [510,"E","EHFND_CSM","ACTVT","*"],
  [510,"E","EHFND_CTRL","ACTVT","*"],
  [510,"E","EHFND_DCTR","ACTVT","*"],
  [510,"E","EHFND_EXPP","ACTVT","*"],
  [510,"E","EHFND_LOC","ACTVT","*"],
  [510,"E","EHFND_PAV","ACTVT","*"],
  [510,"E","EHFND_PCEP","ACTVT","*"],
  [510,"E","EHFND_PHRS","ACTVT","*"],
  [510,"E","EHFND_RCH","ACTVT","*"],
  [510,"E","EHFND_REGL","ACTVT","*"],
  [510,"E","EHFND_REQ","ACTVT","*"],
  [510,"E","EHFND_SPL","ACTVT","*"],
  [510,"E","EHFND_SPLM","ACTVT","*"],
  [510,"E","EHFND_SRQ","ACTVT","*"],
  [510,"E","EHFND_SUB","ACTVT","*"],
  [510,"E","EHFND_VEN","ACTVT","*"],
  [510,"E","EHFND_WFT","ACTVT","*"],
  [510,"E","EHHSS_AGT","ACTVT","*"],
  [510,"E","EHHSS_FSI","ACTVT","*"],
  [510,"E","EHHSS_FSI2","ACTVT","*"],
  [510,"E","EHHSS_HSI","ACTVT","*"],
  [510,"E","EHHSS_HSP","ACTVT","*"],
  [510,"E","EHHSS_INC2","ACTVT","*"],
  [510,"E","EHHSS_INC6","ACTVT","*"],
  [510,"E","EHHSS_INC7","ACTVT","*"],
  [510,"E","EHHSS_JOB","ACTVT","*"],
  [510,"E","EHHSS_PEP","ACTVT","*"],
  [510,"E","EHHSS_RAP","ACTVT","*"],
  [510,"E","EHHSS_RAS","ACTVT","*"],
  [510,"E","EHHSS_RASP","ACTVT","*"],
  [510,"E","EHHSS_RIT","ACTVT","*"],
  [510,"E","EHHSS_SARA","ACTVT","*"],
  [510,"E","EHHSS_SI","ACTVT","*"],
  [510,"E","EHHSS_SITB","ACTVT","*"],
  [510,"E","EHPRC_CDO","ACTVT","*"],
  [510,"E","EHPRC_CMWL","ACTVT","*"],
  [510,"E","EHPRC_CPM1","ACTVT","*"],
  [510,"E","EHPRC_OLM1","ACTVT","*"],
  [510,"E","EHSDS_ARG","ACTVT","*"],
  [510,"E","EHSDS_OR","ACTVT","*"],
  [511,"E","S_SRMGS_CT","ACTVT","01"],
  [511,"E","S_SRMGS_CT","ACTVT","03"],
  [511,"E","S_SRMGS_DC","ACTVT","01"],
  [511,"E","S_SRMGS_DC","ACTVT","06"],
  [511,"E","S_SRMGS_DC","ACTVT","21"],
  [511,"E","S_SRMGS_DC","ACTVT","30"],
  [511,"E","S_SRMGS_DC","ACTVT","PC"],
  [511,"E","S_SRMGS_DC","ACTVT","PD"],
  [511,"E","S_SRMGS_PR","ACTVT","01"],
  [511,"E","S_SRMGS_PR","ACTVT","02"],
  [511,"E","S_SRMGS_PR","ACTVT","03"],
  [511,"E","S_SRMGS_PR","ACTVT","06"],
  [511,"E","S_SRMGS_VV","ACTVT","01"],
  [511,"E","S_SRMGS_VV","ACTVT","06"],
  [511,"E","S_SRMSY_CL","ACTVT","33"],
  [511,"E","S_SRMSY_CL","ACTVT","34"],
  [511,"E","S_SRMSY_CL","ACTVT","35"],
  [512,"E","S_SCMG_FLN","ACTVT","02"],
  [512,"E","S_SCMG_FLN","ACTVT","03"],
  [512,"E","S_SCMG_STA","ACTVT","02"],
  [512,"E","S_SCMG_TXT","ACTVT","01"],
  [512,"E","S_SCMG_TXT","ACTVT","02"],
  [512,"E","S_SCMG_TXT","ACTVT","03"],
  [512,"E","S_SCMG_TXT","ACTVT","06"],
  [513,"E","P_ABAP","COARS","*"],
  [513,"E","P_ABAP","REPID","*"],
  [513,"E","P_APPL","APGRP","*"],
  [513,"E","P_APPL","APTYP","*"],
  [513,"E","P_APPL","AUTHC","*"],
  [513,"E","P_APPL","INFTY","*"],
  [513,"E","P_APPL","PERSA","*"],
  [513,"E","P_APPL","RESRF","*"],
  [513,"E","P_APPL","SUBTY","*"],
  [513,"E","P_APPL","VDSK1","*"],
  [513,"E","P_DEL_PERN","PAYDELROLE","*"],
  [513,"E","P_ORGIN","AUTHC","*"],
  [513,"E","P_ORGIN","INFTY","*"],
  [513,"E","P_ORGIN","PERSA","*"],
  [513,"E","P_ORGIN","PERSG","*"],
  [513,"E","P_ORGIN","PERSK","*"],
  [513,"E","P_ORGIN","SUBTY","*"],
  [513,"E","P_ORGIN","VDSK1","*"],
  [513,"E","P_ORGXX","AUTHC","*"],
  [513,"E","P_ORGXX","INFTY","*"],
  [513,"E","P_ORGXX","SACHA","*"],
  [513,"E","P_ORGXX","SACHP","*"],
  [513,"E","P_ORGXX","SACHZ","*"],
  [513,"E","P_ORGXX","SBMOD","*"],
  [513,"E","P_ORGXX","SUBTY","*"],
  [513,"E","P_PERNR","AUTHC","*"],
  [513,"E","P_PERNR","INFTY","*"],
  [513,"E","P_PERNR","PSIGN","*"],
  [513,"E","P_PERNR","SUBTY","*"],
  [513,"E","PLOG","INFOTYP","*"],
  [513,"E","PLOG","ISTAT","*"],
  [513,"E","PLOG","OTYPE","*"],
  [513,"E","PLOG","PLVAR","*"],
  [513,"E","PLOG","PPFCODE","*"],
  [513,"E","PLOG","SUBTYP","*"],
  [514,"E","F_TRAVL","AUTHC","*"],
  [514,"E","P_TRAVL","AUTHF","*"],
  [515,"E","W_APPT","ACTVT","*"],
  [515,"E","W_ASORT","ACTVT","*"],
  [515,"E","W_ASORT_ST","ACTVT","*"],
  [515,"E","W_AUFT_BAR","ACTVT","*"],
  [515,"E","W_AUFT_RMB","ACTVT","*"],
  [515,"E","W_BUDG_TY","ACTVT","*"],
  [515,"E","W_CS_ITEM","ACTVT","*"],
  [515,"E","W_ES_ADM","ACTVT","*"],
  [515,"E","W_FRM","ACTVT","*"],
  [515,"E","W_GROUPTYP","ACTVT","*"],
  [515,"E","W_IFW_REP","ACTVT","*"],
  [515,"E","W_MARKDOWN","ACTVT","*"],
  [515,"E","W_OAA_PREC","ACTVT","*"],
  [515,"E","W_OAA_SITE","ACTVT","*"],
  [515,"E","W_OAA_VEND","ACTVT","*"],
  [515,"E","W_OPPS","ACTVT","*"],
  [515,"E","W_ORG_PR","ACTVT","*"],
  [515,"E","W_PRICATIN","ACTVT","*"],
  [515,"E","W_REF_SITE","ACTVT","*"],
  [515,"E","W_RF_MPA","ACTVT","*"],
  [515,"E","W_RF_WLAY","ACTVT","*"],
  [515,"E","W_RFAPC_GN","ACTVT","*"],
  [515,"E","W_SRS_POS","ACTVT","*"],
  [515,"E","W_SRS_VKPF","ACTVT","*"],
  [515,"E","W_ST_PKRQ","ACTVT","*"],
  [515,"E","W_ST_PKSEQ","ACTVT","*"],
  [515,"E","W_STO_ASSG","ACTVT","*"],
  [515,"E","W_STO_CPR","ACTVT","*"],
  [515,"E","W_STO_TPRS","ACTVT","*"],
  [515,"E","W_STSTKACT","ACTVT","*"],
  [515,"E","W_STSTKADJ","ACTVT","*"],
  [515,"E","W_TASK_G","ACTVT","*"],
  [515,"E","W_TASK_L","ACTVT","*"],
  [515,"E","W_TASK_P","ACTVT","*"],
  [515,"E","W_VKPR_PLT","ACTVT","01"],
  [515,"E","W_VKPR_PLT","ACTVT","02"],
  [515,"E","W_VKPR_PLT","ACTVT","03"],
  [515,"E","W_VKPR_VKO","ACTVT","01"],
  [515,"E","W_VKPR_VKO","ACTVT","02"],
  [515,"E","W_VKPR_VKO","ACTVT","03"],
  [515,"E","W_VKPR_VTL","ACTVT","01"],
  [515,"E","W_VKPR_VTL","ACTVT","02"],
  [515,"E","W_VKPR_VTL","ACTVT","03"],
  [515,"E","W_VKPR_VTL","ACTVT","F4"],
  [515,"E","W_VKPR_WRK","ACTVT","01"],
  [515,"E","W_VKPR_WRK","ACTVT","02"],
  [515,"E","W_VKPR_WRK","ACTVT","03"],
  [515,"E","W_WAKH_EKO","ACTVT","*"],
  [515,"E","W_WAKH_MAT","ACTVT","*"],
  [515,"E","W_WAKH_THE","ACTVT","*"],
  [515,"E","W_WAKH_VKO","ACTVT","*"],
  [515,"E","W_WB2SHINV","ACTVT","*"],
  [515,"E","W_WBEF_WRK","ACTVT","*"],
  [515,"E","W_WBGT_FIX","ACTVT","*"],
  [515,"E","W_WBHK_TCT","ACTVT","*"],
  [515,"E","W_WIND_TYP","ACTVT","*"],
  [515,"E","W_WTAD_AM","ACTVT","*"],
  [515,"E","W_WTAD_IR","ACTVT","*"],
  [515,"E","W_WTAD_ISU","ACTVT","*"],
  [515,"E","W_WTEW","ACTVT","*"],
  [515,"E","W_WTRA_LOG","ACTVT","*"],
  [515,"E","W_WXP_DESI","ACTVT","*"],
  [515,"E","W_WXP_HIER","ACTVT","*"],
  [515,"E","W_WXP_INT","ACTVT","*"],
  [515,"E","W_WXP_LAY","ACTVT","*"],
  [515,"E","W_WXP_PLAN","ACTVT","*"],
  [515,"E","WB2_SHD_UI","ACTVT","*"],
  [515,"E","WLF_IV","ACTVT","*"],
  [515,"E","WLMVREL","ACTVT","*"],
  [515,"E","WRDM_ADM","ACTVT","*"],
  [515,"E","WRF_CDT_H","ACTVT","*"],
  [515,"E","WRF_CDT_V","ACTVT","*"],
  [515,"E","WRF_PPWADM","ACTVT","*"],
  [516,"E","GRAC_*","ACTVT","*"],
  [517,"E","F_ITTC_BUK","ACTVT","*"],
  [517,"E","F_MIE_SKO","ACTVT","*"],
  [517,"E","F_MNG_CON","ACTVT","*"],
  [517,"E","F_PMT_TRNS","ACTVT","*"],
  [517,"E","F_RE_BRGRP","ACTVT","*"],
  [517,"E","F_RE_REPOR","ACTVT","*"],
  [517,"E","F_RE_TRANS","ACTVT","*"],
  [517,"E","F_REAJ_AT","ACTVT","*"],
  [517,"E","F_REAJ_CG","ACTVT","*"],
  [517,"E","F_REAJ_PR","ACTVT","*"],
  [517,"E","F_REBD_AO","ACTVT","*"],
  [517,"E","F_RECA_TRC","ACTVT","*"],
  [517,"E","F_RECD_CN","ACTVT","*"],
  [517,"E","F_RECD_RO","ACTVT","*"],
  [517,"E","F_RECE_CD","ACTVT","*"],
  [517,"E","F_RECE_PO","ACTVT","*"],
  [517,"E","F_RECE_PR","ACTVT","*"],
  [517,"E","F_RECE_TM","ACTVT","*"],
  [517,"E","F_RECN","ACTVT","*"],
  [517,"E","F_RECN_ATT","ACTVT","*"],
  [517,"E","F_RECN_FDG","ACTVT","*"],
  [517,"E","F_RECN_SKO","ACTVT","*"],
  [517,"E","F_RECO_CE","ACTVT","*"],
  [517,"E","F_REGI_VE","ACTVT","*"],
  [517,"E","F_REIP_IO","ACTVT","*"],
  [517,"E","F_REIP_SG","ACTVT","*"],
  [517,"E","F_REIT_DS","ACTVT","*"],
  [517,"E","F_REIT_OR","ACTVT","*"],
  [517,"E","F_REIT_TC","ACTVT","*"],
  [517,"E","F_RELM_JL","ACTVT","*"],
  [517,"E","F_RELM_LR","ACTVT","*"],
  [517,"E","F_RELM_NA","ACTVT","*"],
  [517,"E","F_RELM_PE1","ACTVT","*"],
  [517,"E","F_RELM_PE2","ACTVT","*"],
  [517,"E","F_RELM_PE3","ACTVT","*"],
  [517,"E","F_RELM_PE4","ACTVT","*"],
  [517,"E","F_RELM_PL","ACTVT","*"],
  [517,"E","F_RELM_RC","ACTVT","*"],
  [517,"E","F_REMM_MN","ACTVT","*"],
  [517,"E","F_REMOB_BR","ACTVT","*"],
  [517,"E","F_REMOB_ER","ACTVT","*"],
  [517,"E","F_REOR_MP","ACTVT","*"],
  [517,"E","F_REOR_OF","ACTVT","*"],
  [517,"E","F_REOR_OO","ACTVT","*"],
  [517,"E","F_REOR_RR","ACTVT","*"],
  [517,"E","F_REOR_RS","ACTVT","*"],
  [517,"E","F_REOR_SRV","ACTVT","*"],
  [517,"E","F_RERA_OP","ACTVT","*"],
  [517,"E","F_RESC_PG","ACTVT","*"],
  [517,"E","F_REXC_MS","ACTVT","*"],
  [517,"E","F_RNT_ADJ","ACTVT","*"],
  [517,"E","F_RNT_OFF","ACTVT","*"],
  [517,"E","F_UMV_BUK","ACTVT","*"],
  [517,"E","F_WIBE","ACTVT","*"],
  [517,"E","F_XCIT_ICI","ACTVT","*"],
  [517,"E","F_XCIT_IRE","ACTVT","*"],
  [517,"E","F_XCPT_IMI","ACTVT","*"],
  [517,"E","FI_ABR_BUK","ACTVT","*"],
  [517,"E","FI_BAV_BUK","ACTVT","*"],
  [517,"E","FI_BEW_ACT","ACTVT","*"],
  [517,"E","FI_BKA_BUK","ACTVT","*"],
  [517,"E","FI_FAG_BUK","ACTVT","*"],
  [517,"E","FI_FAW_BUK","ACTVT","*"],
  [517,"E","FI_GAR_BUK","ACTVT","*"],
  [517,"E","FI_GBU_ACT","ACTVT","*"],
  [517,"E","FI_GEB_BUK","ACTVT","*"],
  [517,"E","FI_GRU_BUK","ACTVT","*"],
  [517,"E","FI_HEI_BUK","ACTVT","*"],
  [517,"E","FI_HKA_BUK","ACTVT","*"],
  [517,"E","FI_IND_BUK","ACTVT","*"],
  [517,"E","FI_KUE_BUK","ACTVT","*"],
  [517,"E","FI_ME1_BUK","ACTVT","*"],
  [517,"E","FI_MIE_BUK","ACTVT","*"],
  [517,"E","FI_MIS_ACT","ACTVT","*"],
  [517,"E","FI_MIS_BUK","ACTVT","*"],
  [517,"E","FI_MOD_BUK","ACTVT","*"],
  [517,"E","FI_RAU_BUK","ACTVT","*"],
  [517,"E","FI_VBE_BUK","ACTVT","*"],
  [517,"E","FI_VWO_BUK","ACTVT","*"],
  [517,"E","FI_WEI_BUK","ACTVT","*"],
  [517,"E","FI_ZAH_BUK","ACTVT","*"],
  [517,"E","FI_ZLG_BUK","ACTVT","*"],
  [517,"E","FI_ZUO_ACT","ACTVT","*"],
  [518,"E","J_6NIM_BC1","ACTVT","*"],
  [518,"E","J_6NIM_BUK","ACTVT","*"],
  [518,"E","J_6NIM_RFC","ACTVT","*"],
  [518,"E","J_6NIM_WP1","ACTVT","*"],
  [518,"E","J_6NIM_WP2","ACTVT","*"],
  [518,"E","J_6NIM_WP3","ACTVT","*"],
  [518,"E","J_6NIM_WP4","ACTVT","*"],
  [518,"E","J_6NIM_WP5","ACTVT","*"],
  [518,"E","J_6NIM_WP6","ACTVT","*"],
  [518,"E","J_6NIM_WP7","ACTVT","*"],
  [518,"E","J_6NIM_WP8","ACTVT","*"],
  [518,"E","J_6NIM_WP9","ACTVT","*"],
  [518,"E","J_6NPF_NAV","J_6NPF_PLA","*"],
  [518,"E","J_6NPF_RFC","ACTVT","*"],
  [518,"E","J_6NPF_WTY","J_6NPF_*","*"],
  [519,"E","P_PCR","ACTVT","*"],
  [519,"E","P_PYEVDOC","ACTVT","*"],
  [519,"E","P_PYEVRUN","ACTVT","*"],
  [520,"E","P_RCF_ACT","ACTVT","*"],
  [520,"E","P_RCF_APPL","RCF_APPL","*"],
  [520,"E","P_RCF_POOL","RCF_POOL","*"],
  [520,"E","P_RCF_STAT","RCF_STATUS","*"],
  [520,"E","P_RCF_VIEW","RCF_VIEW","*"],
  [521,"E","/SCDL/AUTH","ACTVT","*"],
  [521,"E","/SCTM/AUTH","ACTVT","*"],
  [521,"E","/SCWM/ACTI","/SCWM/LGNU","*"],
  [521,"E","/SCWM/AIMP","ACTVT","*"],
  [521,"E","/SCWM/DLV","ACTVT","*"],
  [521,"E","/SCWM/DSAP","ACTVT","*"],
  [521,"E","/SCWM/DSSL","ACTVT","*"],
  [521,"E","/SCWM/ELS","ACTVT","*"],
  [521,"E","/SCWM/ESIC","/SCWM/ESIC","*"],
  [521,"E","/SCWM/IMPL","ACTVT","*"],
  [521,"E","/SCWM/MO","/SCWM/MONI","*"],
  [521,"E","/SCWM/PL","ACTVT","*"],
  [521,"E","/SCWM/QFUP","ACTVT","*"],
  [521,"E","/SCWM/QLVL","ACTVT","*"],
  [521,"E","/SCWM/QUEU","/SCWM/QUEU","*"],
  [521,"E","/SCWM/RFAD","/SCWM/RFAP","*"],
  [521,"E","/SCWM/RFLT","/SCWM/RFAP","*"],
  [521,"E","/SCWM/RSTY","/SCWM/RSTY","*"],
  [521,"E","/SCWM/SFUN","/SCWM/SFUN","*"],
  [521,"E","/SCWM/SLFU","/SCWM/SLFU","*"],
  [521,"E","/SCWM/SLOT","/SCWM/ENTL","*"],
  [521,"E","/SCWM/STBI","/SCWM/ENTL","*"],
  [521,"E","/SCWM/STLG","/SCWM/ENTL","*"],
  [521,"E","/SCWM/STRS","/SCWM/ENTL","*"],
  [521,"E","/SCWM/TOCR","/SCWM/ENTL","*"],
  [521,"E","/SCWM/TR","/SCWM/TRAC","*"],
  [521,"E","/SCWM/WAV1","ACTVT","*"],
  [521,"E","/SCWM/WCTA","ACTVT","*"],
  [521,"E","/SCWM/WST","/SCWM/WOST","*"],
  [522,"E","CPRO_CPT","ACTVT","*"],
  [522,"E","CPRO_DPT","ACTVT","*"],
  [522,"E","CPRO_EVAL","CPRO_EVAL","*"],
  [522,"E","CPRO_VSHDR","ACTVT","*"],
  [523,"E","E_CACS_AGR","ACTVT","*"],
  [523,"E","E_CACS_BDL","ACTVT","*"],
  [523,"E","E_CACS_BK","ACTVT","*"],
  [523,"E","E_CACS_CTR","ACTVT","*"],
  [523,"E","E_CACS_SFC","ACTVT","*"],
  [523,"E","E_CACS_SLC","ACTVT","*"],
  [523,"E","E_CACS_SMC","ACTVT","*"],
  [523,"E","S_TCODE","TCD","CACS*"],
  [524,"E","I_INS_OBJ","ACTVT","*"],
  [524,"E","I_INSO_GRP","ACTVT","*"],
  [524,"E","I_VVKK_BLA","ACTVT","*"],
  [524,"E","I_VVMH_VTR","ACTVT","*"],
  [524,"E","I_VVVK_INF","ACTVT","*"],
  [525,"E","I_ICL_ACLM","ACTVT","*"],
  [525,"E","I_ICL_ACT","ACTVT","*"],
  [525,"E","I_ICL_AGRP","ICL_AUTHGP","*"],
  [525,"E","I_ICL_CTYP","ACTVT","*"],
  [525,"E","I_ICL_LIT","ACTVT","*"],
  [525,"E","I_ICL_NEG","ACTVT","*"],
  [525,"E","I_ICL_PROC","ACTVT","*"],
  [525,"E","I_ICL_SBRG","ACTVT","*"],
  [525,"E","I_ICL_SCLT","ACTVT","*"],
  [525,"E","I_ICL_SUBX","ACTVT","*"],
  [525,"E","I_ICL_SUP","ACTVT","*"],
  [525,"E","I_ICLE_CAT","ACTVT","*"],
  [525,"E","I_ICLPOLSE","ACTVT","*"],
  [525,"E","I_ICLSBOBC","ACTVT","*"],
  [525,"E","S_TCODE","TCD","ICLPAY03"],
  [526,"E","/RAP/MPR","*","*"],
  [527,"E","AUD_ACTION","ACTVT","*"],
  [527,"E","AUD_ACTM","ACTVT","*"],
  [527,"E","AUD_AUDIT","ACTVT","*"],
  [527,"E","AUD_CTRL","ACTVT","*"],
  [527,"E","AUD_CTRLM","ACTVT","*"],
  [527,"E","AUD_CTRLT","ACTVT","*"],
  [527,"E","AUD_DIM","ACTVT","*"],
  [527,"E","AUD_FIND","ACTVT","*"],
  [527,"E","AUD_FINDM","ACTVT","*"],
  [527,"E","AUD_ITEMC","ACTVT","*"],
  [527,"E","AUD_ITEMS","ACTVT","*"],
  [527,"E","AUD_KRINS","ACTVT","*"],
  [527,"E","AUD_PLAN","ACTVT","*"],
  [527,"E","AUD_PLANG","ACTVT","*"],
  [527,"E","AUD_PLGRP","ACTVT","*"],
  [527,"E","AUD_PRC","ACTVT","*"],
  [527,"E","AUD_PRCM","ACTVT","*"],
  [527,"E","AUD_RISK","ACTVT","*"],
  [527,"E","AUD_RISKM","ACTVT","*"],
  [527,"E","AUD_SCOPE","ACTVT","*"],
  [527,"E","AUD_SCOPEG","ACTVT","*"],
  [527,"E","AUD_SCOPEM","ACTVT","*"],
  [527,"E","AUD_STAFF","ACTVT","*"],
  [527,"E","AUD_TASK","ACTVT","*"],
  [527,"E","AUD_TASKG","ACTVT","*"],
  [527,"E","AUD_TASKM","ACTVT","*"],
  [527,"E","AUD_VIEW","ACTVT","*"],
  [527,"E","AUD_WPA","ACTVT","*"],
  [527,"E","AUD_WPAG","ACTVT","*"],
  [527,"E","AUD_WPAM","ACTVT","*"],
  [528,"E","GFRN_USER","ACTVT","*"],
  [529,"E","DFS_ALL_PL","ACTVT","*"],
  [529,"E","DFS_ALL_PR","ACTVT","*"],
  [529,"E","DFS_ALP_S","ACTVT","*"],
  [529,"E","DFS_ASGMTS","ACTVT","*"],
  [529,"E","DFS_ASSGM1","ACTVT","*"],
  [529,"E","DFS_ASSGMS","ACTVT","*"],
  [529,"E","DFS_CAP_CO","ACTVT","*"],
  [529,"E","DFS_CAP_CS","ACTVT","*"],
  [529,"E","DFS_CAP_S","ACTVT","*"],
  [529,"E","DFS_CAPMET","ACTVT","*"],
  [529,"E","DFS_CON_PR","ACTVT","*"],
  [529,"E","DFS_FE_CON","ACTVT","*"],
  [529,"E","DFS_FMPO_A","ACTVT","*"],
  [529,"E","DFS_FMPO_C","ACTVT","*"],
  [529,"E","DFS_FMPO_H","ACTVT","*"],
  [529,"E","DFS_FMPO_M","ACTVT","*"],
  [529,"E","DFS_FMPO_S","ACTVT","*"],
  [529,"E","DFS_METL_S","ACTVT","*"],
  [529,"E","DFS_MODEL","ACTVT","*"],
  [529,"E","DFS_MODELS","ACTVT","*"],
  [529,"E","DFS_ORGM","ACTVT","*"],
  [529,"E","DFS_PERS","ACTVT","*"],
  [529,"E","DFS_PLTFRM","ACTVT","*"],
  [529,"E","DFS_PROD","ACTVT","*"],
  [529,"E","DFS_RELOC","ACTVT","*"],
  [529,"E","DFS_SCN","ACTVT","*"],
  [529,"E","DFS_SCN_S","ACTVT","*"],
  [529,"E","DFS_SNSTVY","ACTVT","*"],
  [529,"E","DFS_STRAUT","ACTVT","*"],
  [530,"E","FRA_CCHIER","ACTVT","*"],
  [530,"E","FRA_COSCEN","ACTVT","*"],
  [531,"E","L_LAM_EAML","ACTVT","*"],
  [532,"E","/ECRS/DVI","ACTVT","*"],
  [532,"E","/ECRS/POI","ACTVT","*"],
  [532,"E","/ECRS/RP","ACTVT","*"],
  [532,"E","/ECRS/WL","ACTVT","*"],
  [532,"E","GTS_AU_EXT","ACTVT","*"],
  [532,"E","GTS_AU_INT","ACTVT","*"],
  [532,"E","GTS_BO_LRG","ACTVT","*"],
  [532,"E","GTS_BP_LRG","ACTVT","*"],
  [532,"E","GTS_CD_CDT","ACTVT","*"],
  [532,"E","GTS_CD_FTO","ACTVT","*"],
  [532,"E","GTS_CD_LRG","ACTVT","*"],
  [532,"E","GTS_CI_CIT","ACTVT","*"],
  [532,"E","GTS_CUS","ACTVT","*"],
  [532,"E","GTS_EMB","ACTVT","*"],
  [532,"E","GTS_LDT","ACTVT","*"],
  [532,"E","GTS_LLNS","ACTVT","*"],
  [532,"E","GTS_LM_FTO","ACTVT","*"],
  [532,"E","GTS_LOC","ACTVT","*"],
  [532,"E","GTS_PR_LRG","ACTVT","*"],
  [532,"E","GTS_PRE","ACTVT","*"],
  [532,"E","GTS_RES","ACTVT","*"],
  [532,"E","GTS_REX","ACTVT","*"],
  [532,"E","GTS_SPL","ACTVT","*"],
  [532,"E","GTS_SPL_UI","ACTVT","*"],
  [532,"E","GTS_SPLEXT","ACTVT","*"],
  [533,"E","/IAM/AAUTH","/IAM/AUTHG","*"],
  [533,"E","/IAM/ATTMT","ACTVT","*"],
  [533,"E","/IAM/COGDR","/IAM/AUTHG","*"],
  [533,"E","/IAM/IAUTH","/IAM/AUTHG","*"],
  [533,"E","/IAM/OREF","ACTVT","*"],
  [533,"E","/IAM/REMIN","ACTVT","*"],
  [533,"E","/IAM/SUBSC","ACTVT","*"],
  [533,"E","/IAM/TXTTY","ACTVT","*"],
  [533,"E","IAM/A_ATTR","/IAM/AUTHG","*"],
  [533,"E","IAM/A_RLCD","/IAM/AUTHG","*"],
  [533,"E","IAM/A_STAT","/IAM/AUTHG","*"],
  [533,"E","IAM/A_USTA","/IAM/AUTHG","*"],
  [533,"E","IAM/CHGLOG","ACTVT","*"],
  [533,"E","IAM/I_RLCD","/IAM/AUTHG","*"],
  [533,"E","IAM/I_STAT","/IAM/AUTHG","*"],
  [533,"E","IAM/I_USTA","/IAM/AUTHG","*"],
  [533,"E","IAM_CAT_AC","/IAM/CATEG","*"],
  [533,"E","IAM_CODEGR","/IAM/AUTHG","*"],
  [534,"E","/CPD/ANLY","ACTVT","*"],
  [534,"E","/CPD/MC","ACTVT","*"],
  [534,"E","/CPD/MP","ACTVT","*"],
  [534,"E","/CPD/OAUTH","ACTVT","*"],
  [535,"E","/CPD/FPH","ACTVT","*"],
  [536,"E","ICM_AUTH","ACTVT","*"],
  [536,"E","PICM_ISSUE","ACTVT","*"],
  [536,"E","PICM_ISTAT","/IAM/AUTHG","*"],
  [536,"E","PICM_STAT","/IAM/AUTHG","*"],
  [537,"E","EHHSS_INC6","ACTVT","01"],
  [537,"E","EHHSS_INC6","ACTVT","02"],
  [537,"E","EHHSS_INC6","ACTVT","06"],
  [538,"E","EHFND_CHA","ACTVT","03"],
  [538,"E","EHFND_RCH","ACTVT","03"],
  [538,"E","EHFND_SPL","ACTVT","03"],
  [538,"E","EHHSS_FSI2","ACTVT","03"],
  [538,"E","EHHSS_HSI","ACTVT","03"],
  [538,"E","EHHSS_RAS","ACTVT","03"],
  [538,"E","EHHSS_SARA","ACTVT","03"],
  [539,"E","X_EM_EH","ACTVT","*"],
  [539,"E","X_EM_EH_CH","ACTVT","*"],
  [539,"E","X_EM_EVM","ACTVT","*"],
  [540,"E","FRA_ALERT","ACTVT","*"],
  [540,"E","FRA_COTASK","ACTVT","*"],
  [540,"E","FRA_DSTRAT","ACTVT","*"],
  [541,"E","EHENV_RPT","ACTVT","*"],
  [541,"E","EHENV_SCEN","ACTVT","*"],
  [541,"E","EHFND_REQ","ACTVT","*"],
  [542,"E","F_PSOB_BEG","ACTVT","*"],
  [542,"E","F_PSOB_VGT","ACTVT","*"],
  [543,"E","P_TCODE","TCD","LSO_*"],
  [544,"E","/MRSS/CONF","ACTVT","*"],
  [544,"E","/MRSS/MNT","ACTVT","*"],
  [544,"E","/MRSS/MNTP","ACTVT","*"],
  [544,"E","/MRSS/PB1","ACTVT","*"],
  [545,"E","N_1CORDER","ACTVT","*"],
  [545,"E","N_1MEEVENT","ACTVT","*"],
  [545,"E","N_1MEORDER","ACTVT","*"],
  [545,"E","N_1NPND","ACTVT","*"],
  [545,"E","N_1ORATE","ACTVT","*"],
  [545,"E","N_1PLATH","ACTVT","*"],
  [545,"E","N_2AD","ACTVT","*"],
  [545,"E","N_2AGENT","ACTVT","*"],
  [545,"E","N_2BI","ACTVT","*"],
  [545,"E","N_2DWS_D","ACTVT","*"],
  [545,"E","N_2DWS_W","ACTVT","*"],
  [545,"E","N_2FILLIST","ACTVT","*"],
  [545,"E","N_2FORMA","ACTVT","*"],
  [545,"E","N_2GL","ACTVT","*"],
  [545,"E","N_2GL_BI","ACTVT","*"],
  [545,"E","N_2GL_LAY","ACTVT","*"],
  [545,"E","N_2GL_M","ACTVT","*"],
  [545,"E","N_2INFO","ACTVT","*"],
  [545,"E","N_2IPPD","ACTVT","*"],
  [545,"E","N_2MEDOK","ACTVT","*"],
  [545,"E","N_2MEFLS","ACTVT","*"],
  [545,"E","N_2METMPL","ACTVT","*"],
  [545,"E","N_2P01_DEF","ACTVT","*"],
  [545,"E","N_2PHYO","ACTVT","*"],
  [545,"E","N_2TM","ACTVT","*"],
  [545,"E","N_2TM_S","ACTVT","*"],
  [545,"E","N_2UX","ACTVT","*"],
  [545,"E","N_2ZTPKT","ACTVT","*"],
  [545,"E","N_AMB_DSP","ACTVT","*"],
  [545,"E","N_AT_EDI","ACTVT","*"],
  [545,"E","N_AT_INTSV","ACTVT","*"],
  [545,"E","N_AT_SCORE","ACTVT","*"],
  [545,"E","N_BORR_HIS","ACTVT","*"],
  [545,"E","N_FALL_ARC","ACTVT","*"],
  [545,"E","N_FORM_PRI","ACTVT","*"],
  [545,"E","N_GPAR_ALL","ACTVT","*"],
  [545,"E","N_GPAR_ARB","ACTVT","*"],
  [545,"E","N_GPAR_ARZ","ACTVT","*"],
  [545,"E","N_GPAR_DEB","ACTVT","*"],
  [545,"E","N_GPAR_KRH","ACTVT","*"],
  [545,"E","N_GPAR_KTR","ACTVT","*"],
  [545,"E","N_GPAR_MIT","ACTVT","*"],
  [545,"E","N_HON_ACOD","ACTVT","*"],
  [545,"E","N_INVOICE","ACTVT","*"],
  [545,"E","N_NBAU_AKT","ACTVT","*"],
  [545,"E","N_NBEW_AKT","ACTVT","*"],
  [545,"E","N_NDIA_ALL","ACTVT","*"],
  [545,"E","N_NDIA_ORG","ACTVT","*"],
  [545,"E","N_NDIA_UEB","ACTVT","*"],
  [545,"E","N_NDRG","ACTVT","*"],
  [545,"E","N_NFKL_PPR","ACTVT","*"],
  [545,"E","N_NICP_FAL","ACTVT","*"],
  [545,"E","N_NL_DBC_V","ACTVT","*"],
  [545,"E","N_NL_TCERT","ACTVT","*"],
  [545,"E","N_NLEI_ANF","ACTVT","*"],
  [545,"E","N_NLEI_ANP","ACTVT","*"],
  [545,"E","N_NLEI_ERB","ACTVT","*"],
  [545,"E","N_NLEI_FAL","ACTVT","*"],
  [545,"E","N_NLEI_FBZ","ACTVT","*"],
  [545,"E","N_NLEI_OBZ","ACTVT","*"],
  [545,"E","N_NMAT_OE","ACTVT","*"],
  [545,"E","N_NORG_AKT","ACTVT","*"],
  [545,"E","N_OP_BEG","ACTVT","*"],
  [545,"E","N_OP_MON","ACTVT","*"],
  [545,"E","N_OP_NEBEN","ACTVT","*"],
  [545,"E","N_OP_PLAN","ACTVT","*"],
  [545,"E","N_OP_PRG","ACTVT","*"],
  [545,"E","N_PAPI_AKT","ACTVT","*"],
  [545,"E","N_PATI_AKT","ACTVT","*"],
  [545,"E","N_PS_PSEUD","ACTVT","*"],
  [545,"E","N_PTS_ORD","ACTVT","*"],
  [545,"E","N_RSF_MAIN","ACTVT","*"],
  [545,"E","N_VITPAR","ACTVT","*"],
  [545,"E","N_VM_DEF","ACTVT","*"],
  [546,"E","F_PAYR_BUK","ACTVT","02"],
  [546,"E","F_PAYR_BUK","ACTVT","03"],
  [546,"E","F_PAYR_BUK","ACTVT","05"],
  [546,"E","F_PAYR_BUK","ACTVT","84"],
  [546,"E","M_RECH_AKZ","ACTVT","02"],
  [546,"E","M_RECH_BUK","ACTVT","02"],
  [546,"E","M_RECH_CPY","ACTVT","02"],
  [546,"E","M_RECH_EKG","ACTVT","02"],
  [546,"E","M_RECH_SPG","ACTVT","02"],
  [546,"E","M_RECH_WRK","ACTVT","02"],
  [547,"E","S_RS_COMP","ACTVT","06"],
  [547,"E","S_RS_COMP","ACTVT","16"],
  [547,"E","S_RS_COMP1","ACTVT","06"],
  [547,"E","S_RS_COMP1","ACTVT","16"],
  [548,"E","/AIF/CFUNC","ACTVT","*"],
  [548,"E","/AIF/CLINK","ACTVT","*"],
  [548,"E","/AIF/CTEXT","ACTVT","*"],
  [548,"E","/AIF/CUST","ACTVT","*"],
  [548,"E","/AIF/DPA","ACTVT","*"],
  [548,"E","/AIF/ERR","ACTVT","*"],
  [548,"E","/AIF/HINTS","ACTVT","*"],
  [548,"E","/AIF/VMAP","ACTVT","*"],
  [549,"A","J_JVA_PRC","ACTVT","*"],
  [549,"A","J_JVA_JOA","ACTVT","*"],
  [549,"A","J_JVA_VNT","ACTVT","*"],
];

export const RULESET: PceRule[] = RAW.map(([step, t, obj, field, val]) => {
  const meta = STEP_META[step];
  return {
    step,
    ruleClass: meta?.ruleClass ?? String(step),
    cls: meta?.cls ?? null,
    engine: meta?.engine,
    type: TYPE_BY_CODE[t] ?? "AUTH",
    obj,
    field: field === 0 ? null : field,
    val: val === 0 ? null : val,
  };
});

/** Gesamtzahl der Regeln — aus dem Regelwerk selbst, nicht behauptet. */
export const PCE_TOTAL_RULES = RULESET.length;

/** Nach Step sortiert: die erste greifende Regel gewinnt. */
export const RULESET_BY_STEP: PceRule[] = [...RULESET].sort((a, b) => a.step - b.step);

/**
 * Index nach Auth-Objekt, je Objekt nach Step sortiert.
 * Ohne ihn liefe die Klassifikation linear ueber alle Regeln — bei 116.540 Konten
 * ist das nicht tragbar.
 */
const BY_OBJECT = (() => {
  const m = new Map<string, PceRule[]>();
  for (const r of RULESET_BY_STEP) {
    const arr = m.get(r.obj);
    if (arr) arr.push(r);
    else m.set(r.obj, [r]);
  }
  return m;
})();

/** Alle Regeln zu einem Auth-Objekt, nach Step sortiert (hoechste Klasse zuerst). */
export function rulesForObject(obj: string): PceRule[] {
  return BY_OBJECT.get(obj) ?? [];
}

/** Wie viele Regeln es zu einem Objekt gibt — fuer die Anzeige "x von y". */
export function ruleCountForObject(obj: string): number {
  return BY_OBJECT.get(obj)?.length ?? 0;
}

/** Alle im Regelwerk vorkommenden Auth-Objekte. */
export function allRuleObjects(): string[] {
  return [...BY_OBJECT.keys()];
}
